/**
 * WMN engagement memory - persists across sessions (localStorage + cookie).
 * Records product views; full search ranks by most recent term-matching view.
 * Genre strong-bump floor 50 for cuba/token-prefix (v2026-08-04b).
 * Explore soft-feeds recordSearch even with field nodes (typed multi-signal).
 * KIND_WEIGHT.search = 1.2 (below search_click).
 */
(function (window, document) {
  'use strict';

  var BASE_KEY = 'wmn_engagement_v1';
  var COOKIE_NAME = 'wmn_eng';
  var COOKIE_DAYS = 365;
  /** Theme Recently viewed cookie — must match assets/app.js recentlyViewed. */
  var RV_COOKIE_NAME = 'recentlyViewed';
  var RV_COOKIE_DAYS = 30;
  var RV_MAX_HANDLES = 12;
  var MAX_EVENTS = 80;
  var MAX_AGE_MS = 1000 * 60 * 60 * 24 * 365;
  var VIEW_WEIGHT = 12;
  var HANDLE_BONUS = 800;
  var HALF_LIFE_MS = 1000 * 60 * 60 * 24 * 180;
  var MAX_SEARCH_INJECT = 5;
  var KIND_WEIGHT = {
    view: 1,
    atc: 3,
    listen: 2,
    chip: 1.5,
    // Intentional rail/discover hover — stronger than passive view
    hover: 1.85,
    // Passive impression — must not drown intentional geo interest
    visible: 0.22,
    // Typed search: below search_click (2.0), above old 0.6 — plan ≈1.2
    search: 1.2,
    search_click: 2.0
  };
  var MAX_ENTITIES = 60;
  var FOCUS_KEY = 'wmn_focus_v1';
  var FOCUS_TTL_MS = 1000 * 60 * 60 * 4;
  /** Temu-style search ghost: max chars; default header placeholder. */
  var SEARCH_GHOST_MAX = 40;
  var DEFAULT_SEARCH_PLACEHOLDER = 'Search for artists & releases…';
  var SEARCH_INPUT_SEL =
    '.wmn-header__search input[name="q"], .header_search_form input[name="q"], [data-dropdown="search"] input[name="q"]';
  /** Durable interest profile (localStorage) — survives reload / new sessions. */
  var INTEREST_KEY = 'wmn_interest_profile_v1';
  var INTEREST_TTL_MS = 1000 * 60 * 60 * 24 * 90;
  var FOCUS_ARTIST_BONUS = 220;
  var FOCUS_GENRE_BONUS = 180;
  /** Session focus owns For You ranking — must beat stale prefs mass. */
  var FOCUS_SESSION_MATCH_BONUS = 600;
  var FOCUS_SESSION_NONMATCH_PENALTY = 400;
  /** @deprecated aliases kept for older smokes / comments */
  var FOCUS_GENRE_DOMINANT_BONUS = FOCUS_SESSION_MATCH_BONUS;
  var FOCUS_GENRE_NONMATCH_PENALTY = FOCUS_SESSION_NONMATCH_PENALTY;
  var FOCUS_HANDLE_BONUS = 300;
  var ARTIST_MMR_PENALTY = 380;
  /** Soft country diversity on top of artist MMR (~0.15× artist). */
  var COUNTRY_MMR_PENALTY = ARTIST_MMR_PENALTY * 0.15;
  /** Catalog emb term — tie-break under focusDominant; fuller when no session focus. */
  var LAMBDA_EMB = 0.25;
  var EMB_SCORE_SCALE = 200;
  /** Phase 5 propensity — softmax over eligible scores; IPS clip M. */
  /**
   * Focus purity (on top of FOCUS_SESSION_MATCH_BONUS).
   * Explicit geo/title beats sole bridge genre; multi-genre comps stay out of prefer.
   */
  var FOCUS_PURITY_COUNTRY_TAG = 280;
  var FOCUS_PURITY_REGION_TAG = 280;
  var FOCUS_PURITY_TITLE = 220;
  var FOCUS_PURITY_FRAC_SCALE = 260;
  var FOCUS_PURITY_SOLE_EXTRA = 80;
  /** Weak bridge-only comps (multi-genre): soft-fill rank, not prefer front. */
  var FOCUS_WEAK_BRIDGE_BONUS = 150;
  /** Bump when focus-hard / match rules change so For You feed remounts locked cards. */
  var FY_POLICY_ID = 'fy-focus-purity-v2';
  /**
   * Country slug → adjective/alias tokens for genre slug/name matching.
   * Sparse country:* product tags (e.g. India has 3) must still prefer
   * indian-classical-raga / bollywood-filmi etc.
   */
  var COUNTRY_ADJ_TOKENS = {
    india: ['indian'],
    brazil: ['brazilian', 'brasil'],
    cuba: ['cuban', 'afro-cuban'],
    jamaica: ['jamaican'],
    mali: ['malian'],
    senegal: ['senegalese'],
    ghana: ['ghanaian'],
    nigeria: ['nigerian'],
    ethiopia: ['ethiopian', 'ethio'],
    morocco: ['moroccan'],
    egypt: ['egyptian'],
    japan: ['japanese'],
    china: ['chinese'],
    france: ['french'],
    spain: ['spanish'],
    portugal: ['portuguese'],
    greece: ['greek'],
    turkey: ['turkish'],
    ireland: ['irish'],
    scotland: ['scottish'],
    wales: ['welsh'],
    england: ['english'],
    argentina: ['argentinian', 'argentine'],
    peru: ['peruvian'],
    afghanistan: ['afghan'],
    cambodia: ['cambodian'],
    madagascar: ['malagasy'],
    palestine: ['palestinian'],
    usa: ['american']
  };
  /** Extra genre seeds when country tags are sparse / adjective match misses. */
  var COUNTRY_GENRE_BRIDGES = {
    india: ['indian-classical-raga', 'bollywood-filmi'],
    cuba: ['afro-cuban-son', 'salsa'],
    brazil: ['brazilian'],
    jamaica: ['reggae-dancehall-ragga'],
    mali: ['desert-blues-sahara', 'tuareg'],
    argentina: ['tango'],
    morocco: ['gnawa']
  };
  /** Region → genre seeds (same role as COUNTRY_GENRE_BRIDGES). */
  var REGION_GENRE_BRIDGES = {
    'latin-america': ['salsa', 'cumbia', 'brazilian', 'afro-cuban-son', 'tango'],
    caribbean: ['reggae-dancehall-ragga', 'salsa', 'calypso', 'mento'],
    'west-africa': ['highlife', 'afrobeat', 'afrobeats', 'mbalax'],
    'sahara-sahel': ['desert-blues-sahara', 'tuareg', 'gnawa'],
    maghreb: ['gnawa', 'arabic-middle-eastern'],
    'middle-east': ['arabic-middle-eastern'],
    balkans: ['balkan', 'klezmer-jewish'],
    'south-asia': ['indian-classical-raga', 'bollywood-filmi'],
    mediterranean: ['flamenco', 'fado', 'rebetika']
  };
  /**
   * Broad style tags that must NOT enter country prefer keys via majority
   * frequency. Mali products often carry `folk`; admitting it floods the
   * prefer pool with Johnny Cash / roots bestsellers and soft-fills away
   * from West Africa. Explicit COUNTRY_GENRE_BRIDGES still win.
   */
  var GENERIC_COUNTRY_FOCUS_GENRES = {
    folk: true,
    jazz: true,
    blues: true,
    country: true,
    gospel: true,
    gospel_spiritual: true,
    'gospel-spiritual': true,
    roots: true,
    acoustic: true,
    world: true,
    'world-music': true,
    'world-compilation': true,
    instrumental: true,
    vocals: true,
    pop: true,
    rock: true,
    classical: true,
    soundtrack: true,
    spoken: true,
    other: true,
    'other-non-music': true
  };
  var FY_SOFTMAX_TEMP = 80;
  var FY_IPS_CLIP = 10;
  var EMB_FOCUS_TIE_SCALE = 48;
  var U_EMB_KEY = 'wmn_u_emb_v1';
  var U_EMB_ALPHA = 0.35;
  /** Expected-utility multipliers for entity kinds (catalog For you scorer). */
  var ENTITY_SCORE_WEIGHT = {
    country: 1.4,
    region: 1.4,
    genre: 1.0,
    artist: 1.0,
    imprint: 0.5,
    product: 0.6
  };
  var EXPLORE_KAPPA0 = 18;
  var EXPLORE_TAU = 80;
  var NEG_GAMMA = 42;
  var NEG_ARTIST_SOFT = 0.15;
  var MAX_NEGATIVES = 80;
  /** Pull interest matches onto collection For you when Shopify page 1 omits them. */
  var COLLECTION_INTEREST_INJECT = 6;
  var DISCOVER_LIMIT = 32;
  var SLOT_CONTINUE = 2;
  var SLOT_RELATED = 8;
  var SLOT_GENRE = 8;
  var SLOT_EXPLOIT = 6;
  var SLOT_EXPLORE = 8;
  var GRID_SELECTORS = [
    '.featured-collection .product-list',
    '.product-list.collection-matrix',
    '.collection-matrix.product-list',
    '.collection-template-section .product-list',
    '.collection-template .product-list',
    '.cart_content .product-list',
    '.cart-section .product-list',
    '.wmn-shopify-recs .product-list',
    '#wmn-for-you-grid'
  ];

  // Shopify sorts that should keep server order (no client algorithm reorder).
  var EXPLICIT_SHOPIFY_SORTS = {
    'title-ascending': true,
    'title-descending': true,
    'price-ascending': true,
    'price-descending': true,
    'created-ascending': true,
    'created-descending': true,
    'best-selling': true,
    manual: true
  };

  function now() {
    return Date.now();
  }

  function readUEmb() {
    try {
      var raw = window.sessionStorage.getItem(U_EMB_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.v) && parsed.v.length) return parsed;
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function writeUEmb(vec) {
    if (!vec || !vec.length) return;
    var payload = { t: now(), v: vec };
    try {
      window.sessionStorage.setItem(U_EMB_KEY, JSON.stringify(payload));
    } catch (e) {
      /* ignore */
    }
    writeInterestProfile({ uEmb: payload });
  }

  function interestProfileKey() {
    var meta = document.querySelector('meta[name="wmn-customer-id"]');
    var cid = meta && meta.getAttribute('content');
    if (cid && /^\d+$/.test(cid)) {
      return INTEREST_KEY + '_c' + cid;
    }
    return INTEREST_KEY;
  }

  function emptyInterestProfile() {
    return { v: 1, t: 0, focus: null, uEmb: null };
  }

  function readInterestProfile() {
    try {
      var raw = window.localStorage.getItem(interestProfileKey());
      if (!raw) return null;
      var p = JSON.parse(raw);
      if (!p || p.v !== 1) return null;
      if (!p.t || now() - p.t > INTEREST_TTL_MS) return null;
      return p;
    } catch (e) {
      return null;
    }
  }

  /**
   * Merge patch into durable interest profile. Refreshes profile.t.
   * @param {{ focus?: object|null, uEmb?: object|null }} patch
   */
  function writeInterestProfile(patch) {
    patch = patch || {};
    var prev = null;
    try {
      var raw = window.localStorage.getItem(interestProfileKey());
      if (raw) prev = JSON.parse(raw);
    } catch (e) {
      prev = null;
    }
    if (!prev || typeof prev !== 'object') prev = emptyInterestProfile();
    var next = {
      v: 1,
      t: now(),
      focus: patch.focus !== undefined ? patch.focus : prev.focus || null,
      uEmb: patch.uEmb !== undefined ? patch.uEmb : prev.uEmb || null
    };
    try {
      window.localStorage.setItem(interestProfileKey(), JSON.stringify(next));
    } catch (e2) {
      /* ignore */
    }
    scheduleCloudWriteback();
  }

  function l2Normalize(vec) {
    if (!vec || !vec.length) return vec;
    var n2 = 0;
    var i;
    for (i = 0; i < vec.length; i++) n2 += vec[i] * vec[i];
    n2 = Math.sqrt(n2) || 1;
    for (i = 0; i < vec.length; i++) vec[i] /= n2;
    return vec;
  }

  /**
   * Blend product embedding into session u_emb (intentional hover/click only).
   */
  function updateUEmbFromHandle(handle) {
    if (!handle || !graphReady()) return;
    try {
      if (
        !window.wmnCatalogGraph ||
        typeof window.wmnCatalogGraph.getProductEmbedding !== 'function'
      ) {
        return;
      }
      var pe = window.wmnCatalogGraph.getProductEmbedding(handle);
      if (!pe || !pe.length) return;
      var prev = readUEmb();
      var next;
      var i;
      if (!prev || !prev.v || prev.v.length !== pe.length) {
        next = pe.slice();
      } else {
        next = new Array(pe.length);
        for (i = 0; i < pe.length; i++) {
          next[i] =
            (1 - U_EMB_ALPHA) * prev.v[i] + U_EMB_ALPHA * pe[i];
        }
        l2Normalize(next);
      }
      writeUEmb(next);
    } catch (e) {
      /* ignore */
    }
  }

  function embDotForHandle(handle, uEmb) {
    if (!handle || !uEmb || !uEmb.length) return 0;
    try {
      if (
        !window.wmnCatalogGraph ||
        typeof window.wmnCatalogGraph.getProductEmbedding !== 'function' ||
        typeof window.wmnCatalogGraph.dotEmbedding !== 'function'
      ) {
        return 0;
      }
      var pe = window.wmnCatalogGraph.getProductEmbedding(handle);
      if (!pe) return 0;
      return window.wmnCatalogGraph.dotEmbedding(uEmb, pe);
    } catch (e) {
      return 0;
    }
  }

  function storageKey() {
    var meta = document.querySelector('meta[name="wmn-customer-id"]');
    var cid = meta && meta.getAttribute('content');
    if (cid && /^\d+$/.test(cid)) {
      return BASE_KEY + '_c' + cid;
    }
    return BASE_KEY;
  }

  function emptyStore() {
    return { v: 1, events: [], entities: {}, negatives: {} };
  }

  function parseStore(raw) {
    if (!raw) return emptyStore();
    try {
      var data = JSON.parse(raw);
      if (!data || !Array.isArray(data.events)) return emptyStore();
      if (!data.entities || typeof data.entities !== 'object') data.entities = {};
      if (!data.negatives || typeof data.negatives !== 'object') data.negatives = {};
      return data;
    } catch (e) {
      return emptyStore();
    }
  }

  function capNegatives(negatives) {
    var keys = Object.keys(negatives || {});
    if (keys.length <= MAX_NEGATIVES) return negatives || {};
    keys.sort(function (a, b) {
      return (negatives[b] || 0) - (negatives[a] || 0);
    });
    var out = {};
    for (var i = 0; i < MAX_NEGATIVES; i++) {
      out[keys[i]] = negatives[keys[i]];
    }
    return out;
  }

  function mergeNegativeMaps(a, b) {
    var out = {};
    function take(src) {
      if (!src) return;
      for (var k in src) {
        if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
        out[k] = Math.max(out[k] || 0, src[k] || 0);
      }
    }
    take(a);
    take(b);
    return capNegatives(out);
  }

  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[\/\(\)'''']/g, ' ')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 100);
  }

  function ensureGraphWarm() {
    if (window.wmnCatalogGraph && typeof window.wmnCatalogGraph.ensureLoaded === 'function') {
      window.wmnCatalogGraph.ensureLoaded().catch(function () {});
    }
  }

  function graphReady() {
    return !!(
      window.wmnCatalogGraph &&
      typeof window.wmnCatalogGraph.getData === 'function' &&
      window.wmnCatalogGraph.getData()
    );
  }

  /** Resolve payload → entity ids (graph when ready, else PDP/artist/imprint/genres). */
  function resolveEntities(payload) {
    var ids = [];
    var seen = {};
    function add(id) {
      if (!id || seen[id]) return;
      seen[id] = true;
      ids.push(id);
    }
    if (!payload) return ids;
    var handle = payload.handle || '';
    if (handle && graphReady()) {
      var resolved = window.wmnCatalogGraph.resolveProduct(handle);
      add('product:' + handle);
      (resolved.artists || []).forEach(function (a) {
        add('artist:' + a);
      });
      (resolved.genres || []).forEach(function (g) {
        add('genre:' + g);
      });
      (resolved.countries || []).forEach(function (c) {
        if (c && c !== 'celtic') add('country:' + c);
      });
      (resolved.regions || []).forEach(function (r) {
        if (r) add('region:' + r);
      });
      (resolved.imprints || []).forEach(function (i) {
        add('imprint:' + i);
      });
      // Merge caller genres (e.g. title-token / country aliases from rails)
      var extraG = payload.genres || [];
      for (var eg = 0; eg < extraG.length; eg++) {
        var egSlug = handleize(extraG[eg]);
        if (egSlug) add('genre:' + egSlug);
      }
      var extraC = payload.countries || [];
      for (var ec = 0; ec < extraC.length; ec++) {
        var ecSlug = handleize(extraC[ec]);
        if (ecSlug && ecSlug !== 'celtic') add('country:' + ecSlug);
      }
      var extraR = payload.regions || [];
      for (var er = 0; er < extraR.length; er++) {
        var erSlug = handleize(extraR[er]);
        if (erSlug) add('region:' + erSlug);
      }
      // Title/handle geo aliases when graph country tags are sparse
      inferGeoFromText(payload.title || handle, add);
      return ids;
    }
    if (handle) add('product:' + handle);
    if (payload.artist) {
      var aSlug = handleize(payload.artist);
      if (aSlug && aSlug !== 'various' && aSlug !== 'various-artists') {
        add('artist:' + aSlug);
      }
    }
    if (payload.imprint) {
      var iSlug = handleize(payload.imprint);
      if (iSlug) add('imprint:' + iSlug);
    }
    var gs = payload.genres || [];
    for (var i = 0; i < gs.length; i++) {
      var gSlug = handleize(gs[i]);
      if (gSlug) add('genre:' + gSlug);
    }
    var extraC2 = payload.countries || [];
    for (var ec2 = 0; ec2 < extraC2.length; ec2++) {
      var ecSlug2 = handleize(extraC2[ec2]);
      if (ecSlug2 && ecSlug2 !== 'celtic') add('country:' + ecSlug2);
    }
    var extraR2 = payload.regions || [];
    for (var er2 = 0; er2 < extraR2.length; er2++) {
      var erSlug2 = handleize(extraR2[er2]);
      if (erSlug2) add('region:' + erSlug2);
    }
    inferGeoFromText(payload.title || handle, add);
    return ids;
  }

  /**
   * Sparse country tags: infer country/region from title/handle text.
   * Celtic never becomes a country. Mississippi → USA.
   */
  function inferGeoFromText(text, add) {
    if (!add || !text) return;
    var s = String(text || '')
      .toLowerCase()
      .replace(/['']/g, '');
    if (!s) return;

    function country(slug) {
      if (slug && slug !== 'celtic') add('country:' + slug);
    }
    function region(slug) {
      if (slug) add('region:' + slug);
    }

    // Regions / pan-geo phrases first
    if (/\bwest\s*africa\b|\bwestafrica\b/.test(s)) {
      region('west-africa');
      region('africa');
    } else if (/\beast\s*africa\b/.test(s)) {
      region('east-africa');
      region('africa');
    } else if (/\bnorth\s*africa\b|\bmaghreb\b/.test(s)) {
      region('north-africa');
      region('maghreb');
      region('africa');
    } else if (/\bsouth(ern)?\s*africa\b/.test(s)) {
      region('southern-africa');
      region('africa');
    } else if (/\bafrica\b|\bafrican\b/.test(s)) {
      region('africa');
    }
    if (/\bsahara\b|\bsahel\b/.test(s)) {
      region('sahara-sahel');
      region('africa');
    }
    if (/\bceltic\b/.test(s)) region('celtic');
    if (/\bmississippi\b/.test(s)) {
      country('usa');
      region('north-america');
    }

    // Common country title tokens (Rough Guides / Think Global)
    var countries = [
      ['mali', 'mali'],
      ['senegal', 'senegal'],
      ['guinea', 'guinea'],
      ['ghana', 'ghana'],
      ['nigeria', 'nigeria'],
      ['ethiopia', 'ethiopia'],
      ['sudan', 'sudan'],
      ['morocco', 'morocco'],
      ['algeria', 'algeria'],
      ['tunisia', 'tunisia'],
      ['egypt', 'egypt'],
      ['kenya', 'kenya'],
      ['tanzania', 'tanzania'],
      ['cameroon', 'cameroon'],
      ['madagascar', 'madagascar'],
      ['congo', 'congo'],
      ['cuba', 'cuba'],
      ['brazil', 'brazil'],
      ['jamaica', 'jamaica'],
      ['india', 'india'],
      ['pakistan', 'pakistan'],
      ['afghanistan', 'afghanistan'],
      ['cambodia', 'cambodia'],
      ['china', 'china'],
      ['japan', 'japan'],
      ['korea', 'korea'],
      ['ireland', 'ireland'],
      ['scotland', 'scotland'],
      ['wales', 'wales'],
      ['england', 'england'],
      ['portugal', 'portugal'],
      ['spain', 'spain'],
      ['france', 'france'],
      ['italy', 'italy'],
      ['greece', 'greece'],
      ['turkey', 'turkey'],
      ['hungary', 'hungary'],
      ['romania', 'romania'],
      ['bulgaria', 'bulgaria'],
      ['poland', 'poland'],
      ['russia', 'russia'],
      ['ukraine', 'ukraine'],
      ['argentina', 'argentina'],
      ['peru', 'peru'],
      ['colombia', 'colombia'],
      ['mexico', 'mexico'],
      ['haiti', 'haiti'],
      ['trinidad', 'trinidad'],
      ['new orleans', 'usa'],
      ['americana', 'usa'],
      ['delta blues', 'usa']
    ];
    var i;
    for (i = 0; i < countries.length; i++) {
      var needle = countries[i][0];
      var slug = countries[i][1];
      if (s.indexOf(needle) !== -1) country(slug);
    }
  }

  /**
   * When a signal carries geo, amplify country/region and damp noisy genres
   * (graph genre mistakes like brazilian-on-Mali must not drown Africa).
   */
  function entityBumpScaleMap(entityIds, kind) {
    var hasGeo = false;
    var i;
    for (i = 0; i < (entityIds || []).length; i++) {
      var p0 = String(entityIds[i]).split(':')[0];
      if (p0 === 'country' || p0 === 'region') {
        hasGeo = true;
        break;
      }
    }
    var map = {};
    var hoverish = kind === 'hover' || kind === 'search_click' || kind === 'atc';
    for (i = 0; i < (entityIds || []).length; i++) {
      var id = entityIds[i];
      if (!id) continue;
      var kindPart = id.split(':')[0];
      var scale = 1;
      if (kindPart === 'country' || kindPart === 'region') {
        scale = hoverish ? 2.2 : kind === 'visible' ? 0.6 : 1.6;
      } else if (kindPart === 'genre' && hasGeo) {
        scale = 0.05;
      } else if (kindPart === 'product' && hoverish) {
        scale = 1.15;
      }
      map[id] = scale;
    }
    return map;
  }

  function capEntities(entities) {
    var keys = Object.keys(entities || {});
    if (keys.length <= MAX_ENTITIES) return entities || {};
    keys.sort(function (a, b) {
      return (entities[b] || 0) - (entities[a] || 0);
    });
    var out = {};
    for (var i = 0; i < MAX_ENTITIES; i++) {
      out[keys[i]] = entities[keys[i]];
    }
    return out;
  }

  /** Decay map since last entity touch, then add bumps.
   *  scaleOrMap: number (all ids) or { id: scale } map.
   */
  function bumpEntities(store, entityIds, kind, scaleOrMap) {
    if (!store) return;
    store.entities = store.entities || {};
    var kindMult = KIND_WEIGHT[kind || 'view'] || 1;
    var isMap = scaleOrMap && typeof scaleOrMap === 'object';
    var factor = 1;
    if (store.et) {
      factor = Math.pow(0.5, Math.max(0, now() - store.et) / HALF_LIFE_MS);
    }
    var ents = store.entities;
    var k;
    for (k in ents) {
      if (Object.prototype.hasOwnProperty.call(ents, k)) {
        ents[k] = ents[k] * factor;
        if (ents[k] < 0.05) delete ents[k];
      }
    }
    var list = entityIds || [];
    for (var i = 0; i < list.length; i++) {
      var id = list[i];
      if (!id) continue;
      var scale = 1;
      if (isMap) {
        scale = scaleOrMap[id] != null ? scaleOrMap[id] : 1;
      } else if (scaleOrMap != null) {
        scale = scaleOrMap;
      }
      ents[id] = (ents[id] || 0) + VIEW_WEIGHT * kindMult * scale;
    }
    store.et = now();
    store.entities = capEntities(ents);
  }

  function mergeEntityMaps(a, b) {
    var out = {};
    function take(src) {
      if (!src) return;
      for (var k in src) {
        if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
        out[k] = Math.max(out[k] || 0, src[k] || 0);
      }
    }
    take(a);
    take(b);
    return capEntities(out);
  }

  function getEntityPrefs() {
    var store = readStore();
    return store.entities || {};
  }

  function readFocus() {
    try {
      var raw = window.sessionStorage.getItem(FOCUS_KEY);
      if (!raw) return null;
      var f = JSON.parse(raw);
      if (!f || !f.t || now() - f.t > FOCUS_TTL_MS) return null;
      return f;
    } catch (e) {
      return null;
    }
  }

  /** Session focus, or durable focus when session expired (for update merge). */
  function readFocusOrDurable() {
    var s = readFocus();
    if (s) return s;
    var prof = readInterestProfile();
    if (prof && prof.focus && prof.focus.t) return prof.focus;
    return null;
  }

  function writeFocus(focus) {
    try {
      window.sessionStorage.setItem(FOCUS_KEY, JSON.stringify(focus));
    } catch (e) {
      /* ignore */
    }
    writeInterestProfile({ focus: focus });
    dispatchFocusChanged(focus);
  }

  function dispatchFocusChanged(focus) {
    try {
      document.dispatchEvent(
        new CustomEvent('wmn:focus-changed', { detail: focus || null })
      );
    } catch (errFocus) {
      /* ignore */
    }
  }

  /** Slug / handle → short human phrase (spaces, no full album titles). */
  function humanizeSlug(slug) {
    return String(slug || '')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Prefer 2–4 words, capped at SEARCH_GHOST_MAX chars.
   * @param {string} phrase
   * @returns {string}
   */
  function shortenGhostPhrase(phrase) {
    var s = String(phrase || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!s) return '';
    var words = s.split(' ');
    var out = [];
    var i;
    for (i = 0; i < words.length && out.length < 4; i++) {
      if (!words[i]) continue;
      var next = out.length ? out.join(' ') + ' ' + words[i] : words[i];
      if (next.length > SEARCH_GHOST_MAX) break;
      out.push(words[i]);
    }
    if (!out.length) {
      return s.slice(0, SEARCH_GHOST_MAX).replace(/\s+\S*$/, '').trim();
    }
    return out.join(' ');
  }

  /**
   * Temu-style ghost suggestion from session focus.
   * Priority: short q → genre → artist → country/region. Never album title.
   * @param {object} [focus]
   * @returns {string}
   */
  function getSearchSuggestion(focus) {
    focus = focus || readFocus();
    if (!focus) return '';
    var q = String(focus.q || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (q && q.length <= SEARCH_GHOST_MAX) {
      return shortenGhostPhrase(q) || q;
    }
    var genre =
      focus.primaryGenre || (focus.genres && focus.genres[0]) || '';
    if (genre) {
      var g = shortenGhostPhrase(humanizeSlug(genre));
      if (g) return g;
    }
    var artist =
      focus.primaryArtist || (focus.artists && focus.artists[0]) || '';
    if (artist) {
      var a = shortenGhostPhrase(humanizeSlug(artist));
      if (a) return a;
    }
    var geo = focus.primaryCountry || focus.primaryRegion || '';
    if (geo) {
      var c = shortenGhostPhrase(humanizeSlug(geo));
      if (c) return c;
    }
    return '';
  }

  function isHeaderSearchInputEl(input) {
    return !!(
      input &&
      input.name === 'q' &&
      input.closest &&
      input.closest(
        '.wmn-header__search, .header_search_form, [data-dropdown="search"]'
      )
    );
  }

  function allHeaderSearchInputs() {
    return document.querySelectorAll(SEARCH_INPUT_SEL);
  }

  function refreshSearchPlaceholder(input) {
    if (!input) return;
    var form = input.closest && input.closest('form');
    var ghost =
      (form && form.getAttribute('data-wmn-search-ghost')) ||
      input.getAttribute('data-wmn-search-ghost') ||
      '';
    var hasValue = !!(
      input.value && String(input.value).replace(/^\s+|\s+$/g, '')
    );
    var focused = document.activeElement === input;
    if (ghost && !hasValue && !focused) {
      input.placeholder = ghost;
    } else {
      input.placeholder = DEFAULT_SEARCH_PLACEHOLDER;
    }
  }

  function applySearchGhost(suggestion) {
    suggestion = suggestion || '';
    var inputs = allHeaderSearchInputs();
    var i;
    for (i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var form = input.closest && input.closest('form');
      var host = form || input;
      if (suggestion) {
        host.setAttribute('data-wmn-search-ghost', suggestion);
      } else {
        host.removeAttribute('data-wmn-search-ghost');
      }
      refreshSearchPlaceholder(input);
    }
  }

  /** Header search ghost placeholders from session focus (sitewide). */
  function bindSearchGhost() {
    if (!window.__wmnSearchGhostBound) {
      window.__wmnSearchGhostBound = true;

      document.addEventListener('wmn:focus-changed', function (e) {
        var focus = e && e.detail;
        applySearchGhost(getSearchSuggestion(focus));
      });

      document.addEventListener(
        'focusin',
        function (e) {
          if (isHeaderSearchInputEl(e.target)) {
            refreshSearchPlaceholder(e.target);
          }
        },
        true
      );

      document.addEventListener(
        'focusout',
        function (e) {
          if (!isHeaderSearchInputEl(e.target)) return;
          var input = e.target;
          setTimeout(function () {
            refreshSearchPlaceholder(input);
          }, 0);
        },
        true
      );

      document.addEventListener(
        'input',
        function (e) {
          if (isHeaderSearchInputEl(e.target)) {
            refreshSearchPlaceholder(e.target);
          }
        },
        true
      );

      document.addEventListener(
        'submit',
        function (e) {
          var form = e.target;
          if (!form || !form.querySelector) return;
          var input = form.querySelector('input[name="q"]');
          if (!isHeaderSearchInputEl(input)) return;
          var q = String(input.value || '').replace(/^\s+|\s+$/g, '');
          var ghost =
            form.getAttribute('data-wmn-search-ghost') ||
            input.getAttribute('data-wmn-search-ghost') ||
            '';
          if (!q && ghost) {
            input.value = ghost;
          }
        },
        true
      );

      document.addEventListener(
        'click',
        function (e) {
          var t = e.target;
          if (!t || !t.closest) return;
          var btn = t.closest('[data-wmn-search-clear]');
          if (!btn) return;
          var form = btn.closest('form');
          var input = form && form.querySelector('input[name="q"]');
          if (!input) return;
          setTimeout(function () {
            input.placeholder = DEFAULT_SEARCH_PLACEHOLDER;
            refreshSearchPlaceholder(input);
          }, 0);
        },
        true
      );
    }
    applySearchGhost(getSearchSuggestion());
  }

  /**
   * Copy durable interest → session when session focus is empty/expired.
   * Does not bump durable t (reload must not look like a new intentional write).
   */
  function hydrateInterestFromDurable() {
    if (readFocus()) return;
    var prof = readInterestProfile();
    if (!prof || !prof.focus || !prof.focus.t) return;
    try {
      window.sessionStorage.setItem(FOCUS_KEY, JSON.stringify(prof.focus));
    } catch (e) {
      /* ignore */
    }
    if (prof.uEmb && prof.uEmb.v && prof.uEmb.v.length) {
      try {
        window.sessionStorage.setItem(U_EMB_KEY, JSON.stringify(prof.uEmb));
      } catch (e2) {
        /* ignore */
      }
    }
  }

  /**
   * Apply cloud intent.focus / uEmb when newer than local durable (or local missing).
   */
  function applyCloudInterest(cloud) {
    if (!cloud || !cloud.intent) return;
    var cloudFocus = cloud.intent.focus || null;
    var cloudUEmb =
      cloud.intent.uEmb && cloud.intent.uEmb.v ? cloud.intent.uEmb : null;
    if (!cloudFocus && !cloudUEmb) return;
    var cloudT =
      (cloudFocus && cloudFocus.t) ||
      (cloudUEmb && cloudUEmb.t) ||
      cloud.intent.updatedAt ||
      0;
    var local = null;
    try {
      var raw = window.localStorage.getItem(interestProfileKey());
      if (raw) local = JSON.parse(raw);
    } catch (e) {
      local = null;
    }
    var localT = (local && local.t) || 0;
    if (localT && cloudT && localT >= cloudT) {
      // Local newer — queue writeback so metafield catches up.
      scheduleCloudWriteback();
      return;
    }
    var next = {
      v: 1,
      t: cloudT || now(),
      focus: cloudFocus || (local && local.focus) || null,
      uEmb: cloudUEmb || (local && local.uEmb) || null
    };
    try {
      window.localStorage.setItem(interestProfileKey(), JSON.stringify(next));
    } catch (e2) {
      /* ignore */
    }
    // Hydrate session from applied cloud (session-only writes).
    if (next.focus) {
      try {
        window.sessionStorage.setItem(FOCUS_KEY, JSON.stringify(next.focus));
      } catch (e3) {
        /* ignore */
      }
    }
    if (next.uEmb) {
      try {
        window.sessionStorage.setItem(U_EMB_KEY, JSON.stringify(next.uEmb));
      } catch (e4) {
        /* ignore */
      }
    }
  }

  /** Push val to front; if already present, re-promote (splice + unshift). */
  function uniqPush(arr, val, max) {
    if (!val) return;
    var ix = arr.indexOf(val);
    if (ix !== -1) arr.splice(ix, 1);
    arr.unshift(val);
    if (arr.length > (max || 8)) arr.length = max || 8;
  }

  /** Session focus vector — last search / view lane (decays over FOCUS_TTL_MS). */
  function updateFocus(opts) {
    opts = opts || {};
    try {
      if (
        opts.q &&
        window.wmnQueryGuard &&
        typeof window.wmnQueryGuard.isBlocked === 'function' &&
        window.wmnQueryGuard.isBlocked(opts.q)
      ) {
        // Keep other focus fields; never persist a dirty stem as focus.q.
        var scrubbed = {};
        var ok;
        for (ok in opts) {
          if (Object.prototype.hasOwnProperty.call(opts, ok) && ok !== 'q') {
            scrubbed[ok] = opts[ok];
          }
        }
        opts = scrubbed;
      }
    } catch (eGuard) {
      /* ignore */
    }
    var prev = readFocusOrDurable() || {
      t: 0,
      artists: [],
      genres: [],
      handles: [],
      primaryGenre: '',
      primaryArtist: '',
      primaryCountry: '',
      primaryRegion: '',
      primaryKind: '',
      q: ''
    };
    var artists = (prev.artists || []).slice();
    var genres = (prev.genres || []).slice();
    var handles = (prev.handles || []).slice();
    var i;
    if (opts.artists) {
      for (i = opts.artists.length - 1; i >= 0; i--) {
        uniqPush(artists, opts.artists[i], 6);
      }
    }
    if (opts.genres) {
      for (i = opts.genres.length - 1; i >= 0; i--) {
        uniqPush(genres, opts.genres[i], 6);
      }
    }
    if (opts.handles) {
      for (i = opts.handles.length - 1; i >= 0; i--) {
        uniqPush(handles, opts.handles[i], 6);
      }
    }
    // Primary = most recent intentional genre/artist (front after uniqPush).
    var primaryGenre = genres[0] || prev.primaryGenre || '';
    var primaryArtist = artists[0] || prev.primaryArtist || '';
    // primaryKind / geo primaries come from THIS intentional payload when set.
    var primaryKind =
      opts.primaryKind != null && opts.primaryKind !== ''
        ? opts.primaryKind
        : prev.primaryKind || '';
    var primaryCountry =
      opts.primaryCountry != null
        ? opts.primaryCountry
        : prev.primaryCountry || '';
    var primaryRegion =
      opts.primaryRegion != null
        ? opts.primaryRegion
        : prev.primaryRegion || '';
    if (opts.primaryKind === 'genre' || opts.primaryKind === 'artist') {
      primaryCountry = '';
      primaryRegion = '';
    }
    if (opts.primaryKind === 'country') {
      primaryRegion = '';
    }
    if (opts.primaryKind === 'region') {
      primaryCountry = '';
    }
    if (!primaryKind) {
      if (primaryCountry) primaryKind = 'country';
      else if (primaryRegion) primaryKind = 'region';
      else if (primaryGenre) primaryKind = 'genre';
      else if (primaryArtist) primaryKind = 'artist';
    }
    writeFocus({
      t: now(),
      artists: artists,
      genres: genres,
      handles: handles,
      primaryGenre: primaryGenre,
      primaryArtist: primaryArtist,
      primaryCountry: primaryCountry,
      primaryRegion: primaryRegion,
      primaryKind: primaryKind,
      q: opts.q != null ? String(opts.q) : prev.q || ''
    });
  }

  /**
   * Re-promote session focus without entity bumps (re-hover after once-gate).
   * Dispatches prefs so For you subtitle / tail can refresh.
   */
  function promoteFocus(payload, query) {
    if (!payload && !query) return;
    updateFocusFromPayload(payload || {}, query);
    dispatchPrefsUpdated(readStore());
  }

  function updateFocusFromPayload(payload, query) {
    if (!payload && !query) return;
    payload = payload || {};
    var artists = [];
    var genres = [];
    var handles = [];
    var countries = [];
    var regions = [];
    var ids = resolveEntities(payload);
    var i;
    for (i = 0; i < ids.length; i++) {
      var parts = String(ids[i]).split(':');
      if (parts[0] === 'artist' && parts[1]) artists.push(parts[1]);
      if (parts[0] === 'genre' && parts[1]) genres.push(parts[1]);
      if (parts[0] === 'product' && parts[1]) handles.push(parts[1]);
      if (parts[0] === 'country' && parts[1]) countries.push(parts[1]);
      if (parts[0] === 'region' && parts[1]) regions.push(parts[1]);
    }
    if (payload.handle) handles.push(payload.handle);
    if (payload.artist) {
      var as = handleize(payload.artist);
      if (as && as !== 'various') artists.push(as);
    }
    // Prefer title-token genre when title clearly names a tagged genre
    // (Jazz Legends with blues+jazz tags → jazz primary for labels).
    var titleMatchedGenre = '';
    if (genres.length && payload.title) {
      var titleLow = String(payload.title).toLowerCase();
      for (i = 0; i < genres.length; i++) {
        var gSlug = genres[i];
        var gWords = String(gSlug || '').replace(/-/g, ' ');
        if (
          gSlug &&
          (titleLow.indexOf(gWords) !== -1 ||
            titleLow.indexOf(gSlug) !== -1)
        ) {
          genres = genres.slice();
          genres.splice(i, 1);
          genres.unshift(gSlug);
          titleMatchedGenre = gSlug;
          break;
        }
      }
    }
    // Title-tail intent: "… Ethiopian Jazz" / "… World Jazz" → genre,
    // "… Music of Mali" / "… Spiritual India" → country.
    // Ending genre word beats country tags so Jazz isn't crushed by ethiopia/brazil.
    var titleTailGenre = '';
    if (genres.length && payload.title) {
      var titleNorm = String(payload.title)
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      for (i = 0; i < genres.length; i++) {
        var tg = genres[i];
        if (!tg) continue;
        var tgWords = String(tg).replace(/-/g, ' ');
        var tgLast = tgWords.split(' ').pop();
        if (
          titleNorm === tgWords ||
          titleNorm.slice(-(tgWords.length + 1)) === ' ' + tgWords ||
          titleNorm.slice(-(tgLast.length + 1)) === ' ' + tgLast
        ) {
          titleTailGenre = tg;
          break;
        }
      }
    }
    // Title ending in a genre word ("… Ethiopian Jazz", "… World Jazz")
    // owns session intent over country tags. Otherwise country-in-graph wins
    // ("… Music of Mali", "… Urban Mali").
    var primaryKind = '';
    var primaryCountry = '';
    var primaryRegion = '';
    if (titleTailGenre) {
      primaryKind = 'genre';
      genres = genres.slice();
      var tix = genres.indexOf(titleTailGenre);
      if (tix > 0) {
        genres.splice(tix, 1);
        genres.unshift(titleTailGenre);
      } else if (tix === -1) {
        genres.unshift(titleTailGenre);
      }
    } else if (countries.length) {
      primaryKind = 'country';
      primaryCountry = countries[0];
    } else if (regions.length) {
      primaryKind = 'region';
      primaryRegion = regions[0];
    } else if (genres.length) {
      primaryKind = 'genre';
    } else if (artists.length) {
      primaryKind = 'artist';
    }
    updateFocus({
      artists: artists,
      genres: genres,
      handles: handles,
      primaryKind: primaryKind,
      primaryCountry: primaryCountry,
      primaryRegion: primaryRegion,
      q: query || ''
    });
    if (payload.handle) updateUEmbFromHandle(payload.handle);
  }

  function topEntityIds(prefs, limit, kinds) {
    var pairs = [];
    var allow = kinds || null;
    Object.keys(prefs || {}).forEach(function (id) {
      if (allow) {
        var kind = id.split(':')[0];
        if (allow.indexOf(kind) === -1) return;
      }
      pairs.push({ id: id, w: prefs[id] || 0 });
    });
    pairs.sort(function (a, b) {
      return b.w - a.w;
    });
    return pairs.slice(0, limit || 8).map(function (p) {
      return p.id;
    });
  }

  /** True when lean/committed — field is the sole ranking brain (no entity/focus mix). */
  function fieldOwnsRanking() {
    try {
      if (!window.wmnIntentField) return false;
      if (typeof window.wmnIntentField.ownsRanking === 'function') {
        return !!window.wmnIntentField.ownsRanking();
      }
      if (typeof window.wmnIntentField.getMode !== 'function') return false;
      var m = window.wmnIntentField.getMode();
      return m === 'lean' || m === 'committed';
    } catch (e) {
      return false;
    }
  }

  /**
   * Discover For-you should use the intent field whenever it has energy nodes,
   * not only lean/committed. A single pasted "cuba" settle stays explore but
   * still injects genre/product nodes — ignoring them freezes seed order.
   */
  function fieldHasRankableEnergy() {
    if (fieldOwnsRanking()) return true;
    try {
      if (
        !window.wmnIntentField ||
        typeof window.wmnIntentField.getField !== 'function'
      ) {
        return false;
      }
      var f = window.wmnIntentField.getField();
      return !!(f && f.nodes && Object.keys(f.nodes).length > 0);
    } catch (e) {
      return false;
    }
  }

  function scoreHandle(handle) {
    if (!handle) return 0;
    // One brain: when field owns lean/committed, don't also apply entity+focus.
    if (
      fieldOwnsRanking() &&
      window.wmnIntentField &&
      typeof window.wmnIntentField.scoreHandle === 'function'
    ) {
      try {
        return window.wmnIntentField.scoreHandle(handle);
      } catch (eField) {
        /* fall through */
      }
    }
    return scoreHandleEntities(handle, null);
  }

  /**
   * Session focus primary for collection algorithm — mirrors scoreCatalogForYou
   * so leftover Mali/imprint mass cannot drown a typed genre interest.
   * Falls back to intent-field primary when focus has no lane yet.
   */
  function collectionFocusPrimary() {
    var focus = readFocus();
    var genre =
      (focus &&
        (focus.primaryGenre || (focus.genres && focus.genres[0]))) ||
      '';
    var country = (focus && focus.primaryCountry) || '';
    var region = (focus && focus.primaryRegion) || '';
    var kind = (focus && focus.primaryKind) || '';
    if (!kind) {
      if (country) kind = 'country';
      else if (region) kind = 'region';
      else if (genre) kind = 'genre';
      else if (
        focus &&
        (focus.primaryArtist || (focus.artists && focus.artists[0]))
      ) {
        kind = 'artist';
      }
    }
    if (
      genre &&
      kind !== 'country' &&
      kind !== 'region' &&
      kind !== 'genre'
    ) {
      kind = 'genre';
    }
    var dominant =
      (kind === 'genre' && !!genre) ||
      (kind === 'country' && !!country) ||
      (kind === 'region' && !!region);

    // Ghost shows focus.q ("indian classical raga") even when primaryGenre was
    // never set — resolve the query so Store For you matches the placeholder.
    if (
      !dominant &&
      focus &&
      focus.q &&
      graphReady() &&
      typeof window.wmnCatalogGraph.linkQuery === 'function'
    ) {
      try {
        var linked = window.wmnCatalogGraph.linkQuery(String(focus.q)) || [];
        var li;
        for (li = 0; li < linked.length; li++) {
          var row = linked[li];
          if (!row || !row.id) continue;
          if ((row.score || 0) < 50) continue;
          var qp = String(row.id).split(':');
          if (qp[0] === 'genre' && qp[1]) {
            return {
              focus: focus,
              kind: 'genre',
              genre: qp[1],
              country: '',
              region: '',
              dominant: true
            };
          }
          if (qp[0] === 'country' && qp[1]) {
            return {
              focus: focus,
              kind: 'country',
              genre: '',
              country: qp[1],
              region: '',
              dominant: true
            };
          }
          if (qp[0] === 'region' && qp[1]) {
            return {
              focus: focus,
              kind: 'region',
              genre: '',
              country: '',
              region: qp[1],
              dominant: true
            };
          }
        }
      } catch (eQ) {
        /* ignore */
      }
    }

    // Ghost / focus.q can say "indian classical raga" while lean field still
    // ranks Jazz — prefer an explicit focus genre. If focus is empty, adopt
    // the field primary so typed settles still drive Store For you.
    if (!dominant && fieldHasRankableEnergy()) {
      try {
        var pi =
          window.wmnIntentField &&
          typeof window.wmnIntentField.primaryIntent === 'function'
            ? window.wmnIntentField.primaryIntent()
            : null;
        if (pi && pi.id) {
          var parts = String(pi.id).split(':');
          if (parts[0] === 'genre' && parts[1]) {
            return {
              focus: focus,
              kind: 'genre',
              genre: parts[1],
              country: '',
              region: '',
              dominant: true
            };
          }
          if (parts[0] === 'country' && parts[1]) {
            return {
              focus: focus,
              kind: 'country',
              genre: '',
              country: parts[1],
              region: '',
              dominant: true
            };
          }
          if (parts[0] === 'region' && parts[1]) {
            return {
              focus: focus,
              kind: 'region',
              genre: '',
              country: '',
              region: parts[1],
              dominant: true
            };
          }
        }
      } catch (ePi) {
        /* ignore */
      }
    }

    return {
      focus: focus,
      kind: kind,
      genre: genre,
      country: country,
      region: region,
      dominant: dominant
    };
  }

  function collectionScoreCtx() {
    var prim = collectionFocusPrimary();
    var store = readStore();
    return {
      prefs: getEntityPrefs(),
      focus: prim.focus,
      negatives: store.negatives || {},
      dampGeoPrefs: prim.dominant
    };
  }

  /**
   * Country/region interest also matches bridge genres (homepage
   * COUNTRY_GENRE_BRIDGES / REGION_GENRE_BRIDGES) so India focus finds
   * indian-classical-raga / bollywood albums that lack c:india.
   * @returns {'tag'|'bridge'|''}
   */
  function collectionFocusMatchKind(handle, prim) {
    if (!handle || !prim || !prim.dominant || !graphReady()) return '';
    var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
    var genres = resolved.genres || [];
    var i;
    if (prim.kind === 'genre' && prim.genre) {
      if (genres.indexOf(prim.genre) !== -1) return 'tag';
      var focusGenres = (prim.focus && prim.focus.genres) || [];
      for (i = 0; i < focusGenres.length; i++) {
        if (focusGenres[i] && genres.indexOf(focusGenres[i]) !== -1) {
          return 'bridge';
        }
      }
      return '';
    }
    if (prim.kind === 'country' && prim.country) {
      if ((resolved.countries || []).indexOf(prim.country) !== -1) {
        return 'tag';
      }
      var cBridges = COUNTRY_GENRE_BRIDGES[prim.country] || [];
      for (i = 0; i < cBridges.length; i++) {
        if (cBridges[i] && genres.indexOf(cBridges[i]) !== -1) return 'bridge';
      }
      return '';
    }
    if (prim.kind === 'region' && prim.region) {
      if ((resolved.regions || []).indexOf(prim.region) !== -1) return 'tag';
      var rBridges = REGION_GENRE_BRIDGES[prim.region] || [];
      for (i = 0; i < rBridges.length; i++) {
        if (rBridges[i] && genres.indexOf(rBridges[i]) !== -1) return 'bridge';
      }
      return '';
    }
    return '';
  }

  function productMatchesCollectionFocus(handle, prim) {
    return !!collectionFocusMatchKind(handle, prim);
  }

  /**
   * Hard latch so the current focus lane outranks residual imprint/entity mass
   * on collection pages (FOCUS_GENRE_BONUS alone loses to country:mali × 1.4).
   * Tag/country match = +480; bridge-only / secondary genre = +300.
   */
  function collectionFocusLatch(handle, prim) {
    if (!handle || !prim || !prim.dominant || !graphReady()) return 0;
    var kind = collectionFocusMatchKind(handle, prim);
    if (kind === 'tag') return 480;
    if (kind === 'bridge') return 300;
    return -60;
  }

  function collectionInterestWarm() {
    var prim = collectionFocusPrimary();
    if (prim && prim.dominant) return true;
    return fieldHasRankableEnergy();
  }

  /**
   * Build a focus object scoreCatalogForYou understands from Store primary
   * (includes ghost/linkQuery + intent-field fallbacks).
   */
  function focusFromCollectionPrimary(prim) {
    if (!prim || !prim.dominant) return null;
    var base = prim.focus ? Object.assign({}, prim.focus) : Object.assign({}, readFocus() || {});
    base.primaryKind = prim.kind;
    if (prim.kind === 'genre' && prim.genre) {
      base.primaryGenre = prim.genre;
      var gList = [prim.genre];
      var prev = base.genres || [];
      var gi;
      for (gi = 0; gi < prev.length; gi++) {
        if (prev[gi] && prev[gi] !== prim.genre) gList.push(prev[gi]);
      }
      base.genres = gList;
    } else if (prim.kind === 'country' && prim.country) {
      base.primaryCountry = prim.country;
    } else if (prim.kind === 'region' && prim.region) {
      base.primaryRegion = prim.region;
    }
    return base;
  }

  /** Detached SSR thumbnails so For you can paint/restore the matrix. */
  var collectionSsrStash =
    typeof WeakMap !== 'undefined' ? new WeakMap() : null;

  function ensureCollectionSsrStash(container) {
    if (!container || !collectionSsrStash) return null;
    var existing = collectionSsrStash.get(container);
    if (existing) return existing;
    var nodes = Array.prototype.slice.call(
      container.querySelectorAll('.thumbnail[data-wmn-handle]')
    );
    var loadMore = container.querySelector('.js-load-more, .load-more');
    var stash = {
      nodes: nodes,
      pageSize: nodes.length > 0 ? nodes.length : 24,
      loadMore: loadMore || null
    };
    collectionSsrStash.set(container, stash);
    container.setAttribute('data-wmn-fy-page-size', String(stash.pageSize));
    return stash;
  }

  function clearCollectionForYouPaint(container) {
    if (!container) return;
    var painted = container.querySelectorAll(
      '.thumbnail[data-wmn-fy-paint="1"]'
    );
    var i;
    for (i = 0; i < painted.length; i++) {
      if (painted[i].parentNode) painted[i].parentNode.removeChild(painted[i]);
    }
    container.removeAttribute('data-wmn-fy-painted');
    clearCollectionInterestInjects(container);
  }

  function restoreCollectionSsr(container) {
    if (!container || !collectionSsrStash) return false;
    var stash = collectionSsrStash.get(container);
    if (!stash || !stash.nodes || !stash.nodes.length) return false;
    clearCollectionForYouPaint(container);
    var current = container.querySelectorAll('.thumbnail[data-wmn-handle]');
    for (var i = 0; i < current.length; i++) {
      if (current[i].parentNode) current[i].parentNode.removeChild(current[i]);
    }
    var frag = document.createDocumentFragment();
    for (i = 0; i < stash.nodes.length; i++) {
      frag.appendChild(stash.nodes[i]);
    }
    if (stash.loadMore) frag.appendChild(stash.loadMore);
    container.appendChild(frag);
    container.removeAttribute('data-wmn-aff-sorted');
    return true;
  }

  /**
   * Paint Store collection matrix from shared scoreCatalogForYou ranking.
   * ?page=N slices the ranked pool (page size = SSR page size).
   * Prefer-lane matches lead; remainder is score-ordered fill so Shopify's
   * multi-page pagination never paints a blank grid.
   */
  function paintCollectionForYouMatrix(container) {
    if (!container || !wantsAlgorithmCollectionSort()) return false;
    // A chip already narrowed this page. Rank those albums; don't swap in the whole catalog.
    if (collectionTagFilterActive()) return false;
    if (!graphReady() || !collectionInterestWarm()) return false;

    var prim = collectionFocusPrimary();
    var focusOverride = focusFromCollectionPrimary(prim);
    var stash = ensureCollectionSsrStash(container);
    var pageSize =
      (stash && stash.pageSize) ||
      parseInt(container.getAttribute('data-wmn-fy-page-size') || '24', 10) ||
      24;
    var page = collectionPageNumber();
    var data =
      window.wmnCatalogGraph.getData && window.wmnCatalogGraph.getData();
    var products = (data && data.products) || {};
    var catalogN = Object.keys(products).length || 0;
    // Need enough ranked handles for this page (and a buffer). Prefer the
    // whole catalog so every Shopify page index has a slice.
    var need = page * pageSize;
    var lim = Math.max(need, pageSize * 2, catalogN || 48);
    if (lim > 600) lim = 600;

    var fy = scoreCatalogForYou({
      limit: lim,
      explore: false,
      pinRecent: false,
      fillToLimit: true,
      focus: focusOverride || undefined
    });
    if (!fy || !fy.picks || !fy.picks.length) return false;

    var slice = sliceRankedForPage(fy.picks, page, pageSize);
    // Past end of ranked pool: restore SSR rather than wiping the grid blank.
    if (!slice.length) {
      try {
        restoreCollectionSsr(container);
      } catch (eRestEmpty) {
        /* ignore */
      }
      return false;
    }

    var key =
      'paint::' +
      collectionInterestKey(prim) +
      '::p' +
      page +
      '::' +
      slice
        .map(function (p) {
          return p.handle;
        })
        .join('|');
    if (container.getAttribute('data-wmn-fy-painted') === key) {
      return true;
    }

    var sample =
      (stash && stash.nodes && stash.nodes[0]) ||
      container.querySelector('.thumbnail[data-wmn-handle]');
    var className = 'thumbnail';
    if (sample && sample.className) {
      className = String(sample.className || '')
        .replace(/\bwmn-memory-search-card\b/g, '')
        .replace(/\bwmn-interest-card\b/g, '')
        .replace(/\beven\b/g, '')
        .replace(/\bodd\b/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    // Detach SSR once, then replace contents with ranked slice.
    if (stash && stash.nodes) {
      for (var di = 0; di < stash.nodes.length; di++) {
        if (stash.nodes[di].parentNode) {
          stash.nodes[di].parentNode.removeChild(stash.nodes[di]);
        }
      }
    }
    clearCollectionForYouPaint(container);
    var leftover = container.querySelectorAll('.thumbnail[data-wmn-handle]');
    for (var li = 0; li < leftover.length; li++) {
      if (leftover[li].parentNode) {
        leftover[li].parentNode.removeChild(leftover[li]);
      }
    }

    var frag = document.createDocumentFragment();
    var si;
    for (si = 0; si < slice.length; si++) {
      var pick = slice[si];
      if (!pick || !pick.handle) continue;
      var p = products[pick.handle] || {};
      var genreLabels = [];
      var gs = pick.genres || p.g || [];
      var gi;
      for (gi = 0; gi < gs.length && genreLabels.length < 2; gi++) {
        genreLabels.push(humanizeSlug(gs[gi]));
      }
      var priceText = '';
      if (stash && stash.nodes) {
        var spi;
        for (spi = 0; spi < stash.nodes.length; spi++) {
          if (stash.nodes[spi].getAttribute('data-wmn-handle') !== pick.handle) continue;
          var priceEl = stash.nodes[spi].querySelector('.price');
          if (priceEl) {
            var priceClone = priceEl.cloneNode(true);
            var wasPrice = priceClone.querySelector('.was_price');
            if (wasPrice && wasPrice.parentNode) wasPrice.parentNode.removeChild(wasPrice);
            priceText = (priceClone.textContent || '').replace(/\s+/g, ' ').trim();
          }
          break;
        }
      }
      var ev = {
        h: pick.handle,
        title: pick.title || p.t || pick.handle,
        img: pick.image || p.img || '',
        price: priceText,
        tags: []
      };
      var card = buildSearchMemoryCard(ev, className, {
        eager: true,
        collection: true,
        genres: genreLabels
      });
      if (!card) continue;
      card.className = className + ' wmn-memory-search-card';
      card.setAttribute('data-wmn-fy-paint', '1');
      card.setAttribute('data-wmn-genres', genreLabels.join('|'));
      frag.appendChild(card);
    }
    var loadMore =
      (stash && stash.loadMore) ||
      container.querySelector('.js-load-more, .load-more');
    container.appendChild(frag);
    if (loadMore && loadMore.parentNode !== container) {
      container.appendChild(loadMore);
    } else if (loadMore) {
      container.appendChild(loadMore);
    }
    container.setAttribute('data-wmn-fy-painted', key);
    container.setAttribute('data-wmn-aff-sorted', 'algo::' + key);
    nudgeStuckLazyImages(container);
    hydrateCollectionPrices(container);
    syncCollectionSortSelect(true);
    rememberFyPage(
      page,
      slice.map(function (p) {
        return p.handle;
      })
    );
    return true;
  }

  var collectionPriceCache = {};

  function centsFromJsPrice(raw) {
    if (typeof raw !== 'number' || !isFinite(raw) || raw <= 0) return null;
    if (raw !== Math.floor(raw)) return Math.round(raw * 100);
    return Math.round(raw);
  }

  function moneyFromCents(cents) {
    if (!(cents > 0)) return '';
    try {
      if (window.wmnMoney && typeof window.wmnMoney.formatCents === 'function') {
        return window.wmnMoney.formatCents(cents);
      }
    } catch (e) {
      /* fall through */
    }
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt =
          (document.body && document.body.getAttribute('data-money-format')) ||
          '£{{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
    } catch (e2) {
      /* fall through */
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function writeCardPrice(card, label) {
    if (!card || !label) return;
    var caption = card.querySelector('.product-info__caption');
    if (!caption || caption.querySelector('.price')) return;
    var span = document.createElement('span');
    span.className = 'price';
    span.textContent = label;
    caption.appendChild(span);
  }

  function hydrateCollectionPrices(root) {
    if (!root || typeof fetch !== 'function') return;
    var cards = root.querySelectorAll('.thumbnail[data-wmn-handle]');
    var i;
    for (i = 0; i < cards.length; i++) {
      (function (card) {
        if (card.querySelector('.price')) return;
        var handle = card.getAttribute('data-wmn-handle');
        if (!handle) return;
        if (collectionPriceCache[handle]) {
          writeCardPrice(card, collectionPriceCache[handle]);
          return;
        }
        var rootPath =
          window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';
        fetch(rootPath + 'products/' + encodeURIComponent(handle) + '.js', {
          credentials: 'same-origin'
        })
          .then(function (res) {
            return res && res.ok ? res.json() : null;
          })
          .then(function (product) {
            if (!product) return;
            var min = centsFromJsPrice(
              product.price_min != null ? product.price_min : product.price
            );
            var max = centsFromJsPrice(
              product.price_max != null ? product.price_max : product.price
            );
            if (!min) return;
            var label = moneyFromCents(min);
            if (!label) return;
            if (product.price_varies || (max && max > min)) label = 'from ' + label;
            collectionPriceCache[handle] = label;
            var live = root.querySelector(
              '.thumbnail[data-wmn-handle="' + handle.replace(/"/g, '') + '"]'
            );
            writeCardPrice(live || card, label);
          })
          .catch(function () {
            /* price stays off the card */
          });
      })(cards[i]);
    }
  }

  function collectionInterestKey(prim) {
    if (!prim || !prim.dominant) return '';
    return (
      prim.kind +
      ':' +
      (prim.genre || prim.country || prim.region || '')
    );
  }

  function collectionInterestHandles(prim, limit) {
    if (!prim || !prim.dominant || !graphReady() || !(limit > 0)) return [];
    var data =
      window.wmnCatalogGraph.getData && window.wmnCatalogGraph.getData();
    if (!data || !data.products) return [];
    var pool = [];
    var handles = Object.keys(data.products);
    var i;
    for (i = 0; i < handles.length; i++) {
      if (productMatchesCollectionFocus(handles[i], prim)) {
        pool.push(handles[i]);
      }
    }
    pool.sort(function (a, b) {
      var sa = collectionFocusLatch(a, prim);
      var sb = collectionFocusLatch(b, prim);
      try {
        if (
          window.wmnIntentField &&
          typeof window.wmnIntentField.scoreHandle === 'function'
        ) {
          sa += window.wmnIntentField.scoreHandle(a) || 0;
          sb += window.wmnIntentField.scoreHandle(b) || 0;
        }
      } catch (eSc) {
        /* ignore */
      }
      if (sb !== sa) return sb - sa;
      return a < b ? -1 : a > b ? 1 : 0;
    });
    return pool.slice(0, limit);
  }

  function clearCollectionInterestInjects(container) {
    if (!container) return;
    var nodes = container.querySelectorAll(
      '.thumbnail[data-wmn-interest-card="1"]'
    );
    var i;
    for (i = 0; i < nodes.length; i++) {
      if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
    }
    container.removeAttribute('data-wmn-interest-injected');
  }

  /**
   * Shopify collection pages only ship ~24 cards. If interest is Indian
   * Classical but page 1 is A–Z Jazz/Blues, reorder alone cannot help —
   * inject graph matches so For you can actually show the interest.
   * Page 2+ must stay native pagination — never re-pin the same injects.
   */
  function collectionPageNumber() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      var p = parseInt(params.get('page') || '1', 10);
      if (!isFinite(p) || p < 1) return 1;
      return p;
    } catch (ePage) {
      return 1;
    }
  }

  var FY_SHOWN_KEY = 'wmn_fy_shown_v1';

  function readFyShown() {
    if (window.__wmnFyShown && typeof window.__wmnFyShown === 'object') {
      return window.__wmnFyShown;
    }
    var data = {};
    try {
      data = JSON.parse(sessionStorage.getItem(FY_SHOWN_KEY) || '{}') || {};
    } catch (eShown) {
      data = {};
    }
    if (!data || typeof data !== 'object') data = {};
    window.__wmnFyShown = data;
    return data;
  }

  function rememberFyPage(page, handles) {
    var all = readFyShown();
    var list = [];
    var seen = {};
    var i;
    for (i = 0; i < (handles || []).length; i++) {
      var h = handles[i];
      if (!h || seen[h]) continue;
      seen[h] = true;
      list.push(h);
    }
    all[String(page)] = list;
    var key;
    for (key in all) {
      if (!Object.prototype.hasOwnProperty.call(all, key)) continue;
      if (parseInt(key, 10) > page) delete all[key];
    }
    window.__wmnFyShown = all;
    try {
      sessionStorage.setItem(FY_SHOWN_KEY, JSON.stringify(all));
    } catch (eWrite) {
      /* ignore */
    }
  }

  function fyHandlesBeforePage(page) {
    var all = readFyShown();
    var set = {};
    var p;
    for (p = 1; p < page; p++) {
      var list = all[String(p)] || [];
      var i;
      for (i = 0; i < list.length; i++) {
        if (list[i]) set[list[i]] = true;
      }
    }
    return set;
  }

  /**
   * Page 1 is the top of the ranking. Later pages skip albums already shown
   * on earlier pages (including ones the live reorder swapped in). A direct
   * visit to page 2 with no memory still uses the numeric offset.
   */
  function sliceRankedForPage(picks, page, pageSize) {
    var prior = fyHandlesBeforePage(page);
    var priorN = 0;
    var k;
    for (k in prior) {
      if (Object.prototype.hasOwnProperty.call(prior, k)) priorN++;
    }
    var out = [];
    var i;
    if (priorN) {
      for (i = 0; i < picks.length && out.length < pageSize; i++) {
        var pick = picks[i];
        if (!pick || !pick.handle || prior[pick.handle]) continue;
        out.push(pick);
      }
      return out;
    }
    var start = (page - 1) * pageSize;
    return picks.slice(start, start + pageSize);
  }

  function injectCollectionInterestMatches(container) {
    if (!container || !wantsAlgorithmCollectionSort()) return 0;
    if (collectionTagFilterActive()) return 0;
    if (!graphReady()) return 0;
    // Only seed the first collection page. Re-injecting on ?page=2+ made the
    // same interest albums lead every page.
    if (collectionPageNumber() > 1) {
      clearCollectionInterestInjects(container);
      return 0;
    }
    var prim = collectionFocusPrimary();
    if (!prim.dominant) {
      clearCollectionInterestInjects(container);
      return 0;
    }
    var key = collectionInterestKey(prim);
    if (!key) return 0;

    var existing = {};
    var onPageMatches = 0;
    var nodes = container.querySelectorAll('.thumbnail[data-wmn-handle]');
    var i;
    for (i = 0; i < nodes.length; i++) {
      var h = nodes[i].getAttribute('data-wmn-handle');
      if (!h) continue;
      existing[h] = true;
      if (
        !nodes[i].getAttribute('data-wmn-interest-card') &&
        productMatchesCollectionFocus(h, prim)
      ) {
        onPageMatches += 1;
      }
    }

    if (container.getAttribute('data-wmn-interest-injected') !== key) {
      clearCollectionInterestInjects(container);
    } else if (onPageMatches >= COLLECTION_INTEREST_INJECT) {
      return 0;
    } else if (container.querySelector('.thumbnail[data-wmn-interest-card="1"]')) {
      // Already injected for this interest; keep cards, just re-rank later.
      return 0;
    }

    var need = Math.max(0, COLLECTION_INTEREST_INJECT - onPageMatches);
    if (need <= 0) {
      container.setAttribute('data-wmn-interest-injected', key);
      return 0;
    }

    var sample = container.querySelector(
      '.thumbnail[data-wmn-handle]:not([data-wmn-injected])'
    );
    if (!sample) {
      sample = container.querySelector('.thumbnail[data-wmn-handle]');
    }
    if (!sample) return 0;
    var className = String(sample.className || '')
      .replace(/\bwmn-memory-search-card\b/g, '')
      .replace(/\bwmn-interest-card\b/g, '')
      .replace(/\beven\b/g, '')
      .replace(/\bodd\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    var data = window.wmnCatalogGraph.getData();
    var candidates = collectionInterestHandles(prim, need + 6);
    var inserted = 0;
    var first = container.querySelector('.thumbnail[data-wmn-handle]');
    for (i = 0; i < candidates.length && inserted < need; i++) {
      var handle = candidates[i];
      if (existing[handle]) continue;
      var p = data.products[handle];
      if (!p) continue;
      var genreLabels = [];
      var gs = p.g || [];
      var gi;
      for (gi = 0; gi < gs.length && genreLabels.length < 2; gi++) {
        genreLabels.push(humanizeSlug(gs[gi]));
      }
      var ev = {
        h: handle,
        title: p.t || handle,
        img: p.img || '',
        price: '',
        tags: []
      };
      var card = buildSearchMemoryCard(ev, className, {
        eager: true,
        collection: true,
        genres: genreLabels
      });
      if (!card) continue;
      card.className = className + ' wmn-memory-search-card';
      card.setAttribute('data-wmn-interest-card', '1');
      card.setAttribute('data-wmn-genres', genreLabels.join('|'));
      if (first && first.parentNode === container) {
        container.insertBefore(card, first);
      } else {
        container.appendChild(card);
      }
      existing[handle] = true;
      inserted += 1;
    }
    container.setAttribute('data-wmn-interest-injected', key);
    if (inserted) {
      nudgeStuckLazyImages(container);
      hydrateCollectionPrices(container);
    }
    return inserted;
  }

  /**
   * Entity + focus expected utility for a handle (no intent-field takeover).
   * Used by scoreCatalogForYou — country/region at ENTITY_SCORE_WEIGHT.
   * @param {string} handle
   * @param {{prefs?: object, focus?: object|null, negatives?: object}|null} ctx
   */
  function scoreHandleEntities(handle, ctx) {
    if (!handle) return 0;
    var prefs = (ctx && ctx.prefs) || getEntityPrefs();
    var negatives = (ctx && ctx.negatives) || (readStore().negatives || {});
    var score = 0;
    var productKey = 'product:' + handle;
    var pw = prefs[productKey];
    if (pw) {
      score += HANDLE_BONUS + pw * 2 * (ENTITY_SCORE_WEIGHT.product || 0.6);
    }
    var resolved = {
      artists: [],
      genres: [],
      countries: [],
      regions: [],
      imprints: []
    };
    if (graphReady()) {
      resolved = window.wmnCatalogGraph.resolveProduct(handle) || resolved;
      var ai;
      for (ai = 0; ai < (resolved.artists || []).length; ai++) {
        var aw = prefs['artist:' + resolved.artists[ai]];
        if (aw) score += aw * (ENTITY_SCORE_WEIGHT.artist || 1);
      }
      for (ai = 0; ai < (resolved.genres || []).length; ai++) {
        var gw = prefs['genre:' + resolved.genres[ai]];
        if (gw) score += gw * (ENTITY_SCORE_WEIGHT.genre || 1);
      }
      // When session is genre-dominant, ignore cold country/region mass so
      // leftover Mali/India prefs cannot outrank Jazz/Blues matches.
      var dampGeo = !!(ctx && ctx.dampGeoPrefs);
      for (ai = 0; ai < (resolved.countries || []).length; ai++) {
        var cslug = resolved.countries[ai];
        if (!cslug || cslug === 'celtic') continue;
        var cw = prefs['country:' + cslug];
        if (cw && !dampGeo) score += cw * (ENTITY_SCORE_WEIGHT.country || 1.4);
      }
      for (ai = 0; ai < (resolved.regions || []).length; ai++) {
        var rslug = resolved.regions[ai];
        if (!rslug) continue;
        var rw = prefs['region:' + rslug];
        if (rw && !dampGeo) score += rw * (ENTITY_SCORE_WEIGHT.region || 1.4);
      }
      for (ai = 0; ai < (resolved.imprints || []).length; ai++) {
        var iw = prefs['imprint:' + resolved.imprints[ai]];
        if (iw) score += iw * (ENTITY_SCORE_WEIGHT.imprint || 0.5);
      }
    }

    var focus = ctx && Object.prototype.hasOwnProperty.call(ctx, 'focus')
      ? ctx.focus
      : readFocus();
    if (focus) {
      var age = now() - (focus.t || 0);
      var decayF = Math.max(0.35, 1 - age / FOCUS_TTL_MS);
      var fi;
      if (focus.handles && focus.handles.indexOf(handle) !== -1) {
        score += FOCUS_HANDLE_BONUS * decayF;
      }
      for (fi = 0; fi < (resolved.artists || []).length; fi++) {
        if (focus.artists && focus.artists.indexOf(resolved.artists[fi]) !== -1) {
          score += FOCUS_ARTIST_BONUS * decayF;
        }
      }
      for (fi = 0; fi < (resolved.genres || []).length; fi++) {
        if (focus.genres && focus.genres.indexOf(resolved.genres[fi]) !== -1) {
          score += FOCUS_GENRE_BONUS * decayF;
        }
      }
    }

    var neg = negatives[handle] || 0;
    if (neg > 0) score -= NEG_GAMMA * neg;
    // Soft artist demotion from skips (does not erase country mass).
    if (resolved.artists && resolved.artists.length) {
      var soft =
        negatives['artist-soft:' + resolved.artists[0]] || 0;
      if (soft > 0) score -= NEG_GAMMA * soft;
    }
    return score;
  }

  /** Softmax-style L1 mass of entity posteriors (for explore anneal). */
  function prefsL1(prefs) {
    var s = 0;
    var k;
    for (k in prefs || {}) {
      if (Object.prototype.hasOwnProperty.call(prefs, k)) {
        s += Math.max(0, prefs[k] || 0);
      }
    }
    return s;
  }

  /** Mulberry32 — deterministic explore when opts.seed is set. */
  function makeRng(seed) {
    var t = (seed >>> 0) || 1;
    return function () {
      t += 0x6d2b79f5;
      var r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  /**
   * Annealed Thompson-ish explore noise. Shrinks as ||α||₁ grows.
   */
  function exploreNoise(prefsNorm, rng) {
    var kappa = EXPLORE_KAPPA0 / (1 + prefsNorm / EXPLORE_TAU);
    // Centered Beta(2,2)-ish via two uniforms (mild, not chaotic).
    var u = rng();
    var v = rng();
    var betaish = (u + v) / 2;
    return kappa * (2 * betaish - 1);
  }

  /**
   * Record a skip / soft-negative on a product handle (Phase 1 store).
   * Soft-demotes primary artist slightly so one skip does not erase a country.
   */
  function recordSkipNegative(handle, amount) {
    if (!handle) return;
    var store = readStore();
    store.negatives = store.negatives || {};
    var add = amount == null ? 0.5 : amount;
    store.negatives[handle] = (store.negatives[handle] || 0) + add;
    if (graphReady() && add > 0) {
      var resolved = window.wmnCatalogGraph.resolveProduct(handle);
      var artist = resolved && resolved.artists && resolved.artists[0];
      if (artist && artist !== 'various' && artist !== 'various-artists') {
        var softKey = 'artist-soft:' + artist;
        store.negatives[softKey] =
          (store.negatives[softKey] || 0) + add * NEG_ARTIST_SOFT;
      }
    }
    store.negatives = capNegatives(store.negatives);
    writeStore(store);
  }

  /** Session dedupe for feed UX → prefs mapping (Phase 3). */
  var feedSignalOnce = { hover: {}, visible: {}, skip: {} };

  /**
   * Thin feed UX → existing preference paths (NOT a second model).
   * click → search_click (~+2.0); hover≥500 → hover; visible → log only;
   * skip → recordSkipNegative + prefs dispatch.
   */
  function mergeMeta(base, extra) {
    var out = {};
    var k;
    if (base && typeof base === 'object') {
      for (k in base) {
        if (Object.prototype.hasOwnProperty.call(base, k)) out[k] = base[k];
      }
    }
    if (extra && typeof extra === 'object') {
      for (k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k];
      }
    }
    return out;
  }

  function recordFeedSignal(opts) {
    if (!opts || !opts.handle) return;
    var kind = opts.kind || '';
    var handle = opts.handle;
    var dwellMs = opts.dwellMs || 0;
    var source = opts.source || '';
    var policyMeta =
      opts.meta && typeof opts.meta === 'object' ? opts.meta : null;
    var payload = {
      handle: handle,
      title: opts.title || '',
      artist: opts.artist || '',
      genres: opts.genres || [],
      countries: opts.countries || undefined,
      regions: opts.regions || undefined,
      meta: policyMeta
    };

    if (kind === 'visible') {
      if (feedSignalOnce.visible[handle]) return;
      feedSignalOnce.visible[handle] = true;
      // Analytics only — recordView('visible') early-returns before prefs write
      payload.kind = 'visible';
      recordView(payload);
      return;
    }

    if (kind === 'hover') {
      if (dwellMs < 500) return;
      if (feedSignalOnce.hover[handle]) return;
      feedSignalOnce.hover[handle] = true;
      // For You rail hovers are analytics-only — must NOT steal session focus
      // from Rough Guides (inspecting a stale India card was flipping subtitle).
      if (source === 'for-you-feed') {
        logIntentEvent({
          type: 'hover',
          handle: handle,
          artist: opts.artist || null,
          dwellMs: dwellMs,
          meta: mergeMeta({ source: source, soft: true }, policyMeta)
        });
        return;
      }
      payload.kind = 'hover';
      recordView(payload);
      return;
    }

    if (kind === 'click') {
      // Strong path — search_click KIND_WEIGHT 2.0 + geo amplify
      payload.kind = 'search_click';
      recordView(payload);
      return;
    }

    if (kind === 'skip') {
      if (feedSignalOnce.skip[handle]) return;
      feedSignalOnce.skip[handle] = true;
      var amt = opts.amount == null ? 0.75 : opts.amount;
      recordSkipNegative(handle, amt);
      // Skip does not erase country mass; still notify live feed tail rebuild
      dispatchPrefsUpdated(readStore());
      logIntentEvent({
        type: 'skip',
        handle: handle,
        artist: opts.artist || null,
        meta: mergeMeta({ source: source, amount: amt }, policyMeta)
      });
    }
  }

  function itemArtistKey(item) {
    if (!item) return '';
    if (item.artist) {
      var a = handleize(item.artist);
      if (a && a !== 'various' && a !== 'various-artists') return a;
    }
    if (item.handle && graphReady()) {
      var r = window.wmnCatalogGraph.resolveProduct(item.handle);
      if (r && r.artists && r.artists[0]) return r.artists[0];
    }
    return '';
  }

  /** Primary country slug for soft MMR (never celtic-as-country). */
  function itemCountryKey(item) {
    if (!item || !item.handle || !graphReady()) return '';
    var r = window.wmnCatalogGraph.resolveProduct(item.handle);
    var list = (r && r.countries) || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i] !== 'celtic') return list[i];
    }
    return '';
  }

  function itemGenreKeys(item) {
    var out = [];
    if (!item) return out;
    if (item.handle && graphReady()) {
      var r = window.wmnCatalogGraph.resolveProduct(item.handle);
      return (r && r.genres) || [];
    }
    return out;
  }

  /**
   * Maximal Marginal Relevance–style pick: high score, penalize repeated artists
   * and (softly) repeated countries (~0.15× artist penalty).
   * opts.skipCountryDiversity — under country/region session focus, concentrate
   * on that geo instead of spreading the rail away from it.
   */
  function mmrSelect(candidates, n, already, scoreFn, minScore, opts) {
    var out = [];
    var used = {};
    var artistCount = {};
    var countryCount = {};
    var floor = minScore == null ? 0.5 : minScore;
    var skipCountryDiv = !!(opts && opts.skipCountryDiversity);
    var i;
    for (i = 0; i < (already || []).length; i++) {
      if (!already[i] || !already[i].handle) continue;
      used[already[i].handle] = true;
      var ak0 = itemArtistKey(already[i]);
      if (ak0) artistCount[ak0] = (artistCount[ak0] || 0) + 1;
      var ck0 = itemCountryKey(already[i]);
      if (ck0) countryCount[ck0] = (countryCount[ck0] || 0) + 1;
    }
    var pool = [];
    for (i = 0; i < (candidates || []).length; i++) {
      if (candidates[i] && candidates[i].handle && !used[candidates[i].handle]) {
        pool.push(candidates[i]);
      }
    }
    while (out.length < n && pool.length) {
      var best = null;
      var bestIdx = -1;
      var bestAdj = -Infinity;
      for (i = 0; i < pool.length; i++) {
        var c = pool[i];
        var base = scoreFn(c);
        if (base < floor) continue;
        var ak = itemArtistKey(c);
        var pen = ak ? (artistCount[ak] || 0) * ARTIST_MMR_PENALTY : 0;
        var ck = itemCountryKey(c);
        var cpen =
          !skipCountryDiv && ck
            ? (countryCount[ck] || 0) * COUNTRY_MMR_PENALTY
            : 0;
        var adj = base - pen - cpen;
        if (adj > bestAdj) {
          bestAdj = adj;
          best = c;
          bestIdx = i;
        }
      }
      if (!best || bestIdx < 0) break;
      out.push(best);
      used[best.handle] = true;
      pool.splice(bestIdx, 1);
      var akk = itemArtistKey(best);
      if (akk) artistCount[akk] = (artistCount[akk] || 0) + 1;
      var ckk = itemCountryKey(best);
      if (ckk) countryCount[ckk] = (countryCount[ckk] || 0) + 1;
    }
    return out;
  }

  function graphItemFromHandle(handle) {
    if (!handle || !graphReady()) return null;
    var data = window.wmnCatalogGraph.getData();
    var p = data.products[handle];
    if (!p) return null;
    var title = p.t || handle;
    var titleL = String(title).toLowerCase();
    if (titleL.indexOf('gift voucher') !== -1 || titleL.indexOf('gift card') !== -1) {
      return null;
    }
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    var artistName = '';
    if (p.a && data.artists[p.a]) artistName = data.artists[p.a].n || p.a;
    var genreLabels = [];
    if (p.g) {
      for (var i = 0; i < p.g.length; i++) {
        var gnode = data.genres[p.g[i]];
        genreLabels.push(gnode && gnode.n ? gnode.n : p.g[i].replace(/-/g, ' '));
      }
    }
    var img = null;
    if (typeof window.wmnCatalogGraph.getProductImage === 'function') {
      img = window.wmnCatalogGraph.getProductImage(handle) || null;
      if (img === '') img = null;
    }
    return {
      handle: handle,
      title: title,
      artist: artistName,
      url: root + 'products/' + handle,
      image: img,
      genres: genreLabels,
      fromGraph: true
    };
  }

  function dispatchPrefsUpdated(store) {
    try {
      document.dispatchEvent(
        new CustomEvent('wmn:preferences-updated', { detail: { store: store } })
      );
    } catch (errEvt) {
      /* ignore */
    }
  }

  function getCookieRaw() {
    try {
      return String(document['cookie'] || '');
    } catch (e) {
      return '';
    }
  }

  function readCookie(name) {
    var parts = getCookieRaw().split(';');
    var prefix = name + '=';
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].replace(/^\s+/, '');
      if (p.indexOf(prefix) === 0) {
        return decodeURIComponent(p.substring(prefix.length));
      }
    }
    return '';
  }

  function writeCookie(name, value, days) {
    try {
      var maxAge = Math.floor(days * 24 * 60 * 60);
      var secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document['cookie'] =
        name +
        '=' +
        encodeURIComponent(value) +
        '; Max-Age=' +
        maxAge +
        '; Path=/; SameSite=Lax' +
        secure;
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * Reject CDN image basenames mistaken for product handles.
   * Shopify files live under …/products/RGNET1316_800x.jpg — scraping
   * /products/ from img src used to poison recentlyViewed with that filename.
   */
  function isLikelyProductHandle(handle) {
    var h = String(handle || '').replace(/^\s+|\s+$/g, '');
    if (!h) return false;
    if (h.indexOf('/') !== -1 || h.indexOf(',') !== -1 || h.indexOf('.') !== -1) {
      return false;
    }
    if (/\.(?:jpe?g|png|webp|gif|svg|mp3|mp4)(?:$|[?#])/i.test(h)) return false;
    if (/_\d+x\d*(?:$|[.?#])/i.test(h)) return false;
    return true;
  }

  /**
   * Normalize a product handle for the theme recentlyViewed cookie.
   * Matches app.js: trim, strip query/hash; handles are already Shopify-slugged.
   */
  function normalizeRecentlyViewedHandle(handle) {
    if (handle == null) return '';
    var h = String(handle).replace(/^\s+|\s+$/g, '');
    if (!h) return '';
    h = h.split('?')[0].split('#')[0];
    var m = h.match(/\/products\/([^\/]+)/);
    if (m) {
      try {
        h = decodeURIComponent(m[1]);
      } catch (eDec) {
        h = m[1];
      }
    }
    h = h.replace(/^\s+|\s+$/g, '');
    if (!isLikelyProductHandle(h)) return '';
    return h;
  }

  /**
   * Pure prepend/dedupe/cap — newest first, unique, max RV_MAX_HANDLES.
   * @param {string[]} list
   * @param {string} handle
   * @param {number} [max]
   * @returns {string[]}
   */
  function prependRecentlyViewedHandles(list, handle, max) {
    var h = normalizeRecentlyViewedHandle(handle);
    if (!h) return (list || []).slice();
    var cap = max == null ? RV_MAX_HANDLES : max;
    var out = [h];
    var prev = list || [];
    for (var i = 0; i < prev.length; i++) {
      var cur = normalizeRecentlyViewedHandle(prev[i]);
      if (cur && cur !== h) out.push(cur);
    }
    if (out.length > cap) out.length = cap;
    return out;
  }

  /**
   * Read recentlyViewed cookie as handle list.
   * app.js: Cookies.get → unescape → split(',')
   * (js-cookie decodeURIComponent; we mirror via readCookie then unescape)
   */
  function readRecentlyViewedCookie() {
    var raw = readCookie(RV_COOKIE_NAME);
    if (!raw) return [];
    var decoded = raw;
    try {
      decoded = unescape(raw);
    } catch (eUn) {
      /* keep raw */
    }
    var parts = String(decoded).split(',');
    var out = [];
    var scrubbed = false;
    for (var i = 0; i < parts.length; i++) {
      var rawPart = String(parts[i] || '').replace(/^\s+|\s+$/g, '');
      var h = normalizeRecentlyViewedHandle(parts[i]);
      if (h) out.push(h);
      else if (rawPart) scrubbed = true;
    }
    // Drop poisoned CDN filenames (rgnet1316_800x.jpg) from the cookie once.
    if (scrubbed) {
      try {
        writeRecentlyViewedCookie(out);
      } catch (eScrub) {
        /* ignore */
      }
    }
    return out;
  }

  /**
   * Write recentlyViewed cookie.
   * app.js setCookieProducts: Cookies.set(escape(join(',')), { expires: 30, path: '/' })
   * js-cookie then encodeURIComponent — writeCookie does the same.
   */
  function writeRecentlyViewedCookie(handles) {
    var list = [];
    var src = handles || [];
    for (var i = 0; i < src.length; i++) {
      var h = normalizeRecentlyViewedHandle(src[i]);
      if (h) list.push(h);
    }
    if (list.length > RV_MAX_HANDLES) list.length = RV_MAX_HANDLES;
    writeCookie(RV_COOKIE_NAME, escape(list.join(',')), RV_COOKIE_DAYS);
  }

  /** Prepend handle to theme recentlyViewed cookie (newest first, dedupe, max 12). */
  function pushRecentlyViewedHandle(handle) {
    var h = normalizeRecentlyViewedHandle(handle);
    if (!h) return;
    writeRecentlyViewedCookie(
      prependRecentlyViewedHandles(readRecentlyViewedCookie(), h, RV_MAX_HANDLES)
    );
  }

  function pruneEvents(events) {
    var cutoff = now() - MAX_AGE_MS;
    var out = [];
    for (var i = 0; i < events.length; i++) {
      var ev = events[i];
      if (!ev || !ev.h || !ev.t || ev.t < cutoff) continue;
      out.push(ev);
    }
    if (out.length > MAX_EVENTS) out = out.slice(-MAX_EVENTS);

    var deduped = [];
    for (var j = 0; j < out.length; j++) {
      var cur = out[j];
      var prev = deduped[deduped.length - 1];
      if (prev && prev.h === cur.h && Math.abs((cur.t || 0) - (prev.t || 0)) < 120000) {
        deduped[deduped.length - 1] = cur;
      } else {
        deduped.push(cur);
      }
    }
    return deduped;
  }

  function richerEvent(a, b) {
    if (!a) return b;
    if (!b) return a;
    return {
      t: Math.max(a.t || 0, b.t || 0),
      k: b.k || a.k || 'view',
      h: a.h || b.h,
      id: a.id || b.id || '',
      g: (a.g && a.g.length ? a.g : b.g) || [],
      title: a.title || b.title || '',
      img: a.img || b.img || '',
      price: a.price || b.price || ''
    };
  }

  function mergeStores(a, b) {
    var map = {};
    var all = (a.events || []).concat(b.events || []);
    for (var i = 0; i < all.length; i++) {
      var ev = all[i];
      if (!ev || !ev.h || !ev.t) continue;
      var key = ev.h + ':' + ev.t;
      map[key] = richerEvent(map[key], ev);
    }
    var merged = [];
    for (var k in map) {
      if (Object.prototype.hasOwnProperty.call(map, k)) merged.push(map[k]);
    }
    merged.sort(function (x, y) {
      return (x.t || 0) - (y.t || 0);
    });
    var entities = mergeEntityMaps(a && a.entities, b && b.entities);
    var negatives = mergeNegativeMaps(a && a.negatives, b && b.negatives);
    var et = Math.max((a && a.et) || 0, (b && b.et) || 0) || undefined;
    var out = {
      v: 1,
      events: pruneEvents(merged),
      entities: entities,
      negatives: negatives
    };
    if (et) out.et = et;
    return out;
  }

  function readLocal() {
    try {
      return parseStore(window.localStorage.getItem(storageKey()));
    } catch (e) {
      return emptyStore();
    }
  }

  function readCookieStore() {
    var raw = readCookie(COOKIE_NAME);
    if (!raw) return emptyStore();
    try {
      var wrapped = JSON.parse(raw);
      if (wrapped && wrapped.sk && wrapped.sk !== storageKey()) return emptyStore();
      if (wrapped && wrapped.data) return parseStore(JSON.stringify(wrapped.data));
      return parseStore(raw);
    } catch (e) {
      return emptyStore();
    }
  }

  function readStore() {
    return mergeStores(readLocal(), readCookieStore());
  }

  function compactForCookie(store) {
    var events = (store.events || []).slice(-25).map(function (ev) {
      return { t: ev.t, h: ev.h, g: (ev.g || []).slice(0, 4) };
    });
    return { sk: storageKey(), data: { v: 1, events: events } };
  }

  function writeStore(data) {
    data.events = pruneEvents(data.events || []);
    data.entities = capEntities(data.entities || {});
    data.negatives = capNegatives(data.negatives || {});
    try {
      window.localStorage.setItem(storageKey(), JSON.stringify(data));
    } catch (e) {
      /* ignore */
    }
    try {
      writeCookie(COOKIE_NAME, JSON.stringify(compactForCookie(data)), COOKIE_DAYS);
    } catch (e2) {
      /* ignore */
    }
  }

  function normalizeGenre(g) {
    return String(g || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function decay(ts) {
    var age = Math.max(0, now() - (ts || 0));
    return Math.pow(0.5, age / HALF_LIFE_MS);
  }

  function affinityMap(events) {
    var genres = {};
    var handles = {};
    for (var i = 0; i < events.length; i++) {
      var ev = events[i];
      var kindMult = KIND_WEIGHT[ev.k || 'view'] || 1;
      var w = decay(ev.t) * VIEW_WEIGHT * kindMult;
      if (ev.h) handles[ev.h] = (handles[ev.h] || 0) + w * 2;
      var gs = ev.g || [];
      for (var j = 0; j < gs.length; j++) {
        var key = normalizeGenre(gs[j]);
        if (key) genres[key] = (genres[key] || 0) + w;
      }
    }
    return { genres: genres, handles: handles };
  }

  /** Compat affinity: event map + entity-derived genres/handles. */
  function getAffinityCompat() {
    var store = readStore();
    var base = affinityMap(store.events || []);
    var prefs = store.entities || {};
    for (var id in prefs) {
      if (!Object.prototype.hasOwnProperty.call(prefs, id)) continue;
      var w = prefs[id] || 0;
      if (w <= 0) continue;
      if (id.indexOf('product:') === 0) {
        var h = id.slice(8);
        base.handles[h] = (base.handles[h] || 0) + w * 2;
      } else if (id.indexOf('genre:') === 0) {
        var slug = id.slice(6);
        var label = slug.replace(/-/g, ' ');
        base.genres[label] = (base.genres[label] || 0) + w;
        base.genres[slug] = (base.genres[slug] || 0) + w;
      } else if (id.indexOf('country:') === 0) {
        var cslug = id.slice(8);
        if (cslug && cslug !== 'celtic') {
          base.handles['__country:' + cslug] = (base.handles['__country:' + cslug] || 0) + w;
          // Soft title match aid
          base.genres[cslug] = (base.genres[cslug] || 0) + w * 0.5;
          base.genres[cslug.replace(/-/g, ' ')] =
            (base.genres[cslug.replace(/-/g, ' ')] || 0) + w * 0.5;
        }
      }
    }
    return base;
  }

  function topGenres(affinity, limit) {
    var pairs = [];
    Object.keys(affinity.genres || {}).forEach(function (g) {
      pairs.push({ g: g, w: affinity.genres[g] });
    });
    pairs.sort(function (a, b) {
      return b.w - a.w;
    });
    return pairs.slice(0, limit || 3);
  }

  function parseListAttr(el, name) {
    var raw = el.getAttribute(name) || '';
    if (!raw) return [];
    return raw.split('|').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function scoreByHandleTitleGenres(handle, title, genres, affinity) {
    var score = 0;
    title = (title || '').toLowerCase();

    if (handle && affinity.handles[handle]) {
      score += HANDLE_BONUS + affinity.handles[handle];
    }

    for (var i = 0; i < (genres || []).length; i++) {
      var g = normalizeGenre(genres[i]);
      if (affinity.genres[g]) score += affinity.genres[g];
    }

    Object.keys(affinity.genres).forEach(function (g) {
      if (g.length >= 4 && title.indexOf(g) !== -1) {
        score += affinity.genres[g] * 0.35;
      }
    });

    if (title.indexOf('gift voucher') !== -1 || title.indexOf('gift card') !== -1) {
      score -= 500;
    }

    return score;
  }

  function scoreElement(el, affinity) {
    return scoreByHandleTitleGenres(
      el.getAttribute('data-wmn-handle') || '',
      el.getAttribute('data-wmn-title') || '',
      parseListAttr(el, 'data-wmn-genres'),
      affinity
    );
  }

  function productHandleFromHref(href) {
    if (!href) return '';
    // Never treat Shopify CDN file URLs as product links
    // (…/files/…/products/RGNET1316_800x.jpg).
    var s = String(href);
    if (/\/cdn\/shop\/|cdn\.shopify\.com|\/files\/\d+\//i.test(s)) return '';
    var m = s.match(/\/products\/([^\/?#]+)/);
    if (!m) return '';
    var h = '';
    try {
      h = decodeURIComponent(m[1]);
    } catch (eDec) {
      h = m[1];
    }
    return isLikelyProductHandle(h) ? h : '';
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    url = String(url);
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  // Prefer largest available Shopify CDN size for a given <img>.
  function bestImgUrlFromElement(imgEl) {
    if (!imgEl) return '';
    var raw =
      imgEl.getAttribute('data-src') ||
      imgEl.currentSrc ||
      imgEl.getAttribute('src') ||
      '';
    var srcset = imgEl.getAttribute('data-srcset') || imgEl.getAttribute('srcset') || '';
    if (srcset) {
      var best = '';
      var bestW = 0;
      srcset.split(',').forEach(function (part) {
        var bits = part.trim().split(/\s+/);
        if (!bits[0]) return;
        var wMatch = (bits[1] || '').match(/^(\d+)w$/);
        var w = wMatch ? parseInt(wMatch[1], 10) : 0;
        if (w >= bestW) {
          bestW = w;
          best = bits[0];
        }
      });
      if (best) raw = best;
    }
    return normalizeImageUrl(raw);
  }

  // Rewrite Shopify CDN size suffix (_200x → _1200x). Named sizes (_compact) → numeric.
  function withShopifyImageWidth(url, width) {
    url = normalizeImageUrl(url);
    if (!url || !width) return url;
    var w = String(width).replace(/x$/i, '');
    if (/_\d+x\d*/.test(url)) {
      return url.replace(/_\d+x\d*/, '_' + w + 'x');
    }
    if (/_(pico|icon|thumb|small|compact|medium|large|grande|original|master)(?=\.)/i.test(url)) {
      return url.replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|master)(?=\.)/i,
        '_' + w + 'x'
      );
    }
    return url.replace(/(\.(?:jpe?g|png|webp|gif))(\?|$)/i, '_' + w + 'x$1$2');
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function recordView(payload) {
    if (!payload) return;
    var kind = payload.kind || 'view';
    var genres = (payload.genres || []).filter(Boolean);
    ensureGraphWarm();

    // Genre-chip signal without a product handle
    if (!payload.handle && genres.length) {
      var storeChip = readStore();
      storeChip.events.push({ t: now(), k: 'chip', g: genres });
      bumpEntities(storeChip, resolveEntities({ genres: genres }), 'chip');
      writeStore(storeChip);
      scheduleCloudWriteback();
      dispatchPrefsUpdated(storeChip);
      logIntentEvent({
        type: 'chip',
        meta: { genres: genres.slice(0, 6) }
      });
      return;
    }
    if (!payload.handle) return;

    var store = readStore();
    var next = {
      t: now(),
      k: kind,
      h: payload.handle,
      id: payload.id || '',
      g: genres,
      title: payload.title || '',
      img: normalizeImageUrl(payload.image || payload.img || ''),
      price: payload.price || ''
    };
    var entityIds = resolveEntities(payload);
    var bumpScale = entityBumpScaleMap(entityIds, kind);
    // Passive impressions must not write prefs or pins — rail scroll was
    // drowning intentional African / geo hovers with Cuba/Brazil/Celtic mass.
    if (kind === 'visible') {
      logIntentEvent({
        type: 'impression',
        handle: payload.handle,
        artist: payload.artist || null,
        meta: mergeMeta(
          { kind: 'visible', source: payload.source || null },
          payload.meta
        )
      });
      return;
    }
    // Dual-write theme Recently viewed cookie whenever this path updates focus.
    // (visible/chip early-returns above skip focus — do not dual-write there.)
    pushRecentlyViewedHandle(payload.handle);
    var pinEvent = true;
    for (var i = store.events.length - 1; i >= 0; i--) {
      if (store.events[i] && store.events[i].h === next.h) {
        if (pinEvent) {
          store.events[i] = richerEvent(store.events[i], next);
          store.events[i].t = next.t;
          store.events[i].k = kind;
        }
        bumpEntities(store, entityIds, kind, bumpScale);
        writeStore(store);
        scheduleCloudWriteback();
        // Focus BEFORE prefs dispatch so For You remount sees the new intent.
        if (pinEvent) updateFocusFromPayload(payload);
        dispatchPrefsUpdated(store);
        logIntentEvent({
          type: kind === 'search_click' ? 'click' : kind,
          handle: payload.handle,
          artist: payload.artist || null,
          meta: payload.meta || null
        });
        try {
          if (window.wmnIntentField) {
            if (kind === 'listen' && window.wmnIntentField.onListen) {
              window.wmnIntentField.onListen(payload.handle);
            } else if (kind === 'atc' && window.wmnIntentField.injectFromProduct) {
              window.wmnIntentField.injectFromProduct(payload.handle, 2.0, 'atc');
            } else if (
              kind !== 'visible' &&
              window.wmnIntentField.injectFromProduct
            ) {
              window.wmnIntentField.injectFromProduct(
                payload.handle,
                kind === 'hover' ? 1.25 : 0.9,
                kind === 'hover' ? 'hover' : 'view'
              );
            }
            if (
              kind !== 'visible' &&
              window.wmnIntentField.learnFromSignal
            ) {
              window.wmnIntentField.learnFromSignal(
                kind === 'listen' || kind === 'atc'
                  ? 'listen'
                  : kind === 'hover'
                    ? 'hover'
                    : 'click',
                true,
                { handle: payload.handle }
              );
            }
          }
        } catch (fe) {
          /* ignore */
        }
        return;
      }
    }
    if (pinEvent) store.events.push(next);
    bumpEntities(store, entityIds, kind, bumpScale);
    writeStore(store);
    scheduleCloudWriteback();
    // Focus BEFORE prefs dispatch so For You remount sees the new intent.
    updateFocusFromPayload(payload);
    dispatchPrefsUpdated(store);
    logIntentEvent({
      type: kind === 'search_click' ? 'click' : kind,
      handle: payload.handle,
      artist: payload.artist || null,
      meta: payload.meta || null
    });
    try {
      if (window.wmnIntentField) {
        if (kind === 'listen' && window.wmnIntentField.onListen) {
          window.wmnIntentField.onListen(payload.handle);
        } else if (kind === 'atc' && window.wmnIntentField.injectFromProduct) {
          window.wmnIntentField.injectFromProduct(payload.handle, 2.0, 'atc');
        } else if (
          kind !== 'visible' &&
          window.wmnIntentField.injectFromProduct
        ) {
          window.wmnIntentField.injectFromProduct(
            payload.handle,
            kind === 'hover' ? 1.25 : 0.9,
            kind === 'hover' ? 'hover' : 'view'
          );
        }
        if (kind !== 'visible' && window.wmnIntentField.learnFromSignal) {
          window.wmnIntentField.learnFromSignal(
            kind === 'listen' || kind === 'atc'
              ? 'listen'
              : kind === 'hover'
                ? 'hover'
                : 'click',
            true,
            { handle: payload.handle }
          );
        }
      }
    } catch (fe2) {
      /* ignore */
    }
  }

  /** Soft entity bumps from a typed search query (graph + visible results). */
  function recordSearch(query, opts) {
    opts = opts || {};
    var q = String(query || '').replace(/^\s+|\s+$/g, '');
    if (q.length < 2) return false;
    try {
      if (
        window.wmnQueryGuard &&
        typeof window.wmnQueryGuard.isBlocked === 'function' &&
        window.wmnQueryGuard.isBlocked(q)
      ) {
        return false;
      }
    } catch (eGuard) {
      /* ignore */
    }

    // One brain: lean/committed settles are field-owned — never bump entities/focus from typing.
    if (fieldOwnsRanking()) {
      try {
        window.__wmnTypedPrefsDirty = true;
      } catch (eOwn) {
        /* ignore */
      }
      return false;
    }

    var results = Array.isArray(opts.results) ? opts.results : [];
    var prevFocus = readFocus();
    var prevQ = prevFocus && prevFocus.q ? String(prevFocus.q) : '';
    var refineBoost = 1;
    if (prevQ) {
      var prevN = prevQ.toLowerCase();
      var nextN = q.toLowerCase();
      if (nextN.indexOf(prevN) === 0 || prevN.indexOf(nextN) === 0) {
        // Same stem refined (blu → blues): strengthen
        refineBoost = 1.35;
      }
    }

    // Always persist query focus immediately so intent never evaporates.
    updateFocus({ q: q });

    function mergeIdScale(ids, scales, id, scale) {
      if (!id) return;
      if (ids.indexOf(id) === -1) ids.push(id);
      var next = Math.min(1.25, Math.max(0.12, scale || 0.35));
      scales[id] = Math.max(scales[id] || 0, next);
    }

    function collectFromResults(ids, scales, artists, genres, handles) {
      var top = results.slice(0, 8);
      for (var i = 0; i < top.length; i++) {
        var item = top[i];
        if (!item) continue;
        var falloff = 1 - i * 0.08;
        var entityIds = resolveEntities(item);
        for (var e = 0; e < entityIds.length; e++) {
          var eid = entityIds[e];
          mergeIdScale(ids, scales, eid, 0.55 * falloff * refineBoost);
          // Focus vectors must follow graph-resolved genres even when search
          // JSON omits genre fields (slim search.json has none).
          var ep = String(eid).split(':');
          if (ep[0] === 'artist' && ep[1]) artists.push(ep[1]);
          if (ep[0] === 'genre' && ep[1]) genres.push(ep[1]);
        }
        if (item.handle) {
          handles.push(item.handle);
          mergeIdScale(ids, scales, 'product:' + item.handle, 0.4 * falloff * refineBoost);
        }
        if (item.artist) {
          var aSlug = handleize(item.artist);
          if (aSlug && aSlug !== 'various' && aSlug !== 'various-artists') {
            artists.push(aSlug);
          }
        }
        var gs = item.genres || [];
        for (var g = 0; g < gs.length; g++) {
          var gSlug = handleize(gs[g]);
          if (gSlug) genres.push(gSlug);
        }
      }
    }

    function collectFromLinkQuery(ids, scales, artists, genres) {
      if (!graphReady() || typeof window.wmnCatalogGraph.linkQuery !== 'function') {
        return;
      }
      var linked = window.wmnCatalogGraph.linkQuery(q) || [];
      var top = linked.slice(0, 8);
      for (var i = 0; i < top.length; i++) {
        var row = top[i];
        if (!row || !row.id) continue;
        var rawScore = row.score || 0;
        var scale = Math.min(1, Math.max(0.15, rawScore / 100)) * refineBoost;
        var p = String(row.id).split(':');
        // Clear genre queries (e.g. "blues", "samba", "cuba"→alias 100) need a
        // strong enough bump to move For you past bestsellers — still below
        // search_click. Floor 50 covers token-prefix genre hits (~50–57).
        if (p[0] === 'genre' && rawScore >= 50) {
          scale = Math.min(1.25, Math.max(scale, 1.1 * refineBoost));
        }
        mergeIdScale(ids, scales, row.id, scale);
        if (p[0] === 'artist' && p[1]) artists.push(p[1]);
        if (p[0] === 'genre' && p[1]) genres.push(p[1]);
      }
    }

    function applyAll(includeLinkQuery, includeResults) {
      var ids = [];
      var scales = {};
      var artists = [];
      var genres = [];
      var handles = [];
      if (includeLinkQuery) collectFromLinkQuery(ids, scales, artists, genres);
      if (includeResults) collectFromResults(ids, scales, artists, genres, handles);
      if (!ids.length && !artists.length && !genres.length) {
        try {
          window.__wmnTypedPrefsDirty = true;
        } catch (e0) {}
        return true;
      }
      var store = readStore();
      if (ids.length) bumpEntities(store, ids, 'search', scales);
      writeStore(store);
      scheduleCloudWriteback();
      // Typed search owns primaryKind from top linkQuery hit so a prior
      // Spiritual India country focus does not keep hard-filtering while the
      // query clearly names Indian Classical/Raga (or Blues, Jazz, …).
      var focusOpts = {
        artists: artists,
        genres: genres,
        handles: handles.slice(0, 6),
        q: q
      };
      if (
        includeLinkQuery &&
        graphReady() &&
        typeof window.wmnCatalogGraph.linkQuery === 'function'
      ) {
        var topLinked = (window.wmnCatalogGraph.linkQuery(q) || [])[0];
        if (topLinked && topLinked.id && (topLinked.score || 0) >= 50) {
          var topParts = String(topLinked.id).split(':');
          if (topParts[0] === 'genre' && topParts[1]) {
            focusOpts.primaryKind = 'genre';
            focusOpts.primaryCountry = '';
            focusOpts.primaryRegion = '';
            if (genres.indexOf(topParts[1]) === -1) genres.unshift(topParts[1]);
          } else if (topParts[0] === 'country' && topParts[1]) {
            focusOpts.primaryKind = 'country';
            focusOpts.primaryCountry = topParts[1];
            focusOpts.primaryRegion = '';
          } else if (topParts[0] === 'region' && topParts[1]) {
            focusOpts.primaryKind = 'region';
            focusOpts.primaryRegion = topParts[1];
            focusOpts.primaryCountry = '';
          }
        }
      }
      updateFocus(focusOpts);
      try {
        window.__wmnTypedPrefsDirty = true;
      } catch (e1) {}
      dispatchPrefsUpdated(store);
      return true;
    }

    ensureGraphWarm();
    if (!graphReady() && window.wmnCatalogGraph && window.wmnCatalogGraph.ensureLoaded) {
      // Visible-result bumps apply immediately; graph linkQuery follows once.
      applyAll(false, true);
      window.wmnCatalogGraph
        .ensureLoaded()
        .then(function () {
          applyAll(true, false);
        })
        .catch(function () {});
      return true;
    }
    return applyAll(true, true);
  }

  /**
   * Query → handle click learning (no graph rebuild).
   * Local store strengthens this shopper immediately; anonymous POST to
   * prefs-proxy /query-click strengthens collective warm-instance boosts.
   */
  var QUERY_CLICK_KEY = 'wmn_query_click_v1';
  var QUERY_CLICK_HALF_LIFE_MS = 1000 * 60 * 60 * 24 * 45;
  var QUERY_CLICK_BUMP = 1;
  var QUERY_CLICK_MAX_W = 25;
  var QUERY_CLICK_MAX_HANDLES = 12;
  /**
   * Score delta per click-weight unit. Must beat fuzzy position prior
   * ((n-index)*10 ≈ up to 300–400) or clicks never surface mid-pack titles
   * like Timchar among equal desert-blues scores.
   */
  var QUERY_CLICK_SCORE_SCALE = 140;
  var QUERY_CLICK_COLLECTIVE_SCALE = 0.85;
  var queryClickCollectiveCache = Object.create(null);
  var queryClickCollectiveInflight = Object.create(null);

  function prefsProxyBaseEngagement() {
    try {
      var meta = document.querySelector('meta[name="wmn-prefs-sync-proxy"]');
      var v = meta && meta.getAttribute('content');
      if (v) return String(v).replace(/\/$/, '');
    } catch (eMeta) {
      /* ignore */
    }
    return 'https://wmn-prefs-sync.vercel.app';
  }

  function normalizeQueryClickKey(q) {
    if (
      window.wmnSearchFuzzy &&
      typeof window.wmnSearchFuzzy.normalize === 'function'
    ) {
      return window.wmnSearchFuzzy.normalize(q) || '';
    }
    return String(q || '')
      .toLowerCase()
      .replace(/[^\w\s\-']/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .slice(0, 80);
  }

  function queryClickAllowed(q) {
    var nq = normalizeQueryClickKey(q);
    if (!nq || nq.length < 2) return false;
    try {
      if (
        window.wmnQueryGuard &&
        typeof window.wmnQueryGuard.isBlocked === 'function' &&
        window.wmnQueryGuard.isBlocked(nq)
      ) {
        return false;
      }
    } catch (eGuard) {
      /* ignore */
    }
    return true;
  }

  function decayQueryClickWeight(w, t, ts) {
    w = Number(w) || 0;
    t = Number(t) || 0;
    if (!w) return 0;
    if (!t || !ts || ts <= t) return w;
    return w * Math.pow(0.5, (ts - t) / QUERY_CLICK_HALF_LIFE_MS);
  }

  function readQueryClickStore() {
    try {
      var raw = window.localStorage.getItem(QUERY_CLICK_KEY);
      if (!raw) return { v: 1, q: {} };
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return { v: 1, q: {} };
      if (!parsed.q || typeof parsed.q !== 'object') parsed.q = {};
      return parsed;
    } catch (e) {
      return { v: 1, q: {} };
    }
  }

  function writeQueryClickStore(store) {
    try {
      window.localStorage.setItem(QUERY_CLICK_KEY, JSON.stringify(store));
    } catch (e) {
      /* ignore quota */
    }
  }

  function pruneQueryClickRow(row, ts) {
    var entries = [];
    var h;
    for (h in row) {
      if (!Object.prototype.hasOwnProperty.call(row, h)) continue;
      if (!isLikelyProductHandle(h)) continue;
      var cur = row[h];
      if (!cur) continue;
      var w = decayQueryClickWeight(cur.w, cur.t, ts);
      if (w < 0.12) continue;
      entries.push({ h: h, w: w, t: cur.t || ts });
    }
    entries.sort(function (a, b) {
      return b.w - a.w || (a.h < b.h ? -1 : 1);
    });
    if (entries.length > QUERY_CLICK_MAX_HANDLES) {
      entries = entries.slice(0, QUERY_CLICK_MAX_HANDLES);
    }
    var out = {};
    var i;
    for (i = 0; i < entries.length; i++) {
      out[entries[i].h] = { w: entries[i].w, t: entries[i].t };
    }
    return out;
  }

  function postQueryClickCollective(nq, handle) {
    if (!nq || !handle || typeof fetch !== 'function') return;
    var base = prefsProxyBaseEngagement();
    if (!base) return;
    try {
      fetch(base + '/query-click', {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: nq, handle: handle })
      })
        .then(function (res) {
          return res && res.ok ? res.json() : null;
        })
        .then(function (json) {
          if (json && json.boosts && typeof json.boosts === 'object') {
            queryClickCollectiveCache[nq] = {
              t: now(),
              boosts: json.boosts
            };
          }
        })
        .catch(function () {});
    } catch (ePost) {
      /* ignore */
    }
  }

  /**
   * Prefetch collective boosts for a typed query (settle / rank path).
   * @returns {Promise<Object.<string,number>>}
   */
  function ensureQueryClickCollective(q) {
    var nq = normalizeQueryClickKey(q);
    if (!queryClickAllowed(nq)) return Promise.resolve({});
    var cached = queryClickCollectiveCache[nq];
    if (cached && cached.boosts && now() - (cached.t || 0) < 1000 * 60 * 10) {
      return Promise.resolve(cached.boosts);
    }
    if (queryClickCollectiveInflight[nq]) {
      return queryClickCollectiveInflight[nq];
    }
    var base = prefsProxyBaseEngagement();
    if (!base || typeof fetch !== 'function') {
      return Promise.resolve({});
    }
    queryClickCollectiveInflight[nq] = fetch(
      base + '/query-click?q=' + encodeURIComponent(nq),
      { credentials: 'omit' }
    )
      .then(function (res) {
        if (!res || !res.ok) return {};
        return res.json();
      })
      .then(function (json) {
        var boosts =
          json && json.boosts && typeof json.boosts === 'object'
            ? json.boosts
            : {};
        queryClickCollectiveCache[nq] = { t: now(), boosts: boosts };
        return boosts;
      })
      .catch(function () {
        return {};
      })
      .then(function (boosts) {
        delete queryClickCollectiveInflight[nq];
        return boosts;
      });
    return queryClickCollectiveInflight[nq];
  }

  /** Record that query `q` led to a click on `handle`. */
  function recordQueryClick(q, handle) {
    var nq = normalizeQueryClickKey(q);
    var h = normalizeRecentlyViewedHandle(handle) || String(handle || '').toLowerCase();
    if (!queryClickAllowed(nq)) return;
    if (!isLikelyProductHandle(h)) return;
    var ts = now();
    var store = readQueryClickStore();
    var row = pruneQueryClickRow(store.q[nq] || {}, ts);
    var cur = row[h] || { w: 0, t: ts };
    row[h] = {
      w: Math.min(
        QUERY_CLICK_MAX_W,
        decayQueryClickWeight(cur.w, cur.t, ts) + QUERY_CLICK_BUMP
      ),
      t: ts
    };
    store.q[nq] = pruneQueryClickRow(row, ts);
    writeQueryClickStore(store);
    postQueryClickCollective(nq, h);
  }

  /**
   * Raw merged weight (local + collective) for query/handle — before score scale.
   */
  function queryClickWeight(q, handle) {
    var nq = normalizeQueryClickKey(q);
    var h = String(handle || '').toLowerCase();
    if (!nq || !h) return 0;
    var ts = now();
    var local = 0;
    try {
      var store = readQueryClickStore();
      var row = store.q && store.q[nq];
      if (row && row[h]) {
        local = decayQueryClickWeight(row[h].w, row[h].t, ts);
      }
    } catch (eLocal) {
      local = 0;
    }
    var collective = 0;
    var cached = queryClickCollectiveCache[nq];
    if (cached && cached.boosts && cached.boosts[h] != null) {
      collective = Number(cached.boosts[h]) || 0;
    }
    return local + collective * QUERY_CLICK_COLLECTIVE_SCALE;
  }

  /** Score delta for fuzzyRank decoration. */
  function queryClickBoost(q, handle) {
    var w = queryClickWeight(q, handle);
    if (w <= 0) return 0;
    return w * QUERY_CLICK_SCORE_SCALE;
  }

  /**
   * Handles previously clicked for this query, strongest first.
   * Used to pin Timchar to the front after "Desert" → Timchar clicks.
   */
  function queryClickTopHandles(q, limit) {
    limit = limit != null ? limit : 6;
    var nq = normalizeQueryClickKey(q);
    if (!nq) return [];
    var ts = now();
    var weights = {};
    try {
      var store = readQueryClickStore();
      var row = store.q && store.q[nq];
      var h;
      if (row) {
        for (h in row) {
          if (!Object.prototype.hasOwnProperty.call(row, h)) continue;
          var w = decayQueryClickWeight(row[h].w, row[h].t, ts);
          if (w > 0) weights[h] = (weights[h] || 0) + w;
        }
      }
    } catch (eLocal) {
      /* ignore */
    }
    var cached = queryClickCollectiveCache[nq];
    if (cached && cached.boosts) {
      var ch;
      for (ch in cached.boosts) {
        if (!Object.prototype.hasOwnProperty.call(cached.boosts, ch)) continue;
        weights[ch] =
          (weights[ch] || 0) +
          (Number(cached.boosts[ch]) || 0) * QUERY_CLICK_COLLECTIVE_SCALE;
      }
    }
    var entries = [];
    var k;
    for (k in weights) {
      if (!Object.prototype.hasOwnProperty.call(weights, k)) continue;
      if (weights[k] < 0.2) continue;
      if (!isLikelyProductHandle(k)) continue;
      entries.push({ h: k, w: weights[k] });
    }
    entries.sort(function (a, b) {
      return b.w - a.w || (a.h < b.h ? -1 : 1);
    });
    var out = [];
    var i;
    for (i = 0; i < entries.length && out.length < limit; i++) {
      out.push(entries[i].h);
    }
    return out;
  }

  /** Stronger bump when user clicks a discover / search result card. */
  function recordSearchClick(payload) {
    if (!payload || !payload.handle) return;
    ensureGraphWarm();
    if (payload.q) {
      try {
        recordQueryClick(payload.q, payload.handle);
      } catch (eQc) {
        /* ignore */
      }
    }
    var owns = fieldOwnsRanking();
    if (
      window.wmnIntentField &&
      typeof window.wmnIntentField.onClick === 'function'
    ) {
      try {
        window.wmnIntentField.onClick(payload.handle);
        if (typeof window.wmnIntentField.learnFromSignal === 'function') {
          window.wmnIntentField.learnFromSignal('click', true, {
            handle: payload.handle
          });
        }
      } catch (fieldClickErr) {
        /* ignore */
      }
    }
    var store = readStore();
    // One brain: field owns lean/committed — skip entity/focus double-count.
    if (!owns) {
      var entityIds = resolveEntities(payload);
      bumpEntities(store, entityIds, 'search_click');
    }
    // Still record the click event for continue-lane pins / memory.
    var next = {
      t: now(),
      k: 'search_click',
      h: payload.handle,
      id: payload.id || '',
      g: payload.genres || [],
      title: payload.title || '',
      img: normalizeImageUrl(payload.image || payload.img || ''),
      price: payload.price || '',
      q: payload.q ? normalizeQueryClickKey(payload.q) : ''
    };
    var updated = false;
    for (var i = store.events.length - 1; i >= 0; i--) {
      if (store.events[i] && store.events[i].h === next.h) {
        store.events[i] = richerEvent(store.events[i], next);
        store.events[i].t = next.t;
        store.events[i].k = 'search_click';
        if (next.q) store.events[i].q = next.q;
        updated = true;
        break;
      }
    }
    if (!updated) store.events.push(next);
    writeStore(store);
    scheduleCloudWriteback();
    if (!owns) {
      updateFocusFromPayload(payload);
    }
    dispatchPrefsUpdated(store);
    // Analytics click lives here so discover does not double-log.
    logIntentEvent({
      type: 'click',
      handle: payload.handle,
      artist: payload.artist || null,
      meta: {
        source: 'search_click',
        q: next.q || null
      }
    });
  }

  function recordFromPage() {
    var node = document.getElementById('wmn-engagement-product');
    if (!node) return;
    try {
      var payload = JSON.parse(node.textContent || '{}');
      recordView(payload);
    } catch (e) {
      /* ignore */
    }
  }

  function ensureBanner(container) {
    if (!container || container.querySelector('.wmn-engagement-banner')) return;
    var tag = container.tagName === 'UL' || container.tagName === 'OL' ? 'li' : 'p';
    var el = document.createElement(tag);
    el.className = 'wmn-engagement-banner';
    el.setAttribute('role', 'status');
    el.textContent = 'Sorted for you based on albums you viewed';
    container.insertBefore(el, container.firstChild);
  }

  function activeSearchTerm() {
    var inputs = document.querySelectorAll('input[name="q"]');
    var best = '';
    for (var i = 0; i < inputs.length; i++) {
      var v = String(inputs[i].value || '')
        .toLowerCase()
        .replace(/\*/g, '')
        .trim();
      if (v.length > best.length) best = v;
    }
    return best;
  }

  function eventTitleHandleMatch(ev, term) {
    if (!term || !ev) return false;
    var title = String(ev.title || '').toLowerCase();
    var handle = String(ev.h || '').toLowerCase().replace(/-/g, ' ');
    return title.indexOf(term) !== -1 || handle.indexOf(term) !== -1;
  }

  function pickMemoryHit(store, term, existing) {
    var best = null;
    for (var j = store.events.length - 1; j >= 0; j--) {
      var ev = store.events[j];
      if (!ev || !ev.h || existing[ev.h] || !catalogOffers(ev.h)) continue;
      if (!eventTitleHandleMatch(ev, term)) continue;
      best = ev;
      break;
    }
    return best;
  }

  function catalogOffers(handle) {
    var g = window.wmnCatalogGraph;
    if (!g || typeof g.getData !== 'function') return true;
    var data = g.getData();
    if (!data || !data.products) return true;
    return !!data.products[handle];
  }

  function buildMemoryResultItem(ev) {
    if (!ev || !ev.h || !catalogOffers(ev.h)) return null;
    var li = document.createElement('li');
    li.className = 'item-result result--product wmn-memory-result';
    li.setAttribute('data-wmn-injected', '1');

    var a = document.createElement('a');
    a.href = '/products/' + encodeURIComponent(ev.h);
    a.setAttribute('data-no-instant', 'true');

    var title = escapeHtml(ev.title || ev.h.replace(/-/g, ' '));
    var price = escapeHtml(ev.price || 'Recently viewed');
    var img = withShopifyImageWidth(normalizeImageUrl(ev.img), 200);
    var html = '';
    if (img) {
      html +=
        '<div class="thumbnail"><img src="' +
        escapeHtml(img) +
        '" alt="" width="70" height="70" loading="lazy" /></div>';
    }
    html +=
      '<div class="title">' +
      title +
      '<br><span class="item-pricing">' +
      price +
      '</span></div>';
    a.innerHTML = html;

    // Bypass InstantClick / churn: navigate on explicit activation.
    a.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      window.location.href = a.href;
    });

    li.appendChild(a);
    return li;
  }

  function injectMemoryIntoAutocomplete(list, term, store) {
    if (!list || !term || !store.events.length) return 0;
    if (list.querySelector('[data-wmn-injected="1"]')) {
      list.setAttribute('data-wmn-injected-term', term);
      return 0;
    }
    if (list.getAttribute('data-wmn-injected-term') === term) {
      list.removeAttribute('data-wmn-injected-term');
    }

    var existing = {};
    var links = list.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var h = productHandleFromHref(links[i].getAttribute('href'));
      if (h) existing[h] = true;
    }

    var ev = pickMemoryHit(store, term, existing);
    if (!ev) {
      list.setAttribute('data-wmn-injected-term', term);
      return 0;
    }

    var li = buildMemoryResultItem(ev);
    if (!li) {
      list.setAttribute('data-wmn-injected-term', term);
      return 0;
    }
    var firstProduct = list.querySelector('li.item-result');
    if (firstProduct) list.insertBefore(li, firstProduct);
    else {
      var seeAll = list.querySelector('li.all-results');
      if (seeAll) list.insertBefore(li, seeAll);
      else list.appendChild(li);
    }
    list.setAttribute('data-wmn-injected-term', term);
    return 1;
  }

  function listSignature(items) {
    return items
      .map(function (el) {
        var a = el.querySelector('a[href*="/products/"]');
        return productHandleFromHref(a && a.getAttribute('href'));
      })
      .join('|');
  }

  function reorderAutocompleteLists() {
    var store = readStore();
    if (!store.events.length && !Object.keys(store.entities || {}).length) return false;
    var affinity = getAffinityCompat();
    var term = activeSearchTerm();
    var lists = document.querySelectorAll('.search__results');
    var any = false;

    for (var i = 0; i < lists.length; i++) {
      var list = lists[i];
      if (!list.querySelector('li.item-result, li.all-results')) continue;

      injectMemoryIntoAutocomplete(list, term, store);

      var items = Array.prototype.slice.call(
        list.querySelectorAll('li.item-result.result--product, li.item-result')
      ).filter(function (li) {
        return !!li.querySelector('a[href*="/products/"]');
      });
      if (items.length < 1) continue;

      var scored = items.map(function (el, index) {
        var a = el.querySelector('a[href*="/products/"]');
        var handle = productHandleFromHref(a && a.getAttribute('href'));
        var title = a ? a.textContent || '' : '';
        var entityScore = scoreHandle(handle);
        return {
          el: el,
          index: index,
          score: scoreByHandleTitleGenres(handle, title, [], affinity) + entityScore,
          handle: handle
        };
      });

      if (!scored.some(function (row) { return row.score > 0; })) continue;

      scored.sort(function (a, b) {
        if (b.score !== a.score) return b.score - a.score;
        return a.index - b.index;
      });

      var before = listSignature(items);
      var after = scored
        .map(function (row) { return row.handle; })
        .join('|');
      var sig = term + '::' + after;

      if (list.getAttribute('data-wmn-sorted') === sig && before === after) {
        ensureBanner(list);
        any = true;
        continue;
      }

      if (before !== after) {
        var seeAll = list.querySelector('li.all-results');
        var banner = list.querySelector('.wmn-engagement-banner');
        scored.forEach(function (row) {
          list.appendChild(row.el);
        });
        if (seeAll) list.appendChild(seeAll);
        if (banner) list.insertBefore(banner, list.firstChild);
      }

      list.setAttribute('data-wmn-sorted', sig);
      ensureBanner(list);
      any = true;
    }
    return any;
  }

  function bindEngagementObservers() {
    if (window.__wmnEngObs) return;
    window.__wmnEngObs = true;

    document.addEventListener(
      'click',
      function (e) {
        var chipA =
          e.target && e.target.closest ? e.target.closest('a.wmn-genres__chip') : null;
        if (chipA) window.__wmnProductNavUntil = Date.now() + 1600;
        var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
        if (!a) return;
        if (a.closest && a.closest('.search__results')) return;
        var handle = productHandleFromHref(a.getAttribute('href'));
        if (!handle) return;
        // Repainting the collection grid in this click removes the link
        // before the browser follows it, so the page reshuffles and stays put.
        window.__wmnProductNavUntil = Date.now() + 1600;
        var imgEl = a.querySelector('img');
        recordView({
          handle: handle,
          title: (a.textContent || '').trim(),
          image: withShopifyImageWidth(bestImgUrlFromElement(imgEl), 1200),
          genres: []
        });
        if (
          e.button === 0 &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.altKey &&
          (!a.target || a.target === '_self')
        ) {
          var href = a.href;
          var before = location.href;
          setTimeout(function () {
            if (!href || location.href !== before) return;
            location.assign(href);
          }, 400);
        }
      },
      true
    );

    if (window.MutationObserver) {
      var scheduled = null;
      var obs = new MutationObserver(function (mutations) {
        if (window.__wmnEngMutating) return;
        var relevant = false;
        for (var m = 0; m < mutations.length; m++) {
          var t = mutations[m].target;
          if (
            t &&
            t.closest &&
            t.closest('.search__results, .search__results-wrapper')
          ) {
            relevant = true;
            break;
          }
          var nodes = mutations[m].addedNodes;
          for (var n = 0; n < nodes.length; n++) {
            var node = nodes[n];
            if (
              node.nodeType === 1 &&
              (node.classList.contains('search__results') ||
                node.querySelector &&
                  node.querySelector('.search__results'))
            ) {
              relevant = true;
              break;
            }
          }
          if (relevant) break;
        }
        if (!relevant) return;
        if (scheduled) return;
        scheduled = window.requestAnimationFrame(function () {
          scheduled = null;
          window.__wmnEngMutating = true;
          try {
            reorderAutocompleteLists();
          } finally {
            window.__wmnEngMutating = false;
          }
        });
      });
      obs.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function searchTermFromPage() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      return String(params.get('q') || '')
        .toLowerCase()
        .replace(/\*/g, '')
        .trim();
    } catch (e) {
      return '';
    }
  }

  function lastViewedByHandle(events) {
    var map = {};
    for (var i = 0; i < (events || []).length; i++) {
      var ev = events[i];
      if (!ev || !ev.h || !ev.t) continue;
      var prev = map[ev.h];
      if (!prev || (ev.t || 0) >= (prev.t || 0)) {
        map[ev.h] = richerEvent(prev, ev);
        map[ev.h].t = Math.max(prev ? prev.t || 0 : 0, ev.t || 0);
      }
    }
    return map;
  }

  function termMatchingViews(store, term) {
    if (!term || !store || !store.events.length) return [];
    var viewed = lastViewedByHandle(store.events);
    var list = [];
    for (var h in viewed) {
      if (!Object.prototype.hasOwnProperty.call(viewed, h)) continue;
      var ev = viewed[h];
      if (eventTitleHandleMatch(ev, term)) list.push(ev);
    }
    list.sort(function (a, b) {
      return (b.t || 0) - (a.t || 0);
    });
    return list;
  }

  function recencyScoreForElement(el, term, viewedMap) {
    var handle = el.getAttribute('data-wmn-handle') || '';
    if (!handle || !viewedMap[handle]) return 0;
    var viewed = viewedMap[handle];
    if (eventTitleHandleMatch(viewed, term)) return viewed.t || 0;
    var title = String(el.getAttribute('data-wmn-title') || '').toLowerCase();
    var handleWords = handle.toLowerCase().replace(/-/g, ' ');
    if (
      term &&
      (title.indexOf(term) !== -1 || handleWords.indexOf(term) !== -1)
    ) {
      return viewed.t || 0;
    }
    return 0;
  }

  function bindMemoryNav(root) {
    var links = root.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      (function (a) {
        a.setAttribute('data-no-instant', 'true');
        a.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = a.href;
        });
      })(links[i]);
    }
  }

  function formatChipsHtml(tags, title) {
    var hasCd = false;
    var hasDigital = false;
    var hasVinyl = false;
    var list = tags || [];
    for (var i = 0; i < list.length; i++) {
      var t = String(list[i] || '').toLowerCase().trim();
      if (t === 'cd' || t === '2xcd' || t === '2 x cd' || t === 'cds' || t.indexOf('x cd') !== -1 || t.indexOf('xcd') !== -1) {
        hasCd = true;
      } else if (t === 'digital' || t === 'download' || t === 'mp3' || t === 'flac') {
        hasDigital = true;
      } else if (t === 'vinyl' || t === 'vinyl lp' || t === 'lp' || t.indexOf('vinyl') !== -1) {
        hasVinyl = true;
      }
    }
    if (!hasCd && !hasDigital && !hasVinyl) {
      var titleL = String(title || '').toLowerCase();
      if (titleL.indexOf('vinyl') !== -1) hasVinyl = true;
      if (titleL.indexOf('digital') !== -1 || titleL.indexOf('download') !== -1 || titleL.indexOf('mp3') !== -1) {
        hasDigital = true;
      }
      if (
        titleL.indexOf(' cd') !== -1 ||
        titleL.indexOf('(cd)') !== -1 ||
        titleL.indexOf('x cd') !== -1 ||
        titleL.indexOf('cd bundle') !== -1 ||
        titleL.indexOf('cds') !== -1
      ) {
        hasCd = true;
      }
    }
    if (!hasCd && !hasDigital && !hasVinyl) return '';
    var html = '<span class="wmn-format" aria-label="Format">';
    if (hasCd) html += '<span class="wmn-format__chip">CD</span>';
    if (hasDigital) html += '<span class="wmn-format__chip">Digital</span>';
    if (hasVinyl) html += '<span class="wmn-format__chip">Vinyl</span>';
    html += '</span>';
    return html;
  }

  function collectionCardExtras(handle, genres) {
    var sp = '';
    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.getSpotifyPath === 'function'
      ) {
        sp = window.wmnCatalogGraph.getSpotifyPath(handle) || '';
      }
    } catch (eSp) {
      sp = '';
    }
    var play = '';
    if (sp) {
      play =
        '<button type="button" class="wmn-card-cover__play" data-wmn-play data-wmn-listen data-wmn-handle="' +
        escapeHtml(handle) +
        '" data-wmn-spotify="' +
        escapeHtml(sp) +
        '" data-wmn-play-source="collection-grid" aria-label="Play preview" aria-pressed="false">' +
        '<svg class="wmn-card-cover__play-icon wmn-card-cover__play-icon--play" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"/></svg>' +
        '<svg class="wmn-card-cover__play-icon wmn-card-cover__play-icon--pause" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>' +
        '</button>';
    }
    var genreHtml = '';
    var list = genres || [];
    if (list.length) {
      genreHtml = '<div class="wmn-genres wmn-genres--compact" data-wmn-genres><ul class="wmn-genres__list" role="list">';
      var gi;
      for (gi = 0; gi < list.length && gi < 3; gi++) {
        var label = list[gi];
        if (!label) continue;
        genreHtml +=
          '<li><a class="wmn-genres__chip" href="/collections/all/' +
          escapeHtml(handleize(label)) +
          '">' +
          escapeHtml(label) +
          '</a></li>';
      }
      genreHtml += '</ul></div>';
    }
    return { play: play, genres: genreHtml, sp: sp };
  }

  function buildSearchMemoryCard(ev, className, opts) {
    if (!ev || !ev.h || !catalogOffers(ev.h)) return null;
    var div = document.createElement('div');
    div.className = (className || 'thumbnail') + ' wmn-memory-search-card';
    div.setAttribute('data-wmn-handle', ev.h);
    div.setAttribute('data-wmn-title', ev.title || '');
    div.setAttribute('data-wmn-injected', '1');

    var href = '/products/' + encodeURIComponent(ev.h);
    var title = escapeHtml(ev.title || ev.h.replace(/-/g, ' '));
    var price = escapeHtml(ev.price || '');
    var kicker = '';
    if (window.wmnCardKicker) {
      kicker = window.wmnCardKicker({
        handle: ev.h,
        title: ev.title || '',
        artist: ev.artist || '',
        tags: ev.tags || []
      });
    }
    kicker = kicker ? '<span class="artist">' + escapeHtml(kicker) + '</span>' : '';
    var img = withShopifyImageWidth(normalizeImageUrl(ev.img), 1200);
    // Dig deeper paints into a [hidden] section first — Safari skips lazy loads there.
    var loadingAttr = opts && opts.eager ? 'eager' : 'lazy';
    var imgHtml = img
      ? '<img src="' +
        escapeHtml(img) +
        '" alt="' +
        title +
        '" loading="' +
        loadingAttr +
        '" decoding="async" width="800" height="800" />'
      : '';
    var formatHtml = formatChipsHtml(ev.tags, ev.title || '');
    var playHtml = '';
    var genreHtml = '';
    if (opts && opts.collection) {
      var extras = collectionCardExtras(ev.h, (opts && opts.genres) || []);
      playHtml = extras.play;
      genreHtml = extras.genres;
      if (extras.sp) div.setAttribute('data-wmn-spotify', extras.sp);
      if (opts.genres && opts.genres.length) {
        div.setAttribute('data-wmn-genres', opts.genres.join('|'));
      }
    }

    div.innerHTML =
      '<div class="product-wrap">' +
      '<div class="relative product_image">' +
      '<a href="' +
      href +
      '">' +
      '<div class="image__container">' +
      imgHtml +
      '</div></a>' +
      playHtml +
      '</div>' +
      '<a class="product-info__caption" href="' +
      href +
      '">' +
      kicker +
      '<span class="title">' +
      title +
      '</span>' +
      formatHtml +
      (price ? '<span class="price">' + price + '</span>' : '') +
      '</a>' +
      genreHtml +
      '</div>';

    bindMemoryNav(div);
    return div;
  }

  function injectViewedIntoSearchGrid(grid, term, store) {
    if (!grid || !term || !store.events.length) return 0;
    if (
      grid.getAttribute('data-wmn-search-injected') === term &&
      grid.querySelector('[data-wmn-injected="1"]')
    ) {
      return 0;
    }

    var existing = {};
    var nodes = grid.querySelectorAll('[data-wmn-handle]');
    for (var i = 0; i < nodes.length; i++) {
      var h = nodes[i].getAttribute('data-wmn-handle');
      if (h) existing[h] = true;
    }

    var sample = grid.querySelector('[data-wmn-handle]:not([data-wmn-injected])');
    if (!sample) sample = grid.querySelector('[data-wmn-handle]');
    if (!sample) return 0;

    var className = sample.className
      .replace(/\bwmn-memory-search-card\b/g, '')
      .replace(/\beven\b/g, '')
      .replace(/\bodd\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    var matches = termMatchingViews(store, term);
    var toInsert = [];
    for (var j = 0; j < matches.length && toInsert.length < MAX_SEARCH_INJECT; j++) {
      if (existing[matches[j].h]) continue;
      toInsert.push(matches[j]);
    }
    if (!toInsert.length) {
      grid.setAttribute('data-wmn-search-injected', term);
      return 0;
    }

    var first = grid.querySelector('[data-wmn-handle]');
    for (var k = toInsert.length - 1; k >= 0; k--) {
      if (!catalogOffers(toInsert[k].h)) continue;
      var memoryCard = buildSearchMemoryCard(toInsert[k], className);
      if (memoryCard) grid.insertBefore(memoryCard, first);
    }
    grid.setAttribute('data-wmn-search-injected', term);
    nudgeStuckLazyImages(grid);
    return toInsert.length;
  }

  /**
   * After DOM reorder/inject, lazySizes often leaves blur-up LQIP (100x) stuck:
   * class stays "lazyload", filter:blur(5px), naturalWidth≈100. Scroll may not
   * re-fire, so viewport cards never unveil. Force-promote visible (and near)
   * product-grid covers, then ask lazySizes to rescan.
   */
  function nudgeStuckLazyImages(root) {
    var scope = root || document;
    function unveilImg(img) {
      if (!img) return false;
      var pending =
        img.getAttribute('data-src') ||
        img.getAttribute('data-srcset') ||
        '';
      var stuckLqip =
        img.classList.contains('lazyload') &&
        pending &&
        (!img.naturalWidth || img.naturalWidth <= 120);
      var missingSrc = !img.getAttribute('src') && !!pending;
      if (!stuckLqip && !missingSrc) return false;
      var full = img.getAttribute('data-src');
      var fullSet = img.getAttribute('data-srcset');
      if (fullSet) {
        img.setAttribute('srcset', fullSet);
        img.removeAttribute('data-srcset');
      }
      if (full) {
        img.setAttribute('src', full);
        img.removeAttribute('data-src');
      }
      img.classList.remove('lazyload', 'lazyloading');
      img.classList.add('lazyloaded');
      return true;
    }

    function run() {
      try {
        if (window.lazySizes && window.lazySizes.loader) {
          window.lazySizes.loader.checkElems();
        }
        if (window.lazySizes && window.lazySizes.autoSizer) {
          window.lazySizes.autoSizer.checkElems();
        }
      } catch (eLs) {
        /* ignore */
      }

      var vh = window.innerHeight || 800;
      var imgs = scope.querySelectorAll
        ? scope.querySelectorAll(
            '.product-list img.lazyload, .thumbnail img.lazyload, .search-matrix img.lazyload'
          )
        : [];
      var i;
      for (i = 0; i < imgs.length; i++) {
        var img = imgs[i];
        var rect = img.getBoundingClientRect();
        // Near viewport — reorder can move LQIP cards on-screen without scroll.
        if (rect.bottom < -240 || rect.top > vh + 900) continue;
        unveilImg(img);
      }

      // Always clear stuck covers on the first screen of product grids
      // (reorder can leave LQIP in row 1–2 with stale rects for a frame).
      var grids = scope.querySelectorAll
        ? scope.querySelectorAll('.product-list, .search-matrix')
        : [];
      for (i = 0; i < grids.length; i++) {
        var cards = grids[i].querySelectorAll('[data-wmn-handle]');
        var ci;
        for (ci = 0; ci < cards.length && ci < 12; ci++) {
          var cardImgs = cards[ci].querySelectorAll('img.lazyload');
          var ji;
          for (ji = 0; ji < cardImgs.length; ji++) unveilImg(cardImgs[ji]);
        }
      }

      // Injected memory cards with empty cover — fill from catalog graph.
      var memCards = scope.querySelectorAll
        ? scope.querySelectorAll('.wmn-memory-search-card[data-wmn-handle]')
        : [];
      for (i = 0; i < memCards.length; i++) {
        var card = memCards[i];
        if (card.querySelector('img')) continue;
        var handle = card.getAttribute('data-wmn-handle');
        if (!handle || !window.wmnCatalogGraph) continue;
        var gImg =
          typeof window.wmnCatalogGraph.getProductImage === 'function'
            ? window.wmnCatalogGraph.getProductImage(handle)
            : '';
        if (!gImg) continue;
        var box = card.querySelector('.image__container');
        if (!box) continue;
        var title = card.getAttribute('data-wmn-title') || '';
        box.innerHTML =
          '<img src="' +
          escapeHtml(withShopifyImageWidth(normalizeImageUrl(gImg), 800)) +
          '" alt="' +
          escapeHtml(title) +
          '" loading="eager" decoding="async" width="800" height="800" />';
      }
    }

    run();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function () {
        requestAnimationFrame(run);
      });
    }
    setTimeout(run, 120);
    setTimeout(run, 500);
  }

  function armStuckLazyImageObserver() {
    if (window.__wmnStuckLazyObs || typeof IntersectionObserver !== 'function') {
      return;
    }
    window.__wmnStuckLazyObs = true;
    var obs = new IntersectionObserver(
      function (entries) {
        var ei;
        for (ei = 0; ei < entries.length; ei++) {
          if (!entries[ei].isIntersecting) continue;
          var img = entries[ei].target;
          if (!img || !img.classList || !img.classList.contains('lazyload')) {
            continue;
          }
          var full = img.getAttribute('data-src');
          var fullSet = img.getAttribute('data-srcset');
          if (!full && !fullSet) continue;
          if (fullSet) {
            img.setAttribute('srcset', fullSet);
            img.removeAttribute('data-srcset');
          }
          if (full) {
            img.setAttribute('src', full);
            img.removeAttribute('data-src');
          }
          img.classList.remove('lazyload', 'lazyloading');
          img.classList.add('lazyloaded');
          obs.unobserve(img);
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 }
    );
    function scan() {
      var imgs = document.querySelectorAll(
        '.product-list img.lazyload[data-src], .thumbnail img.lazyload[data-src], .search-matrix img.lazyload[data-src]'
      );
      var i;
      for (i = 0; i < imgs.length; i++) obs.observe(imgs[i]);
    }
    scan();
    setTimeout(scan, 400);
    setTimeout(scan, 1200);
    document.addEventListener(
      'scroll',
      function () {
        if (window.__wmnStuckLazyScroll) return;
        window.__wmnStuckLazyScroll = true;
        requestAnimationFrame(function () {
          window.__wmnStuckLazyScroll = false;
          nudgeStuckLazyImages(document);
        });
      },
      { passive: true, capture: true }
    );
  }

  function reorderListByRecency(container, itemSelector, term, store) {
    var items = Array.prototype.slice.call(
      container.querySelectorAll(itemSelector)
    );
    if (items.length < 1) return false;
    if (!store.events.length || !term) return false;

    var viewedMap = lastViewedByHandle(store.events);
    var scored = items.map(function (el, index) {
      return {
        el: el,
        index: index,
        score: recencyScoreForElement(el, term, viewedMap),
        handle: el.getAttribute('data-wmn-handle') || ''
      };
    });

    if (!scored.some(function (row) { return row.score > 0; })) return false;

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    });

    if (!scored.some(function (row, i) { return row.index !== i; })) {
      ensureBanner(container);
      return true;
    }

    var loadMore = container.querySelector('.js-load-more, .load-more');
    var banner = container.querySelector('.wmn-engagement-banner');
    var frag = document.createDocumentFragment();
    scored.forEach(function (row) {
      frag.appendChild(row.el);
    });
    container.appendChild(frag);
    if (loadMore) container.appendChild(loadMore);
    if (banner) container.insertBefore(banner, container.firstChild);
    ensureBanner(container);
    nudgeStuckLazyImages(container);
    return true;
  }

  /**
   * When /search?q= matches an exact catalog genre, hide Shopify body/semantic
   * false hits (Gnawa / Rare Groove ranking for "Jazz") and prefer the tag
   * collection URL for a clean result set.
   */
  function filterExactGenreSearchResults(term) {
    if (!term) return false;
    if (
      !window.wmnCatalogGraph ||
      typeof window.wmnCatalogGraph.exactGenreFromQuery !== 'function'
    ) {
      return false;
    }
    if (!graphReady()) {
      ensureGraphWarm();
      if (window.wmnCatalogGraph.ensureLoaded) {
        window.wmnCatalogGraph.ensureLoaded().then(function () {
          filterExactGenreSearchResults(term);
        }).catch(function () {});
      }
      return false;
    }
    var slug = window.wmnCatalogGraph.exactGenreFromQuery(term);
    if (!slug) return false;

    // Full search page → curated tag collection (same chips as PDP genre pills).
    try {
      var path = (window.location.pathname || '').toLowerCase();
      if (path.indexOf('/search') !== -1) {
        var dest = '/collections/all/' + encodeURIComponent(slug);
        window.location.replace(dest);
        return true;
      }
    } catch (eRedir) {
      /* continue with in-page filter */
    }

    var roots = document.querySelectorAll(
      '.product-list.search-matrix, .search-matrix'
    );
    var hidden = 0;
    var ri;
    for (ri = 0; ri < roots.length; ri++) {
      var root = roots[ri];
      var cards = root.querySelectorAll('[data-wmn-handle]');
      var ci;
      for (ci = 0; ci < cards.length; ci++) {
        var el = cards[ci];
        var handle = el.getAttribute('data-wmn-handle') || '';
        if (!handle) continue;
        if (becauseProductMatchesGenre(handle, slug)) continue;
        el.style.display = 'none';
        el.setAttribute('data-wmn-genre-filtered', slug);
        hidden++;
      }
    }
    return hidden > 0;
  }

  function reorderSearch() {
    var term = searchTermFromPage() || activeSearchTerm();
    if (term && filterExactGenreSearchResults(term)) {
      // Redirected to genre collection.
      return true;
    }

    var store = readStore();
    if (!store.events.length) return false;
    if (!term) return false;

    // Product-grid search (settings.search_option = products)
    var grid = document.querySelector('.product-list.search-matrix');
    if (grid && grid.querySelector('[data-wmn-handle]')) {
      injectViewedIntoSearchGrid(grid, term, store);
      return reorderListByRecency(grid, '[data-wmn-handle]', term, store);
    }
    // List-style search fallback (reorder only)
    var list = document.querySelector('.search-matrix');
    if (list && list.querySelector('.product_row[data-wmn-handle]')) {
      return reorderListByRecency(
        list,
        '.product_row[data-wmn-handle]',
        term,
        store
      );
    }
    return false;
  }

  function stripAffinityBanner(container) {
    // Affinity grids used to inject "Personalized for you" — redundant under
    // Shopify "You may also like" and other product-list section headings.
    if (!container) return;
    var banner = container.querySelector('.wmn-engagement-banner');
    if (banner && banner.parentNode) banner.parentNode.removeChild(banner);
  }

  /** A genre or region chip puts a tag on the collection URL. */
  function collectionTagFilterActive() {
    try {
      var parts = String(location.pathname || '')
        .split('/')
        .filter(Boolean);
      return parts[0] === 'collections' && parts.length >= 3 && !!parts[2];
    } catch (eTag) {
      return false;
    }
  }

  /** Current collection sort from URL (empty ⇒ algorithm default). */
  function collectionSortFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      return String(params.get('sort_by') || '').trim();
    } catch (e) {
      return '';
    }
  }

  /**
   * Store/collection pages default to algorithm ("for-you") unless the shopper
   * picked an explicit Shopify sort (A–Z, price, date, best-selling, featured).
   */
  function wantsAlgorithmCollectionSort() {
    var sort = collectionSortFromUrl();
    if (!sort || sort === 'for-you') return true;
    return !EXPLICIT_SHOPIFY_SORTS[sort];
  }

  function isHomepagePersonalRail(node) {
    if (!node || !node.closest) return false;
    return !!node.closest(
      '#wmn-because-you-looked, #wmn-because-grid, #wmn-for-you, #wmn-for-you-grid, [data-wmn-rg-rail], [data-wmn-for-you], [data-wmn-because]'
    );
  }

  function isCollectionProductGrid(container) {
    if (!container || !container.classList) return false;
    if (isHomepagePersonalRail(container)) return false;
    if (container.classList.contains('search-matrix')) return false;
    if (container.closest && container.closest('.search-matrix')) return false;
    // Homepage rails reuse collection-matrix. Only the collection template
    // should be repainted, or the homepage jumps while you scroll.
    if (!container.closest) return false;
    return !!container.closest('.collection-template-section, .collection-template');
  }

  /**
   * Algorithm score for a collection card: intent-field whenever it has energy
   * (typed search settle, not only lean/committed), else entity prefs with
   * geo damp + focus latch. Stable index keeps Shopify order as tiebreaker.
   */
  function scoreCollectionCard(el, affinity) {
    var handle = el.getAttribute('data-wmn-handle') || '';
    if (!handle) return 0;
    var prim = collectionFocusPrimary();
    var score = 0;
    try {
      if (
        fieldHasRankableEnergy() &&
        window.wmnIntentField &&
        typeof window.wmnIntentField.scoreHandle === 'function'
      ) {
        score += window.wmnIntentField.scoreHandle(handle) || 0;
      } else {
        score += scoreHandleEntities(handle, collectionScoreCtx()) || 0;
      }
      // Focus lane always wins on Store For you — even when lean field still
      // has Jazz energy while the ghost says "indian classical raga".
      if (prim.dominant) {
        score += collectionFocusLatch(handle, prim);
        if (!productMatchesCollectionFocus(handle, prim)) {
          score -= 5000;
        }
      }
    } catch (eScore) {
      /* ignore */
    }
    if (affinity) {
      score += scoreElement(el, affinity) * 0.25;
    }
    return score;
  }

  /** Clear prior collection sort signatures so prefs/focus can re-rank. */
  function invalidateCollectionAlgorithmSort() {
    var grids = document.querySelectorAll(
      '.product-list.collection-matrix, .collection-template-section .product-list'
    );
    var i;
    for (i = 0; i < grids.length; i++) {
      grids[i].removeAttribute('data-wmn-aff-sorted');
      // Allow interest inject / paint to re-run when focus/field changes.
      grids[i].removeAttribute('data-wmn-interest-injected');
      grids[i].removeAttribute('data-wmn-fy-painted');
    }
  }

  function reorderAffinityGrid(container, opts) {
    if (!container) return false;
    opts = opts || {};
    stripAffinityBanner(container);
    var isCollection = isCollectionProductGrid(container);
    // Collection cards are .thumbnail[data-wmn-handle]. Play buttons and other
    // nested controls also carry data-wmn-handle — never move those or cards
    // get torn apart and zero-score orphans float into the ranking.
    var itemSelector = isCollection
      ? '.thumbnail[data-wmn-handle]'
      : '[data-wmn-handle]';
    var items = Array.prototype.slice.call(
      container.querySelectorAll(itemSelector)
    );
    if (!isCollection) {
      items = items.filter(function (el) {
        var parent = el.parentElement;
        while (parent && parent !== container) {
          if (parent.getAttribute && parent.getAttribute('data-wmn-handle')) {
            return false;
          }
          parent = parent.parentElement;
        }
        return true;
      });
      if (items.length < 2) return false;
    }

    if (isCollection && !wantsAlgorithmCollectionSort() && !opts.forceAlgorithm) {
      clearCollectionInterestInjects(container);
      clearCollectionForYouPaint(container);
      restoreCollectionSsr(container);
      return false;
    }

    ensureGraphWarm();
    if (isCollection) {
      // Warm interest: paint full ranked pool slice (shared homepage scorer).
      // Cold: keep native Shopify order (+ light reorder / inject fallback).
      if (collectionInterestWarm() && graphReady()) {
        try {
          if (paintCollectionForYouMatrix(container)) {
            syncCollectionSortSelect(true);
            return true;
          }
        } catch (ePaint) {
          /* fall through to inject/reorder */
        }
      } else {
        try {
          restoreCollectionSsr(container);
        } catch (eRest) {
          /* ignore */
        }
      }
      try {
        injectCollectionInterestMatches(container);
      } catch (eInj) {
        /* ignore */
      }
      items = Array.prototype.slice.call(
        container.querySelectorAll(itemSelector)
      );
      if (items.length < 2) return false;
    }

    var store = readStore();
    var affinity = getAffinityCompat();
    var hasSignal =
      (store.events && store.events.length) ||
      Object.keys(store.entities || {}).length ||
      Object.keys(affinity.genres || {}).length ||
      Object.keys(affinity.handles || {}).length ||
      fieldOwnsRanking() ||
      graphReady();

    // Collection pages: still reorder when graph is ready (cold = stable
    // Shopify order via index tiebreak). Other grids need affinity signal.
    if (!isCollection && !hasSignal) return false;
    if (
      !isCollection &&
      !Object.keys(affinity.genres).length &&
      !Object.keys(affinity.handles).length &&
      !Object.keys(store.entities || {}).length &&
      !(store.events && store.events.length)
    ) {
      return false;
    }

    var scored = items.map(function (el, index) {
      var handle = el.getAttribute('data-wmn-handle') || '';
      var score = isCollection
        ? scoreCollectionCard(el, affinity)
        : scoreElement(el, affinity) + scoreHandle(handle);
      return {
        el: el,
        index: index,
        score: score,
        handle: handle
      };
    });

    if (!isCollection && !scored.some(function (row) { return row.score > 0; })) {
      return false;
    }

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    });

    var before = scored
      .slice()
      .sort(function (a, b) { return a.index - b.index; })
      .map(function (r) { return r.handle; })
      .join('|');
    var after = scored.map(function (r) { return r.handle; }).join('|');
    var sig = (isCollection ? 'algo::' : 'aff::') + after;
    if (container.getAttribute('data-wmn-aff-sorted') === sig && before === after) {
      return false;
    }

    if (before === after) {
      container.setAttribute('data-wmn-aff-sorted', sig);
      syncCollectionSortSelect(isCollection);
      return false;
    }

    var loadMore = container.querySelector('.js-load-more, .load-more');
    var frag = document.createDocumentFragment();
    scored.forEach(function (row) {
      frag.appendChild(row.el);
    });
    container.appendChild(frag);
    if (loadMore) container.appendChild(loadMore);
    container.setAttribute('data-wmn-aff-sorted', sig);
    nudgeStuckLazyImages(container);
    syncCollectionSortSelect(isCollection);
    return true;
  }

  function syncCollectionSortSelect(isCollection) {
    if (isCollection === false) return;
    var sel = document.getElementById('sort-by');
    if (!sel) return;
    if (!wantsAlgorithmCollectionSort()) return;
    var opt = sel.querySelector('option[value="for-you"]');
    if (!opt) return;
    if (sel.value !== 'for-you') sel.value = 'for-you';
  }

  function reorderProductGrids() {
    // Skip full search matrix (handled by reorderSearch recency)
    var any = false;
    var seen = [];
    function consider(grid) {
      if (!grid || seen.indexOf(grid) !== -1) return;
      seen.push(grid);
      if (isHomepagePersonalRail(grid)) return;
      if (grid.classList.contains('search-matrix')) return;
      if (grid.closest && grid.closest('.search-matrix')) return;
      if (reorderAffinityGrid(grid)) any = true;
    }
    var i;
    for (i = 0; i < GRID_SELECTORS.length; i++) {
      var nodes = document.querySelectorAll(GRID_SELECTORS[i]);
      var j;
      for (j = 0; j < nodes.length; j++) consider(nodes[j]);
    }
    // Fallback: any collection matrix on the page
    var matrices = document.querySelectorAll('.product-list.collection-matrix');
    for (i = 0; i < matrices.length; i++) consider(matrices[i]);
    return any;
  }

  /**
   * After catalog graph loads, re-run collection algorithm sort (scores need
   * resolveProduct). Safe to call repeatedly.
   */
  function scheduleCollectionAlgorithmSort() {
    function run() {
      if (window.__wmnProductNavUntil && Date.now() < window.__wmnProductNavUntil) return;
      reorderProductGrids();
    }
    run();
    ensureGraphWarm();
    if (
      window.wmnCatalogGraph &&
      typeof window.wmnCatalogGraph.ensureLoaded === 'function'
    ) {
      window.wmnCatalogGraph.ensureLoaded().then(run).catch(function () {});
    }
    setTimeout(run, 400);
    setTimeout(run, 1200);
  }

  function bindCollectionSortSelect() {
    if (window.__wmnCollectionSortBound) return;
    window.__wmnCollectionSortBound = true;
    document.addEventListener(
      'change',
      function (ev) {
        var sel = ev.target;
        if (!sel || sel.id !== 'sort-by') return;
        var val = sel.value;
        if (val === 'for-you') {
          ev.stopImmediatePropagation();
          ev.preventDefault();
          // Drop sort_by so Shopify keeps default page payload; we reorder client-side.
          try {
            var url = new URL(window.location.href);
            url.searchParams.delete('sort_by');
            window.history.replaceState('', '', url.pathname + url.search + url.hash);
          } catch (eUrl) {
            /* ignore */
          }
          var grids = document.querySelectorAll(
            '.collection-template-section .product-list.collection-matrix, .collection-template .product-list.collection-matrix, .collection-template-section .product-list'
          );
          var gi;
          for (gi = 0; gi < grids.length; gi++) {
            if (isHomepagePersonalRail(grids[gi])) continue;
            grids[gi].removeAttribute('data-wmn-aff-sorted');
            reorderAffinityGrid(grids[gi], { forceAlgorithm: true });
          }
          nudgeStuckLazyImages(document);
          return;
        }
        // Explicit Shopify sorts: navigate via existing quickFilter (bubbles).
      },
      true
    );

    // Re-apply after AJAX collection filter replaces .collection-matrix.
    if (typeof MutationObserver === 'function') {
      var timer = 0;
      var obs = new MutationObserver(function (mutations) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () {
          timer = 0;
          if (!wantsAlgorithmCollectionSort()) return;
          var grid = document.querySelector(
            '.collection-template-section .product-list.collection-matrix, .collection-template .product-list.collection-matrix'
          );
          if (!grid || isHomepagePersonalRail(grid)) return;
          // Only skip when the matrix itself was not replaced (same sorted grid).
          var replaced = false;
          var mi;
          for (mi = 0; mi < (mutations || []).length; mi++) {
            var m = mutations[mi];
            if (!m || !m.addedNodes) continue;
            var ni;
            for (ni = 0; ni < m.addedNodes.length; ni++) {
              var node = m.addedNodes[ni];
              if (!node || node.nodeType !== 1) continue;
              if (
                (node.classList &&
                  node.classList.contains('collection-matrix')) ||
                (node.querySelector &&
                  node.querySelector('.product-list.collection-matrix'))
              ) {
                replaced = true;
                break;
              }
            }
            if (replaced) break;
          }
          if (!replaced && grid.getAttribute('data-wmn-aff-sorted')) return;
          if (replaced) invalidateCollectionAlgorithmSort();
          scheduleCollectionAlgorithmSort();
        }, 80);
      });
      var root =
        document.querySelector('.collection-template-section') ||
        document.querySelector('.collection-template');
      if (root) {
        obs.observe(root, { childList: true, subtree: true });
      }
    }
  }

  /** PDP seed handle for Shopify rec gating (engagement JSON → URL). */
  function pdpSeedHandleFromPage() {
    var node = document.getElementById('wmn-engagement-product');
    if (node) {
      try {
        var payload = JSON.parse(node.textContent || '{}');
        if (payload && payload.handle) return String(payload.handle);
      } catch (eSeed) {
        /* ignore */
      }
    }
    return productHandleFromHref(window.location.pathname) || '';
  }

  /**
   * Shopify "You may also like" allow-list: keep cards that share the seed's
   * country, region, or genre (graph gates). Stops India/Africa filling a
   * France+Jazz PDP when Shopify's related API ignores geo tags.
   */
  function shopifyRecMatchesSeed(handle, seedResolved) {
    if (!handle || !seedResolved || !graphReady()) return false;
    var countries = seedResolved.countries || [];
    var regions = seedResolved.regions || [];
    var genres = seedResolved.genres || [];
    var i;
    for (i = 0; i < countries.length; i++) {
      if (becauseProductMatchesCountry(handle, countries[i])) return true;
    }
    for (i = 0; i < regions.length; i++) {
      if (becauseProductMatchesRegion(handle, regions[i])) return true;
    }
    for (i = 0; i < genres.length; i++) {
      if (becauseProductMatchesGenre(handle, genres[i])) return true;
    }
    return false;
  }

  function shopifyRecsColumnClass(list, index) {
    var sample = list && list.querySelector('[data-wmn-handle]');
    if (sample && sample.className) {
      var base = String(sample.className)
        .replace(/\bwmn-memory-search-card\b/g, '')
        .replace(/\bwmn-shopify-recs-topup\b/g, '')
        .replace(/\beven\b/g, '')
        .replace(/\bodd\b/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (base) {
        return base + (index % 2 === 0 ? ' even' : ' odd');
      }
    }
    var cycle = index % 4;
    var pos = cycle === 0 ? 'alpha' : cycle === 3 ? 'omega' : '';
    return (
      'thumbnail four columns' +
      (pos ? ' ' + pos : '') +
      (index % 2 === 0 ? ' even' : ' odd')
    );
  }

  function shopifyRecsTopUpCandidates(seedHandle, seedResolved, existing, need) {
    var out = [];
    if (!need || need <= 0 || !graphReady() || !seedResolved) return out;
    var expandIds = [];
    var seenId = {};
    function pushId(id) {
      if (!id || seenId[id]) return;
      seenId[id] = true;
      expandIds.push(id);
    }
    var i;
    var countries = seedResolved.countries || [];
    var regions = seedResolved.regions || [];
    var genres = seedResolved.genres || [];
    for (i = 0; i < countries.length; i++) {
      if (countries[i]) pushId('country:' + countries[i]);
    }
    for (i = 0; i < regions.length; i++) {
      if (regions[i]) pushId('region:' + regions[i]);
    }
    for (i = 0; i < genres.length; i++) {
      if (genres[i]) pushId('genre:' + genres[i]);
    }
    if (!expandIds.length) return out;

    var exclude = pdpExcludeSet(seedHandle);
    var candidates = window.wmnCatalogGraph.expand(expandIds, 2) || [];
    var ranked = [];
    for (i = 0; i < candidates.length; i++) {
      var h = candidates[i];
      if (!h || existing[h]) continue;
      if (!shopifyRecMatchesSeed(h, seedResolved)) continue;
      var item = graphItemFromHandle(h);
      if (!item) continue;
      if (pdpIsExcluded(h, item, exclude)) continue;
      ranked.push({
        handle: h,
        item: item,
        score: becauseRankScore(h, seedResolved)
      });
    }
    ranked.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return String(a.handle).localeCompare(String(b.handle));
    });
    for (i = 0; i < ranked.length && out.length < need; i++) {
      out.push(ranked[i]);
    }
    return out;
  }

  /**
   * Filter Shopify rec grids to seed geo/genre; top up from catalog graph.
   * No-op when not on a PDP or seed has no country/region/genre in graph.
   */
  function gateShopifyRecsList(list) {
    if (!list || !graphReady()) return false;
    var seedHandle = pdpSeedHandleFromPage();
    if (!seedHandle) return false;
    var seedResolved = window.wmnCatalogGraph.resolveProduct(seedHandle) || {};
    var hasLane =
      (seedResolved.countries && seedResolved.countries.length) ||
      (seedResolved.regions && seedResolved.regions.length) ||
      (seedResolved.genres && seedResolved.genres.length);
    if (!hasLane) return false;

    var cards = Array.prototype.slice.call(
      list.querySelectorAll('[data-wmn-handle]')
    );
    if (!cards.length) return false;

    var targetCount = cards.length;
    var existing = {};
    var kept = [];
    var i;
    for (i = 0; i < cards.length; i++) {
      var el = cards[i];
      var h = el.getAttribute('data-wmn-handle') || '';
      if (!h) {
        if (el.parentNode) el.parentNode.removeChild(el);
        continue;
      }
      if (shopifyRecMatchesSeed(h, seedResolved)) {
        kept.push(el);
        existing[h] = true;
      } else if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    }

    var need = targetCount - kept.length;
    var topped = false;
    if (need > 0) {
      var picks = shopifyRecsTopUpCandidates(
        seedHandle,
        seedResolved,
        existing,
        need
      );
      for (i = 0; i < picks.length; i++) {
        var pick = picks[i];
        if (existing[pick.handle]) continue;
        existing[pick.handle] = true;
        var ev = {
          h: pick.handle,
          title: pick.item.title || pick.handle,
          img: pick.item.image || '',
          price: '',
          tags: []
        };
        var card = buildSearchMemoryCard(
          ev,
          shopifyRecsColumnClass(list, kept.length)
        );
        if (!card) continue;
        card.classList.add('wmn-shopify-recs-topup');
        card.removeAttribute('data-wmn-injected');
        list.appendChild(card);
        kept.push(card);
        topped = true;
      }
    }

    var root =
      list.closest && list.closest('.wmn-shopify-recs')
        ? list.closest('.wmn-shopify-recs')
        : null;
    if (!kept.length) {
      if (root) {
        root.setAttribute('hidden', 'hidden');
        root.classList.add('hidden');
      }
      list.setAttribute('data-wmn-recs-gated', 'empty');
      return true;
    }
    if (root) {
      root.removeAttribute('hidden');
      root.classList.remove('hidden');
    }
    var fp = kept
      .map(function (row) {
        return row.getAttribute('data-wmn-handle') || '';
      })
      .join('|');
    list.setAttribute('data-wmn-recs-gated', fp);
    return kept.length < cards.length || topped;
  }

  function gateShopifyRecs(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll
      ? scope.querySelectorAll('.wmn-shopify-recs .product-list')
      : [];
    if (root && root.classList && root.classList.contains('wmn-shopify-recs')) {
      var pl = root.querySelector('.product-list');
      lists = pl ? [pl] : [];
    }
    var any = false;
    var i;
    for (i = 0; i < lists.length; i++) {
      if (gateShopifyRecsList(lists[i])) any = true;
    }
    return any;
  }

  function rerankRecs(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll
      ? scope.querySelectorAll('.wmn-shopify-recs .product-list')
      : [];
    if (root && root.classList && root.classList.contains('wmn-shopify-recs')) {
      var pl = root.querySelector('.product-list');
      lists = pl ? [pl] : [];
    }
    var any = false;
    for (var i = 0; i < lists.length; i++) {
      if (reorderAffinityGrid(lists[i])) any = true;
    }
    // Geo/genre hard-gate after affinity reorder (Shopify related is blind
    // to country:* / region:* tags).
    if (graphReady()) {
      if (gateShopifyRecs(root)) any = true;
    } else {
      try {
        if (
          window.wmnCatalogGraph &&
          typeof window.wmnCatalogGraph.ensureLoaded === 'function'
        ) {
          window.wmnCatalogGraph.ensureLoaded().then(function () {
            gateShopifyRecs(root);
          }).catch(function () {});
        }
      } catch (eGate) {
        /* ignore */
      }
    }
    return any;
  }

  function bindExtraSignals() {
    if (window.__wmnExtraSignals) return;
    window.__wmnExtraSignals = true;

    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || !form.getAttribute) return;
        var action = (form.getAttribute('action') || '').toLowerCase();
        if (action.indexOf('/cart/add') === -1) return;
        var handle =
          (document.querySelector('meta[property="og:url"]') &&
            productHandleFromHref(
              document.querySelector('meta[property="og:url"]').getAttribute('content')
            )) ||
          productHandleFromHref(window.location.pathname);
        var node = document.getElementById('wmn-engagement-product');
        var genres = [];
        var title = '';
        if (node) {
          try {
            var payload = JSON.parse(node.textContent || '{}');
            genres = payload.genres || [];
            title = payload.title || '';
            handle = payload.handle || handle;
          } catch (err) {
            /* ignore */
          }
        }
        if (handle) {
          recordView({ handle: handle, title: title, genres: genres, kind: 'atc' });
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        var t = e.target && e.target.closest ? e.target.closest('a, button') : null;
        if (!t) return;

        if (t.classList && t.classList.contains('wmn-genres__chip')) {
          var label = (t.textContent || '').trim();
          if (label) recordView({ genres: [label], kind: 'chip' });
          return;
        }

        var listen =
          t.closest &&
          (t.closest('[data-wmn-listen]') ||
            t.closest('.wmn-listen') ||
            t.closest('a[href*="spotify.com"]') ||
            t.closest('a[href*="music.apple.com"]') ||
            t.closest('.product_section a[href*="open.spotify"]'));
        if (listen) {
          var node = document.getElementById('wmn-engagement-product');
          if (node) {
            try {
              var payload = JSON.parse(node.textContent || '{}');
              recordView({
                handle: payload.handle,
                title: payload.title,
                genres: payload.genres || [],
                image: payload.image,
                price: payload.price,
                kind: 'listen'
              });
            } catch (err2) {
              /* ignore */
            }
          }
        }
      },
      true
    );
  }

  /**
   * Always-available cloud payload (does not depend on intent-analytics load order).
   * Prefer analytics builder when present so settles / logTail travel with taste.
   */
  function buildTasteCloudProfile(store) {
    store = store || readStore();
    if (
      window.wmnIntentAnalytics &&
      typeof window.wmnIntentAnalytics.buildCloudProfile === 'function'
    ) {
      try {
        return window.wmnIntentAnalytics.buildCloudProfile(store);
      } catch (eBuild) {
        /* fall through to local builder */
      }
    }
    var interest = readInterestProfile();
    var focus = readFocus();
    if (!focus && interest && interest.focus) focus = interest.focus;
    var uEmb = readUEmb();
    if (!uEmb && interest && interest.uEmb) uEmb = interest.uEmb;
    return {
      v: 2,
      events: store.events || [],
      entities: store.entities || {},
      negatives: store.negatives || {},
      et: store.et,
      intent: {
        updatedAt: Date.now(),
        focus: focus || undefined,
        uEmb: uEmb || undefined
      }
    };
  }

  /**
   * Merge a cloud profile object into local durable taste + session.
   * Used by Liquid seed and by prefs-sync GET hydrate.
   * @returns {boolean} true when something was applied
   */
  function applyCloudProfile(cloud) {
    if (!cloud || typeof cloud !== 'object') return false;
    var applied = false;
    try {
      var hasEngagement =
        Array.isArray(cloud.events) ||
        (cloud.entities && typeof cloud.entities === 'object');
      var hasInterest = !!(
        cloud.intent &&
        (cloud.intent.focus || (cloud.intent.uEmb && cloud.intent.uEmb.v))
      );
      if (!hasEngagement && !hasInterest) return false;
      if (hasEngagement) {
        if (!Array.isArray(cloud.events)) cloud.events = [];
        writeStore(mergeStores(readStore(), cloud));
        applied = true;
      }
      if (hasInterest) {
        applyCloudInterest(cloud);
        applied = true;
      }
      if (applied) {
        // Refresh pending from merged local so a subsequent prefs-sync flush
        // does not overwrite cloud with a stale pre-hydrate payload.
        var mergedStore = readStore();
        var pendingProfile = buildTasteCloudProfile(mergedStore);
        try {
          window.localStorage.setItem(
            'wmn_prefs_pending_sync',
            JSON.stringify(pendingProfile)
          );
        } catch (ePending) {
          /* ignore */
        }
        document.dispatchEvent(
          new CustomEvent('wmn:preferences-updated', {
            detail: {
              store: mergedStore,
              profile: pendingProfile,
              source: 'cloud'
            }
          })
        );
      }
    } catch (e) {
      return false;
    }
    return applied;
  }

  function mergeCloudPreferences() {
    var node = document.getElementById('wmn-engagement-cloud');
    if (!node) return;
    try {
      var raw = node.textContent || '';
      if (!raw || !raw.trim() || raw.trim() === 'null') return;
      var cloud = JSON.parse(raw);
      applyCloudProfile(cloud);
    } catch (e) {
      /* ignore */
    }
  }

  function logIntentEvent(partial) {
    try {
      if (
        window.wmnIntentAnalytics &&
        typeof window.wmnIntentAnalytics.log === 'function'
      ) {
        window.wmnIntentAnalytics.log(partial || {});
      }
    } catch (e) {
      /* ignore */
    }
  }

  function roundPi(x) {
    if (typeof x !== 'number' || !isFinite(x)) return 0;
    return Math.round(x * 1e8) / 1e8;
  }

  /**
   * Softmax π over scored eligible items. Temperature keeps mass off one-hot.
   * @param {{ handle: string, score: number }[]} rows
   * @param {number} [temp]
   * @returns {Object.<string, number>}
   */
  function softmaxPiMap(rows, temp) {
    var out = {};
    if (!rows || !rows.length) return out;
    var t = temp != null && temp > 0 ? temp : FY_SOFTMAX_TEMP;
    var maxS = -Infinity;
    var i;
    for (i = 0; i < rows.length; i++) {
      var s = rows[i] && typeof rows[i].score === 'number' ? rows[i].score : 0;
      if (s > maxS) maxS = s;
    }
    var sum = 0;
    var exps = new Array(rows.length);
    for (i = 0; i < rows.length; i++) {
      var sc =
        rows[i] && typeof rows[i].score === 'number' ? rows[i].score : 0;
      var e = Math.exp((sc - maxS) / t);
      if (!isFinite(e)) e = 0;
      exps[i] = e;
      sum += e;
    }
    if (!(sum > 0)) {
      var u = 1 / rows.length;
      for (i = 0; i < rows.length; i++) {
        if (rows[i] && rows[i].handle) out[rows[i].handle] = u;
      }
      return out;
    }
    for (i = 0; i < rows.length; i++) {
      if (rows[i] && rows[i].handle) {
        out[rows[i].handle] = exps[i] / sum;
      }
    }
    return out;
  }

  function slotWeight(rank) {
    var r = typeof rank === 'number' && rank >= 0 ? rank : 0;
    return 1 / (1 + r);
  }

  /**
   * Clipped IPS + SNIPS over logged events.
   * Event shape: { pi|piSlot, reward, handle? }; piNewMap[handle] or piNewFn(ev).
   */
  function estimateIps(events, piNew, opts) {
    opts = opts || {};
    var clip = opts.clip != null ? opts.clip : FY_IPS_CLIP;
    var useSlot = opts.useSlot !== false;
    var list = events || [];
    var n = list.length;
    if (!n) {
      return { clippedIps: 0, snips: 0, n: 0, weightSum: 0 };
    }
    var piNewFn =
      typeof piNew === 'function'
        ? piNew
        : function (ev) {
            if (!ev) return 0;
            var h = ev.handle;
            if (piNew && h != null && piNew[h] != null) return piNew[h];
            return 0;
          };
    var sumWr = 0;
    var sumW = 0;
    var sumClipped = 0;
    var i;
    for (i = 0; i < n; i++) {
      var ev = list[i];
      if (!ev) continue;
      var piLog = useSlot
        ? ev.piSlot != null
          ? ev.piSlot
          : (ev.pi || 0) * slotWeight(ev.rank)
        : ev.pi || 0;
      if (!(piLog > 0)) continue;
      var piN = piNewFn(ev);
      if (!(piN >= 0) || !isFinite(piN)) piN = 0;
      var w = piN / piLog;
      if (w > clip) w = clip;
      if (!(w >= 0) || !isFinite(w)) w = 0;
      var r = ev.reward != null ? ev.reward : ev.r != null ? ev.r : 0;
      sumWr += w * r;
      sumW += w;
      sumClipped += w * r;
    }
    return {
      clippedIps: sumClipped / n,
      snips: sumW > 0 ? sumWr / sumW : 0,
      n: n,
      weightSum: sumW
    };
  }

  function getPendingSync() {
    try {
      var raw = window.localStorage.getItem('wmn_prefs_pending_sync');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function scheduleCloudWriteback() {
    if (!document.querySelector('meta[name="wmn-customer-id"]')) return;
    if (window.__wmnCloudWriteTimer) window.clearTimeout(window.__wmnCloudWriteTimer);
    window.__wmnCloudWriteTimer = window.setTimeout(function () {
      var store = readStore();
      var profile = buildTasteCloudProfile(store);
      try {
        window.localStorage.setItem(
          'wmn_prefs_pending_sync',
          JSON.stringify(profile)
        );
      } catch (e) {
        /* ignore */
      }
      // Customer Account extension / app proxy can pick this up when available.
      document.dispatchEvent(
        new CustomEvent('wmn:preferences-updated', {
          detail: { store: store, profile: profile }
        })
      );
    }, 1500);
  }

  /**
   * Full-catalog For you scorer (Phase 1).
   * Enumerates all graph products — not expand-neighborhood only.
   * Uses entity EU (country/region 1.4) + focus + skip negatives + optional annealed explore.
   * λ_emb ≈ 0.25 as tie-break under focusDominant; fuller when no session focus.
   *
   * @param {{
   *   limit?: number,
   *   excludeHandles?: string[]|Object,
   *   explore?: boolean,
   *   seed?: number,
   *   pinRecent?: boolean
   * }} [opts]
   * @returns {{ picks: object[], labels: string[], seeds: string[], geo: boolean, scored: number }|null}
   */
  /**
   * Staff Picks / strange finds — rarity + anti-popular, biweekly cohort.
   * Sales not in graph yet: proxy via artist/genre/country long-tail +
   * exclude bestsellers handles (opts.excludeHandles).
   *
   * @param {{
   *   limit?: number,
   *   excludeHandles?: string[]|object,
   *   pool?: number,
   *   now?: number
   * }} [opts]
   * @returns {{ picks: object[], bucket: number, scored: number }|null}
   */
  function staffPicksFortnightBucket(nowMs) {
    var days = Math.floor((nowMs != null ? nowMs : Date.now()) / 86400000);
    return Math.floor(days / 14);
  }

  function staffHashSeed(str) {
    var h = 2166136261;
    var s = String(str || '');
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function scoreCatalogStaffPicks(opts) {
    opts = opts || {};
    var lim = opts.limit || 16;
    var poolN = opts.pool || 72;
    ensureGraphWarm();
    if (!graphReady()) return null;

    var data = window.wmnCatalogGraph.getData();
    var products = (data && data.products) || {};
    var artists = (data && data.artists) || {};
    var genres = (data && data.genres) || {};
    var countries = (data && data.countries) || {};

    var exclude = {};
    var ex = opts.excludeHandles;
    if (ex) {
      if (Array.isArray(ex)) {
        for (var xi = 0; xi < ex.length; xi++) {
          if (ex[xi]) exclude[String(ex[xi])] = true;
        }
      } else if (typeof ex === 'object') {
        for (var xk in ex) {
          if (Object.prototype.hasOwnProperty.call(ex, xk) && ex[xk]) {
            exclude[xk] = true;
          }
        }
      }
    }

    var excludeGenres = {};
    var eg = opts.excludeGenres;
    if (eg) {
      if (Array.isArray(eg)) {
        for (var egi = 0; egi < eg.length; egi++) {
          if (eg[egi]) excludeGenres[String(eg[egi])] = true;
        }
      } else if (typeof eg === 'object') {
        for (var egk in eg) {
          if (Object.prototype.hasOwnProperty.call(eg, egk) && eg[egk]) {
            excludeGenres[egk] = true;
          }
        }
      }
    }

    function degreeOf(map, slug) {
      if (!slug || !map[slug]) return 1;
      var arr = map[slug].p;
      return arr && arr.length ? arr.length : 1;
    }

    function rarityFromDegree(deg) {
      return 1 / Math.log(2 + deg);
    }

    function genreSlugsForHandle(handle) {
      var prod = products[handle];
      return (prod && prod.g) || [];
    }

    function genresConflict(gList, used) {
      if (!gList || !gList.length) return false;
      var gi;
      for (gi = 0; gi < gList.length; gi++) {
        if (gList[gi] && used[gList[gi]]) return true;
      }
      return false;
    }

    function claimGenres(gList, used) {
      if (!gList || !gList.length) return;
      var gi;
      for (gi = 0; gi < gList.length; gi++) {
        if (gList[gi]) used[gList[gi]] = true;
      }
    }

    var ranked = [];
    var handles = Object.keys(products);
    var hi;
    for (hi = 0; hi < handles.length; hi++) {
      var handle = handles[hi];
      if (!handle || exclude[handle]) continue;
      var item = graphItemFromHandle(handle);
      if (!item) continue;

      var p = products[handle];
      var aSlug = p.a || '';
      var aDeg = degreeOf(artists, aSlug);
      // "Various" is common — treat as mid, not ultra-rare
      var artistR =
        aSlug === 'various' || aSlug === 'various-artists'
          ? 0.35
          : rarityFromDegree(aDeg);

      var gList = p.g || [];
      if (genresConflict(gList, excludeGenres)) continue;

      var gR = 0;
      var gi;
      if (gList.length) {
        for (gi = 0; gi < gList.length; gi++) {
          gR += rarityFromDegree(degreeOf(genres, gList[gi]));
        }
        gR /= gList.length;
      } else {
        gR = 0.55;
      }

      var cList = p.c || [];
      var cR = 0;
      var ci;
      if (cList.length) {
        for (ci = 0; ci < cList.length; ci++) {
          cR += rarityFromDegree(degreeOf(countries, cList[ci]));
        }
        cR /= cList.length;
      } else {
        cR = 0.45;
      }

      var titleL = String(item.title || '').toLowerCase();
      var handleL = String(handle).toLowerCase();
      if (
        /t-?shirt|hoodie|tote|mug|poster|sticker|gift voucher|gift card/.test(
          titleL
        ) ||
        /t-?shirt|hoodie|tote-bag/.test(handleL)
      ) {
        continue;
      }
      var massPenalty = 0;
      if (/best of|greatest hits|collection\b/.test(titleL)) massPenalty += 0.12;
      // Core Rough Guide flagships are familiar merch — soft demote, not ban
      if (/^the rough guide to /.test(titleL) && aDeg > 8) massPenalty += 0.08;

      var hasSp = !!(p.sp && String(p.sp));
      var listenBoost = hasSp ? 0.06 : 0;

      // Low "popularity" proxy: sparse artist + rare genre/country
      var weird =
        0.38 * artistR + 0.32 * gR + 0.18 * cR + listenBoost - massPenalty;

      ranked.push({
        handle: handle,
        item: item,
        score: weird,
        hasSp: hasSp,
        genres: gList.slice()
      });
    }

    ranked.sort(function (a, b) {
      return b.score - a.score;
    });

    // Wide pool — genre uniqueness burns through candidates faster.
    var pool = ranked.slice(0, Math.max(poolN, lim * 30));
    var bucket = staffPicksFortnightBucket(opts.now);
    var rng = makeRng(staffHashSeed('wmn-staff-picks-' + bucket));

    // Seeded Fisher–Yates on the weird pool so the fortnight cohort rotates
    var i;
    for (i = pool.length - 1; i > 0; i--) {
      var j = Math.floor(rng() * (i + 1));
      var tmp = pool[i];
      pool[i] = pool[j];
      pool[j] = tmp;
    }

    // Prefer prior-window holdout when available
    var holdKey = 'wmn_staff_picks_prev_' + (bucket - 1);
    var held = {};
    try {
      var rawHold = window.sessionStorage.getItem(holdKey);
      if (rawHold) {
        var arr = JSON.parse(rawHold);
        if (Array.isArray(arr)) {
          for (i = 0; i < arr.length; i++) held[arr[i]] = true;
        }
      }
    } catch (eHold) {
      /* ignore */
    }

    pool.sort(function (a, b) {
      var ha = held[a.handle] ? 1 : 0;
      var hb = held[b.handle] ? 1 : 0;
      if (ha !== hb) return ha - hb;
      return 0;
    });

    var picks = [];
    var seen = {};
    var usedGenres = {};
    for (var ug in excludeGenres) {
      if (Object.prototype.hasOwnProperty.call(excludeGenres, ug) && excludeGenres[ug]) {
        usedGenres[ug] = true;
      }
    }
    for (i = 0; i < pool.length && picks.length < lim; i++) {
      var row = pool[i];
      if (!row || !row.handle || seen[row.handle]) continue;
      var rowGenres = row.genres || genreSlugsForHandle(row.handle);
      // One product per genre across the mosaic — skip any shared slug.
      if (genresConflict(rowGenres, usedGenres)) continue;
      seen[row.handle] = true;
      claimGenres(rowGenres, usedGenres);
      var out = row.item;
      out.score = row.score;
      out.branch = 'staff-picks';
      picks.push(out);
    }

    try {
      window.sessionStorage.setItem(
        'wmn_staff_picks_prev_' + bucket,
        JSON.stringify(
          picks.map(function (p) {
            return p.handle;
          })
        )
      );
    } catch (eSave) {
      /* ignore */
    }

    return {
      picks: picks,
      bucket: bucket,
      scored: ranked.length,
      usedGenres: usedGenres
    };
  }

  /**
   * True when the visitor has enough engagement signal to personalize
   * one Staff Picks slot (events, entity prefs, or session/durable focus).
   */
  function hasStaffPicksAffinitySignal() {
    var store = readStore();
    if (store.events && store.events.length) return true;
    if (store.entities && Object.keys(store.entities).length) return true;
    var focus = readFocusOrDurable();
    if (!focus) return false;
    if (focus.primaryGenre || focus.primaryCountry || focus.primaryRegion) {
      return true;
    }
    if (focus.primaryKind) return true;
    if (focus.genres && focus.genres.length) return true;
    if (focus.countries && focus.countries.length) return true;
    if (focus.artists && focus.artists.length) return true;
    if (focus.handles && focus.handles.length) return true;
    return false;
  }

  /**
   * Inject exactly one analytics-driven product into a Staff Picks cohort.
   * Slot 0 is the designated "for you" card. Cold visitors keep the
   * editorial/biweekly list unchanged.
   * Must stay unique vs Dig deeper — never share that affinity handle.
   *
   * @param {{ picks: object[], bucket?: number, scored?: number }|null} built
   * @param {{ excludeHandles?: string[]|object, slot?: number }=} opts
   * @returns {{
   *   picks: object[],
   *   bucket?: number,
   *   scored?: number,
   *   affinitySlot: number|null,
   *   affinityHandle: string|null
   * }|null}
   */
  var lastStaffPicksAffinityHandle = '';

  function setStaffPicksAffinityHandle(handle) {
    lastStaffPicksAffinityHandle = handle ? String(handle) : '';
  }

  /** Last Staff Picks "For you" handle (module + DOM fallback). */
  function getStaffPicksAffinityHandle() {
    if (lastStaffPicksAffinityHandle) return lastStaffPicksAffinityHandle;
    try {
      var root = document.getElementById('wmn-staff-picks');
      if (root) {
        var attr = root.getAttribute('data-wmn-staff-affinity');
        if (attr) return attr;
      }
      var el = document.querySelector(
        '#wmn-staff-picks [data-wmn-staff-for-you="1"][data-wmn-handle], [data-wmn-staff-for-you="1"][data-wmn-handle]'
      );
      if (el) return el.getAttribute('data-wmn-handle') || '';
    } catch (eDom) {
      /* ignore */
    }
    return '';
  }

  /** Dig deeper pool + painted cards — Staff affinity must not collide. */
  function digDeeperOccupiedHandles() {
    var out = {};
    try {
      var root =
        document.getElementById('wmn-because-you-looked') ||
        document.querySelector('[data-wmn-because]');
      var state = root && root._wmnBecauseState;
      if (state && state.pool && state.pool.length) {
        var pi;
        for (pi = 0; pi < state.pool.length; pi++) {
          if (state.pool[pi] && state.pool[pi].handle) {
            out[state.pool[pi].handle] = true;
          }
        }
      }
      var nodes = document.querySelectorAll(
        '#wmn-because-grid [data-wmn-handle], [data-wmn-because-grid] [data-wmn-handle]'
      );
      var ni;
      for (ni = 0; ni < nodes.length; ni++) {
        var h = nodes[ni].getAttribute('data-wmn-handle');
        if (h) out[h] = true;
      }
    } catch (eDig) {
      /* ignore */
    }
    return out;
  }

  function blendStaffPicksAffinity(built, opts) {
    opts = opts || {};
    if (!built || !built.picks || !built.picks.length) return built;

    var out = {
      picks: built.picks.slice(),
      bucket: built.bucket,
      scored: built.scored,
      affinitySlot: null,
      affinityHandle: null
    };

    if (!hasStaffPicksAffinitySignal() || !graphReady()) {
      setStaffPicksAffinityHandle('');
      return out;
    }

    var SLOT = opts.slot != null ? opts.slot : 0;
    if (SLOT < 0) SLOT = 0;
    if (SLOT >= out.picks.length) SLOT = 0;

    var exclude = {};
    var ex = opts.excludeHandles;
    if (ex) {
      if (Array.isArray(ex)) {
        for (var xi = 0; xi < ex.length; xi++) {
          if (ex[xi]) exclude[String(ex[xi])] = true;
        }
      } else if (typeof ex === 'object') {
        for (var xk in ex) {
          if (Object.prototype.hasOwnProperty.call(ex, xk) && ex[xk]) {
            exclude[xk] = true;
          }
        }
      }
    }

    // Prefer discovery over re-showing the last viewed album in Staff Picks.
    var store = readStore();
    var recent = store.events || [];
    var ri;
    for (ri = Math.max(0, recent.length - 8); ri < recent.length; ri++) {
      if (recent[ri] && recent[ri].h) exclude[recent[ri].h] = true;
    }

    // Never pick a Dig deeper product for the Staff Picks affinity slot.
    var digOcc = digDeeperOccupiedHandles();
    var dk;
    for (dk in digOcc) {
      if (Object.prototype.hasOwnProperty.call(digOcc, dk) && digOcc[dk]) {
        exclude[dk] = true;
      }
    }

    var fy = scoreCatalogForYou({
      limit: 36,
      excludeHandles: exclude,
      pinRecent: false,
      explore: false
    });
    if (!fy || !fy.picks || !fy.picks.length) {
      setStaffPicksAffinityHandle('');
      return out;
    }

    var indexByHandle = {};
    var pi;
    for (pi = 0; pi < out.picks.length; pi++) {
      if (out.picks[pi] && out.picks[pi].handle) {
        indexByHandle[out.picks[pi].handle] = pi;
      }
    }

    var chosen = null;
    var chosenIdx = -1;
    var ci;
    for (ci = 0; ci < fy.picks.length; ci++) {
      var cand = fy.picks[ci];
      if (!cand || !cand.handle) continue;
      if (exclude[cand.handle]) continue;
      // Skip merchandise / non-album noise (same filters as staff scorer).
      var titleL = String(cand.title || '').toLowerCase();
      var handleL = String(cand.handle).toLowerCase();
      if (
        /t-?shirt|hoodie|tote|mug|poster|sticker|gift voucher|gift card/.test(
          titleL
        ) ||
        /t-?shirt|hoodie|tote-bag/.test(handleL)
      ) {
        continue;
      }
      if (!cand.image) {
        var imgTry = graphItemFromHandle(cand.handle);
        if (imgTry && imgTry.image) cand = imgTry;
      }
      if (!cand.image) continue;

      if (Object.prototype.hasOwnProperty.call(indexByHandle, cand.handle)) {
        chosen = out.picks[indexByHandle[cand.handle]] || cand;
        chosenIdx = indexByHandle[cand.handle];
      } else {
        chosen = cand;
        chosenIdx = -1;
      }
      break;
    }

    if (!chosen || !chosen.handle) {
      setStaffPicksAffinityHandle('');
      return out;
    }

    var marked = chosen;
    marked.branch = 'staff-picks-for-you';
    marked.staffAffinity = true;

    var next = out.picks.slice();
    if (chosenIdx === SLOT) {
      next[SLOT] = marked;
    } else if (chosenIdx >= 0) {
      next.splice(chosenIdx, 1);
      next.splice(SLOT, 0, marked);
    } else {
      next[SLOT] = marked;
    }

    function staffGenreSlugs(item) {
      if (!item || !item.handle || !graphReady()) return [];
      try {
        var gdata = window.wmnCatalogGraph.getData();
        var gp = gdata && gdata.products && gdata.products[item.handle];
        return (gp && gp.g) || [];
      } catch (eG) {
        return [];
      }
    }

    function staffGenresConflict(gList, used) {
      if (!gList || !gList.length) return false;
      var gi;
      for (gi = 0; gi < gList.length; gi++) {
        if (gList[gi] && used[gList[gi]]) return true;
      }
      return false;
    }

    function staffClaimGenres(gList, used) {
      if (!gList || !gList.length) return;
      var gi;
      for (gi = 0; gi < gList.length; gi++) {
        if (gList[gi]) used[gList[gi]] = true;
      }
    }

    // Guarantee handle uniqueness + one product per genre (affinity wins its genres).
    var targetLen = built.picks.length;
    var seen = {};
    var usedGenres = {};
    var deduped = [];
    var affinityGenres = staffGenreSlugs(marked);
    staffClaimGenres(affinityGenres, usedGenres);

    // Pin affinity first so later editorial picks cannot share its genres.
    seen[marked.handle] = true;
    deduped.push(marked);

    for (pi = 0; pi < next.length; pi++) {
      var row = next[pi];
      if (!row || !row.handle || seen[row.handle]) continue;
      var rowGenres = staffGenreSlugs(row);
      if (staffGenresConflict(rowGenres, usedGenres)) continue;
      seen[row.handle] = true;
      staffClaimGenres(rowGenres, usedGenres);
      if (row.staffAffinity) {
        delete row.staffAffinity;
        if (row.branch === 'staff-picks-for-you') row.branch = 'staff-picks';
      }
      deduped.push(row);
    }

    // Top up from original cohort if replace/dedupe shortened the rail.
    for (pi = 0; pi < built.picks.length && deduped.length < targetLen; pi++) {
      var fill = built.picks[pi];
      if (!fill || !fill.handle || seen[fill.handle]) continue;
      var fillGenres = staffGenreSlugs(fill);
      if (staffGenresConflict(fillGenres, usedGenres)) continue;
      seen[fill.handle] = true;
      staffClaimGenres(fillGenres, usedGenres);
      deduped.push(fill);
    }

    // Still short after affinity genre collisions — pull more diverse editorial picks.
    if (deduped.length < targetLen) {
      var topUpExclude = {};
      var ek;
      for (ek in exclude) {
        if (Object.prototype.hasOwnProperty.call(exclude, ek) && exclude[ek]) {
          topUpExclude[ek] = true;
        }
      }
      for (ek in seen) {
        if (Object.prototype.hasOwnProperty.call(seen, ek) && seen[ek]) {
          topUpExclude[ek] = true;
        }
      }
      var more = scoreCatalogStaffPicks({
        limit: targetLen - deduped.length + 4,
        excludeHandles: topUpExclude,
        excludeGenres: usedGenres,
        pool: 160
      });
      if (more && more.picks && more.picks.length) {
        for (pi = 0; pi < more.picks.length && deduped.length < targetLen; pi++) {
          var extra = more.picks[pi];
          if (!extra || !extra.handle || seen[extra.handle]) continue;
          var extraGenres = staffGenreSlugs(extra);
          if (staffGenresConflict(extraGenres, usedGenres)) continue;
          seen[extra.handle] = true;
          staffClaimGenres(extraGenres, usedGenres);
          extra.staffAffinity = false;
          extra.branch = 'staff-picks';
          deduped.push(extra);
        }
      }
    }

    // Affinity must stay in SLOT (usually 0).
    if (deduped.length && deduped[0] && deduped[0].handle === marked.handle) {
      /* already first */
    } else {
      var affAt = -1;
      for (pi = 0; pi < deduped.length; pi++) {
        if (deduped[pi] && deduped[pi].handle === marked.handle) {
          affAt = pi;
          break;
        }
      }
      if (affAt > 0) {
        var moved = deduped.splice(affAt, 1)[0];
        deduped.splice(SLOT, 0, moved);
      } else if (affAt < 0) {
        deduped.splice(SLOT, 0, marked);
      }
    }
    // Re-mark affinity after any reorder.
    for (pi = 0; pi < deduped.length; pi++) {
      if (!deduped[pi]) continue;
      if (deduped[pi].handle === marked.handle) {
        deduped[pi].branch = 'staff-picks-for-you';
        deduped[pi].staffAffinity = true;
      } else if (deduped[pi].staffAffinity) {
        delete deduped[pi].staffAffinity;
        if (deduped[pi].branch === 'staff-picks-for-you') {
          deduped[pi].branch = 'staff-picks';
        }
      }
    }

    out.picks = deduped.slice(0, targetLen);
    out.affinitySlot = SLOT;
    out.affinityHandle = marked.handle;
    setStaffPicksAffinityHandle(marked.handle);
    return out;
  }

  function scoreCatalogForYou(opts) {
    opts = opts || {};
    var lim = opts.limit || 18;
    var explore = !!opts.explore;
    var pinRecent = opts.pinRecent !== false;
    // Store collection pagination needs the full ranked catalog (prefer first,
    // then score-ordered remainder). Homepage rails keep prefer-only.
    var fillToLimit = !!opts.fillToLimit;
    ensureGraphWarm();
    var store = readStore();
    var prefs = store.entities || {};
    var negatives = store.negatives || {};
    // Cold sessions (no events/prefs) still rank the full catalog so the
    // homepage rail never vanishes — soft bestsellers-style path below.
    if (!graphReady()) return null;

    var exclude = {};
    var ex = opts.excludeHandles;
    if (ex) {
      if (Array.isArray(ex)) {
        for (var xi = 0; xi < ex.length; xi++) {
          if (ex[xi]) exclude[ex[xi]] = true;
        }
      } else if (typeof ex === 'object') {
        for (var xk in ex) {
          if (Object.prototype.hasOwnProperty.call(ex, xk) && ex[xk]) {
            exclude[xk] = true;
          }
        }
      }
    }

    var topCountries = topEntityIds(prefs, 4, ['country']);
    var topRegions = topEntityIds(prefs, 4, ['region']);
    var topArtistGenre = topEntityIds(prefs, 6, ['artist', 'genre']);
    var topProducts = topEntityIds(prefs, 4, ['product']);
    var labelSeeds = topCountries.slice();
    var si;
    for (si = 0; si < topRegions.length; si++) {
      if (labelSeeds.indexOf(topRegions[si]) === -1) labelSeeds.push(topRegions[si]);
    }
    for (si = 0; si < topArtistGenre.length; si++) {
      if (labelSeeds.indexOf(topArtistGenre[si]) === -1) {
        labelSeeds.push(topArtistGenre[si]);
      }
    }
    for (si = 0; si < topProducts.length; si++) {
      if (labelSeeds.indexOf(topProducts[si]) === -1) labelSeeds.push(topProducts[si]);
    }
    // Cold / pre-focus: empty seeds are fine — candidates still rank via score.

    var focus = opts.focus != null ? opts.focus : readFocus();
    var uEmbState = readUEmb();
    var scoreCtx = {
      prefs: prefs,
      focus: focus,
      negatives: negatives,
      uEmb: uEmbState && uEmbState.v ? uEmbState.v : null
    };
    // scoreCtx.dampGeoPrefs set below once focusDominant is known
    var prefsNorm = prefsL1(prefs);
    var rng = makeRng(
      opts.seed != null
        ? opts.seed
        : (store.et || 1) ^ ((store.events && store.events.length) || 0)
    );

    var geoKeys = {};
    var gi;

    // Session focus is the SOLE ranking driver when primaryKind is set
    // (genre / country / region). Stale Mali/India prefs must not fill the rail
    // while the subtitle says Latin America / Jazz / Blues.
    var focusPrimaryGenre =
      (focus &&
        (focus.primaryGenre || (focus.genres && focus.genres[0]))) ||
      '';
    var focusPrimaryCountry = (focus && focus.primaryCountry) || '';
    var focusPrimaryRegion = (focus && focus.primaryRegion) || '';
    var focusPrimaryKind = (focus && focus.primaryKind) || '';
    if (!focusPrimaryKind && focus) {
      if (focusPrimaryCountry) focusPrimaryKind = 'country';
      else if (focusPrimaryRegion) focusPrimaryKind = 'region';
      else if (focusPrimaryGenre) focusPrimaryKind = 'genre';
      else if (
        focus.primaryArtist ||
        (focus.artists && focus.artists[0])
      ) {
        focusPrimaryKind = 'artist';
      }
    }

    // Align with subtitle: genre label wins unless country/region owns kind.
    // Artist kind + primaryGenre (e.g. blues) must still hard-filter the rail.
    if (
      focus &&
      focusPrimaryGenre &&
      focusPrimaryKind !== 'country' &&
      focusPrimaryKind !== 'region' &&
      focusPrimaryKind !== 'genre'
    ) {
      focusPrimaryKind = 'genre';
    }

    var focusDominant =
      !!focus &&
      ((focusPrimaryKind === 'genre' && !!focusPrimaryGenre) ||
        (focusPrimaryKind === 'country' && !!focusPrimaryCountry) ||
        (focusPrimaryKind === 'region' && !!focusPrimaryRegion));

    // Legacy prefs-geo only when there is NO session focus primary.
    var geoDominant =
      !focusDominant && !!(topCountries.length || topRegions.length);

    // Dampen stale country/region entity mass whenever session focus owns ranking.
    scoreCtx.dampGeoPrefs = focusDominant;

    if (focusDominant && focusPrimaryKind === 'country') {
      geoKeys['country:' + focusPrimaryCountry] = true;
    } else if (focusDominant && focusPrimaryKind === 'region') {
      geoKeys['region:' + focusPrimaryRegion] = true;
    } else if (geoDominant) {
      for (gi = 0; gi < topCountries.length; gi++) {
        geoKeys[topCountries[gi]] = true;
      }
      for (gi = 0; gi < topRegions.length; gi++) {
        geoKeys[topRegions[gi]] = true;
      }
    }

    // Genre hard-filter keys: ONLY exact focusPrimaryGenre.
    // Do NOT expand relatedGenres into the prefer gate / handleMatchesFocus —
    // jazz→gospel-spiritual sibling was admitting Spiritual India. Related
    // genres may still boost soft ranking via FOCUS_GENRE_BONUS on focus.genres;
    // country focus keeps its own bridge/related path (focusCountryGenreKeys).
    var FOCUS_GENRE_KEYS_CAP = 12;
    var focusGenreKeys = [];
    function addFocusGenreKey(slug) {
      if (!slug || focusGenreKeys.indexOf(slug) !== -1) return false;
      if (focusGenreKeys.length >= FOCUS_GENRE_KEYS_CAP) return false;
      focusGenreKeys.push(slug);
      return true;
    }
    if (focusDominant && focusPrimaryKind === 'genre') {
      if (focusPrimaryGenre) addFocusGenreKey(focusPrimaryGenre);
    }

    // Country focus: expand prefer pool via country-named genres + bridges.
    // Catalog often tags only a few products with country:* (India → 3) while
    // dozens sit on indian-classical-raga / bollywood-filmi.
    var focusCountryGenreKeys = [];
    function addFocusCountryGenreKey(slug) {
      if (!slug || focusCountryGenreKeys.indexOf(slug) !== -1) return false;
      if (focusCountryGenreKeys.length >= FOCUS_GENRE_KEYS_CAP) return false;
      focusCountryGenreKeys.push(slug);
      return true;
    }
    function genreMatchesCountryTokens(slug, name, tokens) {
      var s = String(slug || '').toLowerCase();
      var n = String(name || '').toLowerCase();
      var ti;
      for (ti = 0; ti < tokens.length; ti++) {
        var t = tokens[ti];
        if (!t) continue;
        if (
          s === t ||
          s.indexOf(t + '-') === 0 ||
          s.indexOf('-' + t + '-') !== -1 ||
          s.slice(-(t.length + 1)) === '-' + t
        ) {
          return true;
        }
        var tre = new RegExp(
          '\\b' + String(t).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b',
          'i'
        );
        if (tre.test(n) || tre.test(s.replace(/-/g, ' '))) return true;
      }
      return false;
    }
    function buildCountryFocusGenreKeys(countrySlug) {
      if (!countrySlug || !graphReady()) return;
      var gdata = window.wmnCatalogGraph.getData();
      var genresMap = (gdata && gdata.genres) || {};
      var tokens = [countrySlug].concat(COUNTRY_ADJ_TOKENS[countrySlug] || []);
      var slug;
      for (slug in genresMap) {
        if (!Object.prototype.hasOwnProperty.call(genresMap, slug)) continue;
        var node = genresMap[slug];
        if (genreMatchesCountryTokens(slug, (node && node.n) || '', tokens)) {
          addFocusCountryGenreKey(slug);
        }
      }
      var bridges = COUNTRY_GENRE_BRIDGES[countrySlug] || [];
      for (gi = 0; gi < bridges.length; gi++) {
        if (genresMap[bridges[gi]]) addFocusCountryGenreKey(bridges[gi]);
      }
      // Majority genres on country-tagged products (skip one-off noise like
      // gospel-spiritual on Spiritual India). Also skip broad styles (folk/
      // jazz/blues) that would admit half the catalog under Mali/Cuba.
      var cNode =
        gdata.countries && gdata.countries[countrySlug]
          ? gdata.countries[countrySlug]
          : null;
      var cProducts = (cNode && cNode.p) || [];
      if (cProducts.length) {
        var freq = {};
        var pi;
        for (pi = 0; pi < cProducts.length; pi++) {
          var cp = gdata.products[cProducts[pi]];
          var cgs = (cp && cp.g) || [];
          var cgi;
          for (cgi = 0; cgi < cgs.length; cgi++) {
            if (cgs[cgi]) freq[cgs[cgi]] = (freq[cgs[cgi]] || 0) + 1;
          }
        }
        var need = Math.max(2, Math.ceil(cProducts.length * 0.5));
        for (slug in freq) {
          if (!Object.prototype.hasOwnProperty.call(freq, slug)) continue;
          if (GENERIC_COUNTRY_FOCUS_GENRES[slug]) continue;
          if (freq[slug] >= need) addFocusCountryGenreKey(slug);
        }
      }
      // One related hop, but only keep siblings that still look on-country
      // (name tokens) or already appear on a country-tagged product — avoids
      // cuba→afro-cuban-son→brazilian dumping all of Latin into India-style rails.
      if (
        typeof window.wmnCatalogGraph.relatedGenres === 'function' &&
        focusCountryGenreKeys.length
      ) {
        var seeds = focusCountryGenreKeys.slice();
        var countryProductGenre = {};
        for (pi = 0; pi < cProducts.length; pi++) {
          var cp2 = gdata.products[cProducts[pi]];
          var cgs2 = (cp2 && cp2.g) || [];
          for (cgi = 0; cgi < cgs2.length; cgi++) {
            if (cgs2[cgi]) countryProductGenre[cgs2[cgi]] = true;
          }
        }
        for (gi = 0; gi < seeds.length; gi++) {
          if (focusCountryGenreKeys.length >= FOCUS_GENRE_KEYS_CAP) break;
          var related = window.wmnCatalogGraph.relatedGenres(seeds[gi]);
          var ri;
          for (ri = 0; ri < (related || []).length; ri++) {
            var row = related[ri];
            var relSlug = row && (row.slug || row);
            if (typeof relSlug !== 'string') continue;
            var relNode = genresMap[relSlug];
            // Never hop into generic styles (folk→country, jazz→…), even if
            // a country-tagged product carries that tag.
            if (GENERIC_COUNTRY_FOCUS_GENRES[relSlug]) continue;
            if (
              genreMatchesCountryTokens(
                relSlug,
                (relNode && relNode.n) || '',
                tokens
              ) ||
              countryProductGenre[relSlug] ||
              (COUNTRY_GENRE_BRIDGES[countrySlug] || []).indexOf(relSlug) !== -1
            ) {
              addFocusCountryGenreKey(relSlug);
            }
          }
        }
      }
    }
    if (focusDominant && focusPrimaryKind === 'country' && focusPrimaryCountry) {
      buildCountryFocusGenreKeys(focusPrimaryCountry);
    }

    var focusRegionGenreKeys = [];
    function addFocusRegionGenreKey(slug) {
      if (!slug || focusRegionGenreKeys.indexOf(slug) !== -1) return false;
      if (focusRegionGenreKeys.length >= FOCUS_GENRE_KEYS_CAP) return false;
      focusRegionGenreKeys.push(slug);
      return true;
    }
    function buildRegionFocusGenreKeys(regionSlug) {
      if (!regionSlug || !graphReady()) return;
      var gdata = window.wmnCatalogGraph.getData();
      var genresMap = (gdata && gdata.genres) || {};
      var bridges = REGION_GENRE_BRIDGES[regionSlug] || [];
      var bi;
      for (bi = 0; bi < bridges.length; bi++) {
        if (genresMap[bridges[bi]]) addFocusRegionGenreKey(bridges[bi]);
      }
      var rNode =
        gdata.regions && gdata.regions[regionSlug]
          ? gdata.regions[regionSlug]
          : null;
      var rProducts = (rNode && rNode.p) || [];
      if (rProducts.length) {
        var freq = {};
        var pi;
        for (pi = 0; pi < rProducts.length; pi++) {
          var rp = gdata.products[rProducts[pi]];
          var rgs = (rp && rp.g) || [];
          var rgi;
          for (rgi = 0; rgi < rgs.length; rgi++) {
            if (rgs[rgi]) freq[rgs[rgi]] = (freq[rgs[rgi]] || 0) + 1;
          }
        }
        var need = Math.max(2, Math.ceil(rProducts.length * 0.5));
        var slug;
        for (slug in freq) {
          if (!Object.prototype.hasOwnProperty.call(freq, slug)) continue;
          if (GENERIC_COUNTRY_FOCUS_GENRES[slug]) continue;
          if (freq[slug] >= need) addFocusRegionGenreKey(slug);
        }
      }
      // Name-token genres: "balkan", "caribbean", "andes" — not bare "africa".
      var tokens = [];
      if (regionSlug.indexOf('-') !== -1) {
        tokens.push(regionSlug);
        var parts = regionSlug.split('-');
        var ti;
        for (ti = 0; ti < parts.length; ti++) {
          if (parts[ti] && parts[ti].length > 4 && parts[ti] !== 'africa') {
            tokens.push(parts[ti]);
          }
        }
      } else if (regionSlug.length > 4 && regionSlug !== 'africa') {
        tokens.push(regionSlug);
      }
      if (tokens.length) {
        var gslug;
        for (gslug in genresMap) {
          if (!Object.prototype.hasOwnProperty.call(genresMap, gslug)) continue;
          var gnode = genresMap[gslug];
          if (
            genreMatchesCountryTokens(gslug, (gnode && gnode.n) || '', tokens)
          ) {
            addFocusRegionGenreKey(gslug);
          }
        }
      }
    }
    if (focusDominant && focusPrimaryKind === 'region' && focusPrimaryRegion) {
      buildRegionFocusGenreKeys(focusPrimaryRegion);
    }

    function handleMatchesFocus(handle) {
      if (!focusDominant || !handle || !graphReady()) return false;
      var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
      var p = window.wmnCatalogGraph.getData().products[handle];
      var title = String((p && p.t) || handle).toLowerCase();
      var hLow = String(handle).toLowerCase();

      if (focusPrimaryKind === 'genre') {
        return genreFocusMatchStrength(handle, resolved, title, hLow) ===
          'strong';
      }
      if (focusPrimaryKind === 'country') {
        return countryFocusMatchStrength(handle, resolved, title, hLow) ===
          'strong';
      }
      if (focusPrimaryKind === 'region') {
        return regionFocusMatchStrength(handle, resolved, title, hLow) ===
          'strong';
      }
      return false;
    }

    /** Core genres for purity: drop broad style tags unless they are focus keys. */
    function focusCoreGenres(genres, keepKeys) {
      var out = [];
      var keep = keepKeys || [];
      var gi;
      for (gi = 0; gi < (genres || []).length; gi++) {
        var g = genres[gi];
        if (!g) continue;
        if (GENERIC_COUNTRY_FOCUS_GENRES[g] && keep.indexOf(g) === -1) continue;
        out.push(g);
      }
      return out;
    }

    function genreInTitleOrHandle(g, title, hLow) {
      if (!g || typeof g !== 'string') return false;
      if (hLow.indexOf(g) !== -1) return true;
      var gWords = String(g).replace(/-/g, ' ').trim();
      if (!gWords) return false;
      if (gWords.indexOf(' ') === -1) {
        var re = new RegExp(
          '\\b' + gWords.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b',
          'i'
        );
        return re.test(title) || re.test(hLow);
      }
      return title.indexOf(gWords) !== -1;
    }

    function genreFocusTitleHit(title, hLow) {
      var ci;
      for (ci = 0; ci < focusGenreKeys.length; ci++) {
        if (genreInTitleOrHandle(focusGenreKeys[ci], title, hLow)) return true;
      }
      return false;
    }

    /**
     * Genre prefer vs soft-fill.
     * strong = title/handle genre OR all core tags are the focus genre(s)
     * weak = carries focus genre among other substantive styles
     */
    function genreFocusMatchStrength(handle, resolved, title, hLow) {
      if (!focusPrimaryGenre || !focusGenreKeys.length) return '';
      resolved = resolved || window.wmnCatalogGraph.resolveProduct(handle) || {};
      if (title == null || hLow == null) {
        var p0 = window.wmnCatalogGraph.getData().products[handle];
        title = String((p0 && p0.t) || handle).toLowerCase();
        hLow = String(handle).toLowerCase();
      }
      if (genreFocusTitleHit(title, hLow)) return 'strong';
      var genres = resolved.genres || [];
      var core = focusCoreGenres(genres, focusGenreKeys);
      var list = core.length ? core : genres;
      var hits = 0;
      var off = 0;
      var ci;
      for (ci = 0; ci < list.length; ci++) {
        if (focusGenreKeys.indexOf(list[ci]) !== -1) hits++;
        else off++;
      }
      if (hits > 0 && off === 0) return 'strong';
      if (hits > 0) return 'weak';
      return '';
    }

    function genreFocusPurityBonus(handle) {
      if (
        !focusDominant ||
        focusPrimaryKind !== 'genre' ||
        !focusGenreKeys.length ||
        !handle ||
        !graphReady()
      ) {
        return 0;
      }
      var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
      var p = window.wmnCatalogGraph.getData().products[handle];
      var title = String((p && p.t) || handle).toLowerCase();
      var hLow = String(handle).toLowerCase();
      var bonus = 0;
      if (genreFocusTitleHit(title, hLow)) bonus += FOCUS_PURITY_TITLE;
      var genres = resolved.genres || [];
      var core = focusCoreGenres(genres, focusGenreKeys);
      var list = core.length ? core : genres;
      var hits = 0;
      var ci;
      for (ci = 0; ci < list.length; ci++) {
        if (focusGenreKeys.indexOf(list[ci]) !== -1) hits++;
      }
      var denom = Math.max(1, list.length);
      bonus += (hits / denom) * FOCUS_PURITY_FRAC_SCALE;
      if (hits > 0 && hits === list.length && list.length === 1) {
        bonus += FOCUS_PURITY_SOLE_EXTRA;
      }
      return bonus;
    }

    /**
     * Country focus prefer vs soft-fill.
     * strong = country tag | title/adj | all core genres are on-country bridges
     * weak = merely shares a bridge genre among multi-region tags
     */
    function countryFocusTitleHit(title, hLow) {
      var cWords = String(focusPrimaryCountry).replace(/-/g, ' ');
      if (title.indexOf(cWords) !== -1 || hLow.indexOf(focusPrimaryCountry) !== -1) {
        return true;
      }
      var adjList = COUNTRY_ADJ_TOKENS[focusPrimaryCountry] || [];
      var ci;
      for (ci = 0; ci < adjList.length; ci++) {
        var adj = adjList[ci];
        if (!adj) continue;
        var adjRe = new RegExp(
          '\\b' + String(adj).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b',
          'i'
        );
        if (adjRe.test(title) || adjRe.test(hLow)) return true;
      }
      var inferredC = [];
      inferGeoFromText(title, function (id) {
        inferredC.push(id);
      });
      return inferredC.indexOf('country:' + focusPrimaryCountry) !== -1;
    }

    function countryFocusMatchStrength(handle, resolved, title, hLow) {
      if (!focusPrimaryCountry) return '';
      resolved = resolved || window.wmnCatalogGraph.resolveProduct(handle) || {};
      if (title == null || hLow == null) {
        var p0 = window.wmnCatalogGraph.getData().products[handle];
        title = String((p0 && p0.t) || handle).toLowerCase();
        hLow = String(handle).toLowerCase();
      }
      var ci;
      for (ci = 0; ci < (resolved.countries || []).length; ci++) {
        if (resolved.countries[ci] === focusPrimaryCountry) return 'strong';
      }
      if (countryFocusTitleHit(title, hLow)) return 'strong';

      var genres = resolved.genres || [];
      var core = focusCoreGenres(genres, focusCountryGenreKeys);
      var list = core.length ? core : genres;
      var hits = 0;
      var off = 0;
      for (ci = 0; ci < list.length; ci++) {
        if (focusCountryGenreKeys.indexOf(list[ci]) !== -1) hits++;
        else off++;
      }
      if (hits > 0 && off === 0) return 'strong';
      if (hits > 0) return 'weak';
      return '';
    }

    function countryFocusPurityBonus(handle) {
      if (
        !focusDominant ||
        focusPrimaryKind !== 'country' ||
        !focusPrimaryCountry ||
        !handle ||
        !graphReady()
      ) {
        return 0;
      }
      var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
      var p = window.wmnCatalogGraph.getData().products[handle];
      var title = String((p && p.t) || handle).toLowerCase();
      var hLow = String(handle).toLowerCase();
      var bonus = 0;
      var ci;
      for (ci = 0; ci < (resolved.countries || []).length; ci++) {
        if (resolved.countries[ci] === focusPrimaryCountry) {
          bonus += FOCUS_PURITY_COUNTRY_TAG;
          break;
        }
      }
      if (countryFocusTitleHit(title, hLow)) bonus += FOCUS_PURITY_TITLE;
      var genres = resolved.genres || [];
      var core = focusCoreGenres(genres, focusCountryGenreKeys);
      var list = core.length ? core : genres;
      var hits = 0;
      for (ci = 0; ci < list.length; ci++) {
        if (focusCountryGenreKeys.indexOf(list[ci]) !== -1) hits++;
      }
      var denom = Math.max(1, list.length);
      bonus += (hits / denom) * FOCUS_PURITY_FRAC_SCALE;
      if (hits > 0 && hits === list.length && list.length === 1) {
        bonus += FOCUS_PURITY_SOLE_EXTRA;
      }
      return bonus;
    }

    function regionFocusTitleHit(title, hLow) {
      var rWords = String(focusPrimaryRegion).replace(/-/g, ' ');
      if (
        title.indexOf(rWords) !== -1 ||
        hLow.indexOf(focusPrimaryRegion) !== -1
      ) {
        return true;
      }
      if (focusPrimaryRegion === 'latin-america') {
        if (
          /latin|salsa|cumbia|brazil|cuba|tango|colombia|peru|mexico|argentina/i.test(
            title
          ) ||
          /latin|salsa|cumbia|brazil|cuba|tango/i.test(hLow)
        ) {
          return true;
        }
      }
      var inferredR = [];
      inferGeoFromText(title, function (id) {
        inferredR.push(id);
      });
      return inferredR.indexOf('region:' + focusPrimaryRegion) !== -1;
    }

    function regionFocusMatchStrength(handle, resolved, title, hLow) {
      if (!focusPrimaryRegion) return '';
      resolved = resolved || window.wmnCatalogGraph.resolveProduct(handle) || {};
      if (title == null || hLow == null) {
        var p0 = window.wmnCatalogGraph.getData().products[handle];
        title = String((p0 && p0.t) || handle).toLowerCase();
        hLow = String(handle).toLowerCase();
      }
      var ci;
      for (ci = 0; ci < (resolved.regions || []).length; ci++) {
        if (resolved.regions[ci] === focusPrimaryRegion) return 'strong';
      }
      if (regionFocusTitleHit(title, hLow)) return 'strong';

      var genres = resolved.genres || [];
      var core = focusCoreGenres(genres, focusRegionGenreKeys);
      var list = core.length ? core : genres;
      var hits = 0;
      var off = 0;
      for (ci = 0; ci < list.length; ci++) {
        if (focusRegionGenreKeys.indexOf(list[ci]) !== -1) hits++;
        else off++;
      }
      if (hits > 0 && off === 0) return 'strong';
      if (hits > 0) return 'weak';
      return '';
    }

    function regionFocusPurityBonus(handle) {
      if (
        !focusDominant ||
        focusPrimaryKind !== 'region' ||
        !focusPrimaryRegion ||
        !handle ||
        !graphReady()
      ) {
        return 0;
      }
      var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
      var p = window.wmnCatalogGraph.getData().products[handle];
      var title = String((p && p.t) || handle).toLowerCase();
      var hLow = String(handle).toLowerCase();
      var bonus = 0;
      var ci;
      for (ci = 0; ci < (resolved.regions || []).length; ci++) {
        if (resolved.regions[ci] === focusPrimaryRegion) {
          bonus += FOCUS_PURITY_REGION_TAG;
          break;
        }
      }
      if (regionFocusTitleHit(title, hLow)) bonus += FOCUS_PURITY_TITLE;
      var genres = resolved.genres || [];
      var core = focusCoreGenres(genres, focusRegionGenreKeys);
      var list = core.length ? core : genres;
      var hits = 0;
      for (ci = 0; ci < list.length; ci++) {
        if (focusRegionGenreKeys.indexOf(list[ci]) !== -1) hits++;
      }
      var denom = Math.max(1, list.length);
      bonus += (hits / denom) * FOCUS_PURITY_FRAC_SCALE;
      if (hits > 0 && hits === list.length && list.length === 1) {
        bonus += FOCUS_PURITY_SOLE_EXTRA;
      }
      return bonus;
    }

    function focusPurityBonus(handle) {
      if (focusPrimaryKind === 'country') return countryFocusPurityBonus(handle);
      if (focusPrimaryKind === 'region') return regionFocusPurityBonus(handle);
      if (focusPrimaryKind === 'genre') return genreFocusPurityBonus(handle);
      return 0;
    }

    function handleMatchesFocusWeak(handle) {
      if (!focusDominant || !handle || !graphReady()) return false;
      var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
      var p = window.wmnCatalogGraph.getData().products[handle];
      var title = String((p && p.t) || handle).toLowerCase();
      var hLow = String(handle).toLowerCase();
      if (focusPrimaryKind === 'country') {
        return countryFocusMatchStrength(handle, resolved, title, hLow) ===
          'weak';
      }
      if (focusPrimaryKind === 'region') {
        return regionFocusMatchStrength(handle, resolved, title, hLow) ===
          'weak';
      }
      if (focusPrimaryKind === 'genre') {
        return genreFocusMatchStrength(handle, resolved, title, hLow) ===
          'weak';
      }
      return false;
    }

    function handleMatchesGeo(handle) {
      if (!geoDominant || !handle || !graphReady()) return false;
      var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
      var ci;
      for (ci = 0; ci < (resolved.countries || []).length; ci++) {
        var c = resolved.countries[ci];
        if (c && c !== 'celtic' && geoKeys['country:' + c]) return true;
      }
      for (ci = 0; ci < (resolved.regions || []).length; ci++) {
        var r = resolved.regions[ci];
        if (r && geoKeys['region:' + r]) return true;
      }
      var p = window.wmnCatalogGraph.getData().products[handle];
      var title = (p && p.t) || handle;
      var inferred = [];
      inferGeoFromText(title, function (id) {
        inferred.push(id);
      });
      for (ci = 0; ci < inferred.length; ci++) {
        if (geoKeys[inferred[ci]]) return true;
      }
      return false;
    }

    function scoreWithGeoBonus(handle) {
      var base = scoreHandleEntities(handle, scoreCtx);
      if (focusDominant) {
        if (handleMatchesFocus(handle)) {
          base += FOCUS_SESSION_MATCH_BONUS;
          base += focusPurityBonus(handle);
        } else if (handleMatchesFocusWeak(handle)) {
          // Bridge-sampled comps: soft-fill only, never prefer-front.
          base += FOCUS_WEAK_BRIDGE_BONUS;
          base += focusPurityBonus(handle) * 0.35;
        } else {
          base -= FOCUS_SESSION_NONMATCH_PENALTY;
        }
        // Emb is tie-break only under session focus (cannot overcome ±match).
        if (LAMBDA_EMB > 0 && scoreCtx.uEmb) {
          base +=
            LAMBDA_EMB *
            EMB_FOCUS_TIE_SCALE *
            embDotForHandle(handle, scoreCtx.uEmb);
        }
      } else if (geoDominant) {
        if (handleMatchesGeo(handle)) {
          base += 160;
        } else {
          base -= 220;
        }
        if (LAMBDA_EMB > 0 && scoreCtx.uEmb) {
          base +=
            LAMBDA_EMB *
            EMB_SCORE_SCALE *
            embDotForHandle(handle, scoreCtx.uEmb);
        }
      } else if (LAMBDA_EMB > 0 && scoreCtx.uEmb) {
        base +=
          LAMBDA_EMB *
          EMB_SCORE_SCALE *
          embDotForHandle(handle, scoreCtx.uEmb);
      }
      return base;
    }

    var data = window.wmnCatalogGraph.getData();
    var handles = Object.keys(data.products || {});
    var candidates = [];
    var hi;
    for (hi = 0; hi < handles.length; hi++) {
      var h = handles[hi];
      if (!h || exclude[h]) continue;
      var gItem = graphItemFromHandle(h);
      if (!gItem) continue;
      var base = scoreWithGeoBonus(h);
      if (explore) base += exploreNoise(prefsNorm, rng);
      gItem._fyScore = base;
      candidates.push(gItem);
    }

    candidates.sort(function (a, b) {
      return (b._fyScore || 0) - (a._fyScore || 0);
    });

    var picks = [];
    var seen = {};
    var branchByHandle = {};
    function pushItem(item, branch) {
      if (!item || !item.handle || seen[item.handle]) return;
      seen[item.handle] = true;
      if (branch) branchByHandle[item.handle] = branch;
      picks.push(item);
    }

    var recentHandles = [];
    if (pinRecent) {
      store.events.forEach(function (ev) {
        if (ev && ev.h && recentHandles.indexOf(ev.h) === -1) {
          recentHandles.push(ev.h);
        }
      });
      recentHandles = recentHandles.slice(-10).reverse();
      if (focusDominant) {
        var focusRecent = [];
        for (var rf = 0; rf < recentHandles.length; rf++) {
          if (handleMatchesFocus(recentHandles[rf])) {
            focusRecent.push(recentHandles[rf]);
          }
        }
        recentHandles = focusRecent;
      } else if (geoDominant) {
        var geoRecent = [];
        for (var rj = 0; rj < recentHandles.length; rj++) {
          if (handleMatchesGeo(recentHandles[rj])) geoRecent.push(recentHandles[rj]);
        }
        recentHandles = geoRecent;
      }
      var pinCap = Math.min(6, Math.max(3, Math.floor(lim / 4)));
      var rh;
      for (rh = 0; rh < recentHandles.length && picks.length < pinCap; rh++) {
        if (exclude[recentHandles[rh]]) continue;
        var pinned = graphItemFromHandle(recentHandles[rh]);
        if (pinned) {
          pinned._fyScore =
            scoreWithGeoBonus(recentHandles[rh]) + FOCUS_HANDLE_BONUS;
          pushItem(pinned, 'pin');
        }
      }
    }

    // Prefer focus-matching pool (or prefs-geo pool) for MMR.
    // Under session focusDominant with a non-empty prefer pool: do not mmr-fill
    // with unrelated bestsellers (labels would lie). Empty prefer → soft catalog.
    var mmrPool = [];
    var mmrPreferPool = [];
    function rebuildMmrPools() {
      mmrPool = [];
      mmrPreferPool = [];
      for (var ci = 0; ci < candidates.length; ci++) {
        if (seen[candidates[ci].handle]) continue;
        if (focusDominant && handleMatchesFocus(candidates[ci].handle)) {
          mmrPreferPool.push(candidates[ci]);
        } else if (geoDominant && handleMatchesGeo(candidates[ci].handle)) {
          mmrPreferPool.push(candidates[ci]);
        } else {
          mmrPool.push(candidates[ci]);
        }
      }
    }
    rebuildMmrPools();

    var preferActive;
    var preferPool;
    var preferBranch;
    var focusSoftFallback = false;
    if (focusDominant) {
      if (mmrPreferPool.length > 0) {
        preferActive = true;
        preferPool = mmrPreferPool;
        preferBranch = 'mmr-prefer';
      } else {
        // Empty focus prefer (strict genre hard-gate, etc.) — show catalog
        // rather than hiding the whole Picked for you section.
        focusSoftFallback = true;
        preferActive = false;
        preferPool = mmrPool;
        preferBranch = 'mmr-soft';
      }
    } else {
      preferActive = geoDominant && mmrPreferPool.length;
      preferPool = preferActive ? mmrPreferPool : mmrPool;
      preferBranch = preferActive ? 'mmr-prefer' : 'mmr';
    }
    var skipCountryMmr =
      focusDominant &&
      (focusPrimaryKind === 'country' || focusPrimaryKind === 'region');
    var mmrPicked = mmrSelect(
      preferPool,
      lim - picks.length,
      picks,
      function (it) {
        return it._fyScore || 0;
      },
      -Infinity,
      { skipCountryDiversity: skipCountryMmr }
    );
    for (var mi = 0; mi < mmrPicked.length; mi++) {
      pushItem(mmrPicked[mi], preferBranch);
    }
    // Soft-fill when prefs-geo prefer is active, or focus prefer was empty /
    // too sparse after hard gate (< 3) so the rail never vanishes.
    // fillToLimit: Store For you paginates Shopify's full catalog — after the
    // India/prefer block, continue with score-ordered remainder (mmr-fill)
    // without clearing focusDominant / lying about the focus subtitle.
    if (
      picks.length < lim &&
      ((preferActive && preferPool === mmrPreferPool && !focusDominant) ||
        focusSoftFallback ||
        (focusDominant && picks.length < 3) ||
        (focusDominant && fillToLimit))
    ) {
      if (focusDominant && picks.length < 3 && !fillToLimit) {
        focusSoftFallback = true;
      }
      var fill = mmrSelect(
        mmrPool,
        lim - picks.length,
        picks,
        function (it) {
          return it._fyScore || 0;
        },
        -Infinity,
        { skipCountryDiversity: skipCountryMmr }
      );
      for (var fi = 0; fi < fill.length; fi++) {
        pushItem(
          fill[fi],
          focusSoftFallback ? 'mmr-soft' : 'mmr-fill'
        );
      }
    }

    // Rails need ≥3 cards; single-tile callers (mega-promo limit:1–5) may
    // request fewer — only abort when we can't fill the requested lim floor.
    if (picks.length < Math.min(3, lim)) return null;

    // Soft fallback must not keep a lying focus subtitle / focusDominant flag.
    if (focusSoftFallback) {
      focusDominant = false;
      focus = null;
    }

    // Softmax π over full eligible candidate set (scores include explore noise).
    var piRows = [];
    for (var pri = 0; pri < candidates.length; pri++) {
      piRows.push({
        handle: candidates[pri].handle,
        score: candidates[pri]._fyScore || 0
      });
    }
    // Pins may have elevated score not in candidates list — include them.
    for (var ppi = 0; ppi < picks.length; ppi++) {
      var ph = picks[ppi].handle;
      var found = false;
      for (var pj = 0; pj < piRows.length; pj++) {
        if (piRows[pj].handle === ph) {
          found = true;
          if ((picks[ppi]._fyScore || 0) > piRows[pj].score) {
            piRows[pj].score = picks[ppi]._fyScore || 0;
          }
          break;
        }
      }
      if (!found) {
        piRows.push({ handle: ph, score: picks[ppi]._fyScore || 0 });
      }
    }
    var piMap = softmaxPiMap(piRows, FY_SOFTMAX_TEMP);

    // Labels: single primary from last intentional focus (hover/click/search),
    // not a stack of neighbors. uniqPush re-promotes so Blues after Klezmer wins.
    // Prefer genre/artist when set (Jazz/Blues subtitle); else country/region.
    var focusLabelIds = [];
    if (focus) {
      var primaryG =
        focus.primaryGenre || (focus.genres && focus.genres[0]) || '';
      var primaryA =
        focus.primaryArtist || (focus.artists && focus.artists[0]) || '';
      var kind = focus.primaryKind || focusPrimaryKind || '';
      if (primaryG && kind !== 'country' && kind !== 'region') {
        focusLabelIds.push('genre:' + primaryG);
      } else if (kind === 'country' && focus.primaryCountry) {
        focusLabelIds.push('country:' + focus.primaryCountry);
      } else if (kind === 'region' && focus.primaryRegion) {
        focusLabelIds.push('region:' + focus.primaryRegion);
      } else if (primaryG) {
        focusLabelIds.push('genre:' + primaryG);
      } else if (primaryA) {
        focusLabelIds.push('artist:' + primaryA);
      }
    }

    var labelIds;
    if (focusLabelIds.length) {
      labelIds = focusLabelIds.slice(0, 1);
    } else if (topCountries.length) {
      labelIds = topCountries.slice(0, 2);
    } else if (topRegions.length) {
      labelIds = topRegions.slice(0, 2);
    } else if (topArtistGenre.length) {
      labelIds = topArtistGenre.slice(0, 2);
    } else {
      labelIds = labelSeeds.slice(0, 2);
    }
    var labels = labelIds.map(function (id) {
      var parts = id.split(':');
      var key = parts[1] || id;
      var gdata = window.wmnCatalogGraph.getData();
      if (parts[0] === 'country' && gdata.countries && gdata.countries[key]) {
        return gdata.countries[key].n;
      }
      if (parts[0] === 'region' && gdata.regions && gdata.regions[key]) {
        return gdata.regions[key].n;
      }
      if (parts[0] === 'artist' && gdata.artists[key]) return gdata.artists[key].n;
      if (parts[0] === 'genre' && gdata.genres[key]) return gdata.genres[key].n;
      return key.replace(/-/g, ' ').replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
    });

    // Strip internal score before return (keep for debug via scored count).
    var outPicks = picks.slice(0, lim).map(function (it, idx) {
      var pi = piMap[it.handle] != null ? piMap[it.handle] : 0;
      var br = branchByHandle[it.handle] || 'mmr';
      var copy = {
        handle: it.handle,
        title: it.title,
        artist: it.artist,
        url: it.url,
        image: it.image,
        genres: it.genres,
        fromGraph: it.fromGraph,
        score: it._fyScore,
        pi: roundPi(pi),
        rank: idx,
        piSlot: roundPi(pi * slotWeight(idx)),
        branch: br,
        policy: FY_POLICY_ID,
        explore: !!explore
      };
      return copy;
    });

    return {
      picks: outPicks,
      labels: labels,
      seeds: labelSeeds,
      geo:
        focusDominant
          ? focusPrimaryKind === 'country' || focusPrimaryKind === 'region'
          : !!(topCountries.length || topRegions.length),
      focusDominant: !!focusDominant,
      focusKind: focusPrimaryKind || '',
      scored: candidates.length,
      policy: FY_POLICY_ID,
      explore: !!explore,
      eligible: piRows.length
    };
  }

  /**
   * Shared For you composition (legacy homepage paint + Discover via scoreCatalogForYou(32)).
   * Homepage endless feed owns #wmn-for-you via wmn-for-you-feed.js.
   * @returns {{ picks: object[], labels: string[], seeds: string[] }|null}
   */
  function buildForYouPicks(limit) {
    return scoreCatalogForYou({
      limit: limit || 18,
      explore: false,
      pinRecent: true
    });
  }

  function hasGeoForYouPrefs() {
    var prefs = (readStore().entities || {});
    return (
      topEntityIds(prefs, 1, ['country']).length > 0 ||
      topEntityIds(prefs, 1, ['region']).length > 0
    );
  }

  function forYouFeedOwns(root) {
    var el = root || document.getElementById('wmn-for-you');
    if (!el) return false;
    if (el.getAttribute('data-wmn-fy-feed') === '1') return true;
    return !!(window.wmnForYouFeed && typeof window.wmnForYouFeed.ensure === 'function');
  }

  var BECAUSE_CAP = 80;
  var BECAUSE_PAGE = 8;
  var BECAUSE_TITLE_MAX = 40;

  /**
   * Handle key for Because exclude: lowercase, strip leading `the-`, collapse dashes.
   * Also accepts /products/… paths (same as RV cookie normalize).
   */
  function normalizeBecauseHandleKey(handle) {
    var h = normalizeRecentlyViewedHandle(handle);
    if (!h) h = String(handle == null ? '' : handle).replace(/^\s+|\s+$/g, '');
    if (!h) return '';
    h = h.toLowerCase();
    if (h.indexOf('the-') === 0) h = h.slice(4);
    h = h.replace(/-+/g, '-').replace(/^-|-$/g, '');
    return h;
  }

  /**
   * Title core for Because exclude: lowercase, strip punctuation / "the" /
   * "rough guide to", collapse whitespace. "Rough Guide to Spiritual Blues"
   * and "Spiritual Blues" share the same key.
   */
  function normalizeBecauseTitleKey(title) {
    var s = String(title || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/['']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '');
    s = s.replace(/^the\s+/, '');
    s = s.replace(/^rough\s+guide\s+to\s+/, '');
    s = s.replace(/^rough\s+guide\s+/, '');
    return s;
  }

  /**
   * Stable Shopify CDN fingerprint: basename without `_200x` size / query.
   * Catches duplicate SKUs that share a cover (e.g. spiritual-blues ↔ gospel-holy).
   */
  function becauseImageKey(url) {
    if (!url) return '';
    var u = normalizeImageUrl(url).toLowerCase().split('?')[0];
    u = u.replace(/_\d+x\d*(?=\.(?:jpe?g|png|webp|gif)$)/i, '');
    u = u.replace(
      /_(pico|icon|thumb|small|compact|medium|large|grande|original|master)(?=\.(?:jpe?g|png|webp|gif)$)/i,
      ''
    );
    var slash = u.lastIndexOf('/');
    var base = slash >= 0 ? u.slice(slash + 1) : u;
    return base.replace(/\.(?:jpe?g|png|webp|gif)$/i, '') || u;
  }

  /**
   * Distinctive seed phrases for fuzzy exclude, e.g. spiritual-blues / spiritual blues.
   * Derived from handle + title cores (after stripping the-/rough-guide-to-).
   */
  /**
   * True when phrase is a catalog genre slug/name (e.g. "flamenco").
   * Those must not become fuzzy-exclude tokens — they wipe the Dig deeper
   * pure-genre pool (Calaita Flamenco Son, Rough Guide to Flamenco vol 1…).
   */
  function becauseIsCatalogGenrePhrase(phrase) {
    if (!phrase || !graphReady()) return false;
    var data = window.wmnCatalogGraph.getData();
    if (!data || !data.genres) return false;
    var raw = String(phrase)
      .toLowerCase()
      .replace(/^\s+|\s+$/g, '');
    if (!raw) return false;
    var slug = raw.replace(/\s+/g, '-').replace(/-+/g, '-');
    if (data.genres[slug]) return true;
    var want = raw.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
    var k;
    for (k in data.genres) {
      if (!Object.prototype.hasOwnProperty.call(data.genres, k)) continue;
      var n = data.genres[k] && data.genres[k].n;
      if (!n) continue;
      var nn = String(n)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (nn && nn === want) return true;
    }
    return false;
  }

  function becauseSeedFuzzyPhrases(seed, seedTitle) {
    var out = [];
    var seen = {};
    function add(p) {
      var k = String(p || '')
        .toLowerCase()
        .replace(/^\s+|\s+$/g, '');
      if (!k || k.length < 6 || seen[k]) return;
      // Genre-only cores exclude the whole Dig deeper lane — skip them.
      if (becauseIsCatalogGenrePhrase(k)) return;
      seen[k] = true;
      out.push(k);
    }
    var norm = normalizeBecauseHandleKey(seed);
    var hCore = norm
      .replace(/^rough-guide-to-/, '')
      .replace(/^rough-guide-/, '');
    if (hCore) {
      add(hCore);
      add(hCore.replace(/-/g, ' '));
    }
    var tCore = normalizeBecauseTitleKey(seedTitle || '');
    if (tCore) {
      add(tCore);
      add(tCore.replace(/\s+/g, '-'));
    }
    return out;
  }

  function becauseFuzzyMatchesSeed(handle, title, phrases) {
    if (!phrases || !phrases.length) return false;
    var h = String(handle || '').toLowerCase();
    var t = normalizeBecauseTitleKey(title || '');
    var blob = h + ' ' + h.replace(/-/g, ' ') + ' ' + t;
    var i;
    for (i = 0; i < phrases.length; i++) {
      if (phrases[i] && blob.indexOf(phrases[i]) !== -1) return true;
    }
    return false;
  }

  /** Seed = focus.handles[0] or latest view / search_click event handle. */
  function becauseSeedHandle() {
    var focus = readFocus();
    if (focus && focus.handles && focus.handles.length && focus.handles[0]) {
      return String(focus.handles[0]);
    }
    var store = readStore();
    var events = store.events || [];
    for (var i = events.length - 1; i >= 0; i--) {
      var ev = events[i];
      if (!ev || !ev.h) continue;
      var k = ev.k || 'view';
      if (k === 'view' || k === 'search_click') return String(ev.h);
    }
    return '';
  }

  function becauseSeedTitle(handle) {
    if (!handle) return '';
    try {
      var item = graphItemFromHandle(handle);
      if (item && item.title) return item.title;
      // Handle mismatch: try normalized / the- variants against the graph.
      var norm = normalizeBecauseHandleKey(handle);
      if (norm && norm !== handle) {
        item = graphItemFromHandle(norm);
        if (item && item.title) return item.title;
        item = graphItemFromHandle('the-' + norm);
        if (item && item.title) return item.title;
      }
    } catch (e) {
      /* ignore */
    }
    var store = readStore();
    var events = store.events || [];
    for (var i = events.length - 1; i >= 0; i--) {
      if (events[i] && events[i].h === handle && events[i].title) {
        return String(events[i].title);
      }
    }
    return humanizeSlug(handle);
  }

  function truncateBecauseTitle(title) {
    var s = String(title || '').replace(/\s+/g, ' ').trim();
    if (s.length <= BECAUSE_TITLE_MAX) return s;
    var cut = s.slice(0, BECAUSE_TITLE_MAX - 1);
    var sp = cut.lastIndexOf(' ');
    if (sp > 16) cut = cut.slice(0, sp);
    return cut.replace(/[.,;:\-–—\s]+$/, '') + '…';
  }

  /**
   * Block seed (+ handle variants), core titles, identical cover images,
   * fuzzy seed phrases, RV cookie, and For You locked.
   * @returns {{ handles: Object, norms: Object, titles: Object, images: Object, phrases: string[] }}
   */
  function becauseExcludeSet(seed) {
    var handles = {};
    var norms = {};
    var titles = {};
    var images = {};
    var phrases = [];

    function blockHandle(h) {
      if (!h) return;
      var raw = normalizeRecentlyViewedHandle(h) || String(h).replace(/^\s+|\s+$/g, '');
      if (!raw) return;
      handles[raw] = true;
      handles[String(h)] = true;
      var n = normalizeBecauseHandleKey(raw);
      if (n) {
        norms[n] = true;
        handles[n] = true;
        handles['the-' + n] = true;
      }
    }

    function blockTitle(t) {
      var key = normalizeBecauseTitleKey(t);
      if (key) titles[key] = true;
    }

    function blockImage(url) {
      var key = becauseImageKey(url);
      if (key) images[key] = true;
    }

    blockHandle(seed);
    var seedTitle = becauseSeedTitle(seed);
    blockTitle(seedTitle);
    phrases = becauseSeedFuzzyPhrases(seed, seedTitle);

    try {
      var seedItem = graphItemFromHandle(seed);
      if (seedItem) {
        if (seedItem.title) blockTitle(seedItem.title);
        if (seedItem.image) blockImage(seedItem.image);
      }
      var seedNorm = normalizeBecauseHandleKey(seed);
      if (seedNorm) {
        var alt =
          graphItemFromHandle(seedNorm) || graphItemFromHandle('the-' + seedNorm);
        if (alt) {
          if (alt.title) blockTitle(alt.title);
          if (alt.image) blockImage(alt.image);
          blockHandle(alt.handle || seedNorm);
        }
        // Raw catalog img (before getProductImage width rewrite).
        if (graphReady()) {
          var data = window.wmnCatalogGraph.getData();
          var rawP =
            (data && data.products && data.products[seed]) ||
            (data && data.products && data.products[seedNorm]) ||
            (data && data.products && data.products['the-' + seedNorm]);
          if (rawP && rawP.img) blockImage(rawP.img);
          // Block every catalog twin that shares the seed cover.
          if (rawP && rawP.img && data && data.products) {
            var seedImgKey = becauseImageKey(rawP.img);
            if (seedImgKey) {
              var ph;
              for (ph in data.products) {
                if (!Object.prototype.hasOwnProperty.call(data.products, ph)) {
                  continue;
                }
                var twin = data.products[ph];
                if (!twin || !twin.img) continue;
                if (becauseImageKey(twin.img) !== seedImgKey) continue;
                blockHandle(ph);
                if (twin.t) blockTitle(twin.t);
                blockImage(twin.img);
              }
            }
          }
        }
      }
    } catch (eSeed) {
      /* ignore */
    }

    try {
      var rv = readRecentlyViewedCookie();
      for (var r = 0; r < rv.length; r++) blockHandle(rv[r]);
    } catch (eRv) {
      /* ignore */
    }

    try {
      var cards = document.querySelectorAll(
        '#wmn-for-you-grid [data-wmn-handle][data-wmn-fy-locked="1"]'
      );
      for (var i = 0; i < cards.length; i++) {
        var h = cards[i].getAttribute('data-wmn-handle');
        if (h) blockHandle(h);
      }
    } catch (e) {
      /* ignore */
    }

    // Staff Picks "For you" slot must never also appear in Dig deeper.
    try {
      var staffFy = getStaffPicksAffinityHandle();
      if (staffFy) blockHandle(staffFy);
    } catch (eStaff) {
      /* ignore */
    }

    return {
      handles: handles,
      norms: norms,
      titles: titles,
      images: images,
      phrases: phrases
    };
  }

  function becauseIsExcluded(handle, item, excl) {
    if (!handle || !excl) return true;
    if (excl.handles[handle]) return true;
    var n = normalizeBecauseHandleKey(handle);
    if (n && (excl.norms[n] || excl.handles[n] || excl.handles['the-' + n])) {
      return true;
    }
    var title = item && item.title ? item.title : '';
    var image = item && item.image ? item.image : '';
    if (!title || !image) {
      try {
        var gi = graphItemFromHandle(handle);
        if (gi) {
          if (!title && gi.title) title = gi.title;
          if (!image && gi.image) image = gi.image;
        }
      } catch (eT) {
        /* ignore */
      }
    }
    if (!image && graphReady()) {
      try {
        var data = window.wmnCatalogGraph.getData();
        var raw =
          data && data.products && (data.products[handle] || data.products[n]);
        if (raw && raw.img) image = raw.img;
        if (!title && raw && raw.t) title = raw.t;
      } catch (eImg) {
        /* ignore */
      }
    }
    var tk = normalizeBecauseTitleKey(title);
    if (tk && excl.titles[tk]) return true;
    var ik = becauseImageKey(image);
    if (ik && excl.images && excl.images[ik]) return true;
    if (becauseFuzzyMatchesSeed(handle, title, excl.phrases)) return true;
    return false;
  }

  /** Merge two becauseExcludeSet results (used when raw seed ≠ resolved seedKey). */
  function becauseMergeExclude(into, extra) {
    if (!into || !extra) return into;
    var ek;
    for (ek in extra.handles) {
      if (Object.prototype.hasOwnProperty.call(extra.handles, ek)) {
        into.handles[ek] = true;
      }
    }
    for (ek in extra.norms) {
      if (Object.prototype.hasOwnProperty.call(extra.norms, ek)) {
        into.norms[ek] = true;
      }
    }
    for (ek in extra.titles) {
      if (Object.prototype.hasOwnProperty.call(extra.titles, ek)) {
        into.titles[ek] = true;
      }
    }
    if (extra.images) {
      if (!into.images) into.images = {};
      for (ek in extra.images) {
        if (Object.prototype.hasOwnProperty.call(extra.images, ek)) {
          into.images[ek] = true;
        }
      }
    }
    if (extra.phrases && extra.phrases.length) {
      into.phrases = (into.phrases || []).concat(extra.phrases);
    }
    return into;
  }

  function becauseRankScore(handle, seedResolved) {
    if (!handle || !seedResolved || !graphReady()) return 0;
    var r = window.wmnCatalogGraph.resolveProduct(handle) || {};
    var score = 0;
    var sa = seedResolved.artists || [];
    var ra = r.artists || [];
    var i;
    for (i = 0; i < sa.length; i++) {
      if (sa[i] && ra.indexOf(sa[i]) !== -1) {
        score += 1000;
        break;
      }
    }
    var sg = seedResolved.genres || [];
    var rg = r.genres || [];
    if (sg[0] && rg.indexOf(sg[0]) !== -1) score += 200;
    for (i = 1; i < sg.length; i++) {
      if (sg[i] && rg.indexOf(sg[i]) !== -1) {
        score += 40;
        break;
      }
    }
    var sc = seedResolved.countries || [];
    var rc = r.countries || [];
    for (i = 0; i < sc.length; i++) {
      if (sc[i] && rc.indexOf(sc[i]) !== -1) {
        score += 80;
        break;
      }
    }
    return score;
  }

  /**
   * Dig deeper lane target — prefers session focus (search "jazz") so the
   * title and pool stay aligned, then falls back to the seed product.
   * @returns {{ kind: 'genre'|'country'|'region', slug: string }|null}
   */
  function becauseDigDeeperTarget(seedKey) {
    var focus = readFocus();
    var resolved =
      seedKey && graphReady()
        ? window.wmnCatalogGraph.resolveProduct(seedKey) || {}
        : {};

    if (focus && focus.primaryKind === 'genre') {
      var fg =
        focus.primaryGenre || (focus.genres && focus.genres[0]) || '';
      if (fg) return { kind: 'genre', slug: String(fg) };
    }
    if (focus && focus.primaryKind === 'country' && focus.primaryCountry) {
      return { kind: 'country', slug: String(focus.primaryCountry) };
    }
    if (focus && focus.primaryKind === 'region' && focus.primaryRegion) {
      return { kind: 'region', slug: String(focus.primaryRegion) };
    }

    var g0 = (resolved.genres && resolved.genres[0]) || '';
    if (!g0 && focus) {
      g0 = focus.primaryGenre || (focus.genres && focus.genres[0]) || '';
    }
    if (g0) return { kind: 'genre', slug: String(g0) };

    var c0 = (resolved.countries && resolved.countries[0]) || '';
    if (!c0 && focus) c0 = focus.primaryCountry || '';
    if (c0) return { kind: 'country', slug: String(c0) };

    var r0 = (resolved.regions && resolved.regions[0]) || '';
    if (!r0 && focus) r0 = focus.primaryRegion || '';
    if (r0) return { kind: 'region', slug: String(r0) };

    return null;
  }

  /**
   * Word-bound genre match against title/handle text.
   * @param {string} text
   * @param {string} genreSlug
   * @returns {boolean}
   */
  function becauseGenreWordInText(text, genreSlug) {
    var gWords = String(genreSlug || '')
      .toLowerCase()
      .replace(/-/g, ' ')
      .trim();
    if (!gWords || !text) return false;
    if (gWords.indexOf(' ') === -1) {
      var re = new RegExp(
        '\\b' + gWords.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b',
        'i'
      );
      return re.test(text);
    }
    return text.indexOf(gWords) !== -1;
  }

  /**
   * "Jazz and Blues Legends" series — Blues-primary dual tags; the word
   * "jazz" in the series name is not Jazz-genre evidence.
   */
  function becauseIsJazzAndBluesSeries(title, handle) {
    var text = (String(title || '') + ' ' + String(handle || ''))
      .toLowerCase()
      .replace(/-/g, ' ');
    return /\bjazz\s+and\s+blues\b/.test(text);
  }

  /**
   * Neighbor genre slugs for Dig deeper soft-fill.
   * Genre digs: relatedGenres siblings only — never region bridges
   * (salsa∈caribbean was pulling reggae/calypso into "Dig deeper into Salsa").
   * Country/region digs: still use geo→genre bridges.
   */
  function becauseDigNeighborSlugs(digTarget) {
    var out = [];
    var seen = {};
    if (!digTarget || !digTarget.slug || !graphReady()) return out;
    var data = window.wmnCatalogGraph.getData() || {};
    var genres = data.genres || {};

    function add(slug) {
      var s = String(slug || '').toLowerCase();
      if (!s || seen[s] || !genres[s]) return;
      if (digTarget.kind === 'genre' && s === String(digTarget.slug).toLowerCase()) {
        return;
      }
      seen[s] = true;
      out.push(s);
    }

    if (digTarget.kind === 'genre') {
      var digSlug = String(digTarget.slug).toLowerCase();
      try {
        if (typeof window.wmnCatalogGraph.relatedGenres === 'function') {
          var rels = window.wmnCatalogGraph.relatedGenres(digSlug) || [];
          var ri;
          for (ri = 0; ri < rels.length; ri++) {
            if (rels[ri] && rels[ri].slug) add(rels[ri].slug);
          }
        }
      } catch (eRel) {
        /* ignore */
      }
    } else if (digTarget.kind === 'country') {
      var cBridge = COUNTRY_GENRE_BRIDGES[String(digTarget.slug).toLowerCase()] || [];
      var ci;
      for (ci = 0; ci < cBridge.length; ci++) add(cBridge[ci]);
    } else if (digTarget.kind === 'region') {
      var rBridge =
        REGION_GENRE_BRIDGES[String(digTarget.slug).toLowerCase()] || [];
      var rbi;
      for (rbi = 0; rbi < rBridge.length; rbi++) add(rBridge[rbi]);
    }
    return out;
  }

  /**
   * Soft Dig deeper match — primary genre must be a neighbor.
   * Secondary brazilian/reggae chips on unrelated albums must not pad Salsa.
   */
  function becauseProductMatchesNeighborGenre(handle, neighborSlugs) {
    if (!handle || !neighborSlugs || !neighborSlugs.length || !graphReady()) {
      return false;
    }
    var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
    var pGenres = resolved.genres || [];
    if (!pGenres.length) return false;
    // Kitchen-sink comps (4+ chips) are never honest soft neighbours.
    if (pGenres.length >= 4) return false;
    var primary = String(pGenres[0] || '').toLowerCase();
    if (!primary) return false;
    var j;
    for (j = 0; j < neighborSlugs.length; j++) {
      if (primary === neighborSlugs[j]) return true;
    }
    return false;
  }

  /**
   * Dig deeper genre gate — oval chips (graph g[]) are source of truth.
   * Jazz stays tighter: "Jazz and Blues" series ≠ Jazz; secondary Jazz
   * still needs the word in the title. Kitchen-sink comps (≥4 chips) need
   * the dig genre in the title or as primary — "Best Music You've Never
   * Heard" must not qualify as Salsa from a buried chip.
   */
  function becauseProductMatchesGenre(handle, genreSlug) {
    if (!handle || !genreSlug || !graphReady()) return false;
    var slug = String(genreSlug).toLowerCase();
    var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
    var pGenres = resolved.genres || [];
    var i;
    var hasChip = false;
    var genresLow = [];
    for (i = 0; i < pGenres.length; i++) {
      var gLow = String(pGenres[i] || '').toLowerCase();
      if (!gLow) continue;
      genresLow.push(gLow);
      if (gLow === slug) hasChip = true;
    }
    if (!hasChip) return false;

    var data = window.wmnCatalogGraph.getData();
    var p =
      (data && data.products && data.products[handle]) || null;
    var title = String((p && p.t) || handle).toLowerCase();
    var hLow = String(handle).toLowerCase();
    var text = title + ' ' + hLow.replace(/-/g, ' ');
    var digInTitle = becauseGenreWordInText(text, slug);

    if (slug === 'jazz' && becauseIsJazzAndBluesSeries(title, handle)) {
      return false;
    }

    // Multi-tag comps: buried chips are not Dig deeper evidence.
    if (genresLow.length >= 4 && genresLow[0] !== slug && !digInTitle) {
      return false;
    }

    // Primary oval matches dig genre — reject when title clearly names
    // another tagged genre instead (Highlife 2nd ed tagged Jazz first).
    if (genresLow.length && genresLow[0] === slug) {
      if (!digInTitle) {
        for (i = 0; i < genresLow.length; i++) {
          if (genresLow[i] === slug) continue;
          if (becauseGenreWordInText(text, genresLow[i])) return false;
        }
      }
      return true;
    }

    // Secondary chip: Jazz stays title-bound; other genres trust the chip.
    if (slug === 'jazz') return digInTitle;
    return true;
  }

  /**
   * Dig deeper country gate — require graph country tag OR explicit
   * country/adjective in title/handle. No soft genre association (that was
   * flooding "Dig deeper into Japan" with Latin/Brazil neighbours).
   */
  function becauseProductMatchesCountry(handle, countrySlug) {
    if (!handle || !countrySlug || !graphReady()) return false;
    var slug = String(countrySlug).toLowerCase();
    var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
    var countries = resolved.countries || [];
    var i;
    for (i = 0; i < countries.length; i++) {
      if (String(countries[i] || '').toLowerCase() === slug) return true;
    }
    var data = window.wmnCatalogGraph.getData();
    var p = (data && data.products && data.products[handle]) || null;
    var title = String((p && p.t) || handle).toLowerCase();
    var hLow = String(handle).toLowerCase();
    var text = title + ' ' + hLow.replace(/-/g, ' ');
    var cWords = slug.replace(/-/g, ' ');
    if (cWords && text.indexOf(cWords) !== -1) return true;
    var adjList = COUNTRY_ADJ_TOKENS[slug] || [];
    for (i = 0; i < adjList.length; i++) {
      var adj = adjList[i];
      if (!adj) continue;
      var adjRe = new RegExp(
        '\\b' + String(adj).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b',
        'i'
      );
      if (adjRe.test(title) || adjRe.test(hLow)) return true;
    }
    return false;
  }

  /**
   * Dig deeper region gate — require graph region membership (or Latin
   * America soft genre/title cues already encoded on the product).
   */
  function becauseProductMatchesRegion(handle, regionSlug) {
    if (!handle || !regionSlug || !graphReady()) return false;
    var slug = String(regionSlug).toLowerCase();
    var resolved = window.wmnCatalogGraph.resolveProduct(handle) || {};
    var regions = resolved.regions || [];
    var i;
    for (i = 0; i < regions.length; i++) {
      if (String(regions[i] || '').toLowerCase() === slug) return true;
    }
    var data = window.wmnCatalogGraph.getData();
    var p = (data && data.products && data.products[handle]) || null;
    var title = String((p && p.t) || handle).toLowerCase();
    var hLow = String(handle).toLowerCase();
    var text = title + ' ' + hLow.replace(/-/g, ' ');
    var rWords = slug.replace(/-/g, ' ');
    if (rWords && text.indexOf(rWords) !== -1) return true;
    if (slug === 'latin-america') {
      var latinGenres = [
        'salsa',
        'cumbia',
        'brazilian',
        'afro-cuban-son',
        'tango'
      ];
      var genres = resolved.genres || [];
      for (i = 0; i < genres.length; i++) {
        if (latinGenres.indexOf(String(genres[i] || '').toLowerCase()) !== -1) {
          return true;
        }
      }
      if (
        /latin|salsa|cumbia|brazil|cuba|tango|colombia|peru|mexico|argentina/i.test(
          text
        )
      ) {
        return true;
      }
    }
    return false;
  }

  function becauseColumnClass(index) {
    var cycle = index % 4;
    var pos = cycle === 0 ? 'alpha' : cycle === 3 ? 'omega' : '';
    return (
      'thumbnail four columns wmn-because-card' +
      (pos ? ' ' + pos : '') +
      (index % 2 === 0 ? ' even' : ' odd')
    );
  }

  /** Dig deeper cover URL — item.image, graph cache, then baked products[h].img. */
  function becauseResolveImage(handle, item) {
    var img = (item && item.image) || '';
    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.getProductImage === 'function'
      ) {
        img = window.wmnCatalogGraph.getProductImage(handle) || img;
      }
      if (
        !img &&
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.getData === 'function'
      ) {
        var data = window.wmnCatalogGraph.getData();
        var p = data && data.products && data.products[handle];
        if (p && p.img) img = p.img;
      }
    } catch (eImg) {
      /* ignore */
    }
    return withShopifyImageWidth(normalizeImageUrl(img), 800) || '';
  }

  function becausePatchCardImages(grid) {
    if (!grid) return;
    var cards = grid.querySelectorAll('[data-wmn-because-card]');
    var i;
    for (i = 0; i < cards.length; i++) {
      var card = cards[i];
      var handle = card.getAttribute('data-wmn-handle');
      if (!handle) continue;
      var src = becauseResolveImage(handle, null);
      if (!src) continue;
      var img = card.querySelector('.product_image img, .image__container img, img');
      if (img) {
        var cur = img.getAttribute('src') || '';
        if (cur && img.complete && img.naturalWidth > 0) continue;
        img.removeAttribute('data-src');
        img.setAttribute('loading', 'eager');
        img.setAttribute('decoding', 'async');
        img.setAttribute('width', '800');
        img.setAttribute('height', '800');
        img.setAttribute('src', src);
        continue;
      }
      var box = card.querySelector('.image__container');
      if (box) {
        box.innerHTML =
          '<img src="' +
          escapeHtml(src) +
          '" alt="' +
          escapeHtml(card.getAttribute('data-wmn-title') || '') +
          '" loading="eager" decoding="async" width="800" height="800" />';
      }
    }
  }

  /**
   * Safari/WebKit: imgs inserted under [hidden]/display:none often never
   * fetch. After the rail is revealed, re-assign src to kick the loader.
   */
  function becauseNudgeImages(grid) {
    if (!grid) return;
    var imgs = grid.querySelectorAll(
      '[data-wmn-because-card] .image__container img, [data-wmn-because-card] img'
    );
    var i;
    for (i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!img) continue;
      if (img.complete && img.naturalWidth > 0) continue;
      var src = img.getAttribute('src') || img.getAttribute('data-src') || '';
      if (!src) continue;
      img.removeAttribute('data-src');
      img.setAttribute('loading', 'eager');
      img.setAttribute('decoding', 'async');
      // Clear then restore — forces WebKit to start the request post-reveal.
      img.removeAttribute('src');
      img.setAttribute('src', src);
    }
  }

  function becauseHydratePageImages(grid, handles) {
    if (!grid) return;
    var need = [];
    var seen = {};
    var i;
    for (i = 0; i < (handles || []).length; i++) {
      var h = handles[i];
      if (!h || seen[h]) continue;
      seen[h] = true;
      need.push(h);
    }
    var cards = grid.querySelectorAll('[data-wmn-because-card]');
    for (i = 0; i < cards.length; i++) {
      var handle = cards[i].getAttribute('data-wmn-handle');
      if (!handle || seen[handle]) continue;
      var img = cards[i].querySelector('img');
      var src = img && img.getAttribute('src');
      if (src) continue;
      seen[handle] = true;
      need.push(handle);
    }
    if (!need.length) {
      becausePatchCardImages(grid);
      becauseNudgeImages(grid);
      return;
    }
    if (
      !window.wmnCatalogGraph ||
      typeof window.wmnCatalogGraph.hydrateImages !== 'function'
    ) {
      becausePatchCardImages(grid);
      becauseNudgeImages(grid);
      return;
    }
    window.wmnCatalogGraph
      .hydrateImages(need)
      .then(function () {
        becausePatchCardImages(grid);
        becauseNudgeImages(grid);
      })
      .catch(function () {
        becausePatchCardImages(grid);
        becauseNudgeImages(grid);
      });
  }

  function becausePaintPage(root, grid) {
    var state = root && root._wmnBecauseState;
    if (!state || !state.pool || !state.pool.length || !grid) return 0;
    var pool = state.pool;
    var start = ((state.pageIndex % pool.length) + pool.length) % pool.length;
    try {
      var holdH = grid.offsetHeight;
      if (holdH > 40) grid.style.minHeight = holdH + 'px';
    } catch (eHold) {
      /* ignore */
    }
    grid.innerHTML = '';
    grid.className = 'product-list clearfix collection-matrix';
    var added = 0;
    var needHydrate = [];
    var i;
    for (i = 0; i < BECAUSE_PAGE && i < pool.length; i++) {
      var pick = pool[(start + i) % pool.length];
      if (!pick || !pick.handle || !pick.item) continue;
      var imgUrl = becauseResolveImage(pick.handle, pick.item);
      if (!imgUrl) needHydrate.push(pick.handle);
      // Keep item.image warm for later pages / exclude fingerprints.
      if (imgUrl && pick.item && !pick.item.image) pick.item.image = imgUrl;
      var ev = {
        h: pick.handle,
        title: pick.item.title || pick.handle,
        img: imgUrl,
        price: '',
        tags: []
      };
      var card = buildSearchMemoryCard(ev, becauseColumnClass(added), {
        eager: true
      });
      if (!card) continue;
      // Keep wmn-memory-search-card so cover CSS (width:100%) still applies.
      card.className = becauseColumnClass(added) + ' wmn-memory-search-card';
      card.removeAttribute('data-wmn-injected');
      card.setAttribute('data-wmn-handle', pick.handle);
      card.setAttribute('data-wmn-title', ev.title);
      card.setAttribute('data-wmn-because-card', '1');
      grid.appendChild(card);
      added += 1;
    }
    becauseHydratePageImages(grid, needHydrate);
    try {
      var pageFp =
        (root.getAttribute('data-wmn-because-seed') || '') +
        '@' +
        String(state.pageIndex || 0) +
        ':' +
        Array.prototype.slice
          .call(grid.querySelectorAll('[data-wmn-because-card]'))
          .map(function (el) {
            return el.getAttribute('data-wmn-handle') || '';
          })
          .join('|');
      if (root && pageFp) root.setAttribute('data-wmn-because-fp', pageFp);
    } catch (eFp) {
      /* ignore */
    }
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        try {
          grid.style.minHeight = '';
        } catch (eClr) {
          /* ignore */
        }
      });
    }
    return added;
  }

  function becauseBindPageControls(root, grid) {
    if (!root || root._wmnBecauseNavBound) return;
    root._wmnBecauseNavBound = true;
    var prev = root.querySelector('[data-wmn-because-prev]');
    var next = root.querySelector('[data-wmn-because-next]');

    function go(dir) {
      var state = root._wmnBecauseState;
      if (!state || !state.pool || !state.pool.length) return;
      var step = Math.min(BECAUSE_PAGE, state.pool.length);
      state.pageIndex =
        ((state.pageIndex + dir * step) % state.pool.length + state.pool.length) %
        state.pool.length;
      becausePaintPage(root, grid);
      becauseNudgeImages(grid);
    }

    if (prev) {
      prev.addEventListener('click', function (e) {
        e.preventDefault();
        go(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function (e) {
        e.preventDefault();
        go(1);
      });
    }
  }

  /** Human label for "Dig deeper into {genre|country|region}". */
  function becauseLabelForTarget(target) {
    if (!graphReady() || !target || !target.slug) return '';
    var data = window.wmnCatalogGraph.getData() || {};
    var kind = target.kind;
    var slug = target.slug;

    function titleCaseSlug(s) {
      return String(s || '')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, function (c) {
          return c.toUpperCase();
        });
    }

    if (kind === 'genre' && data.genres && data.genres[slug] && data.genres[slug].n) {
      return data.genres[slug].n;
    }
    if (
      kind === 'country' &&
      data.countries &&
      data.countries[slug] &&
      data.countries[slug].n
    ) {
      return data.countries[slug].n;
    }
    if (
      kind === 'region' &&
      data.regions &&
      data.regions[slug] &&
      data.regions[slug].n
    ) {
      return data.regions[slug].n;
    }
    return titleCaseSlug(slug);
  }

  function becauseDigDeeperLabel(seedKey) {
    return becauseLabelForTarget(becauseDigDeeperTarget(seedKey));
  }

  function hideBecauseRail(root, grid) {
    if (grid) {
      try {
        grid.style.minHeight = '';
      } catch (eMin) {
        /* ignore */
      }
      grid.innerHTML = '';
    }
    if (root) {
      root.setAttribute('hidden', 'hidden');
      root.classList.add('hidden');
      root.removeAttribute('data-wmn-because-live');
      root.removeAttribute('data-wmn-because-seed');
      root.removeAttribute('data-wmn-because-fp');
      root._wmnBecauseState = null;
    }
  }

  function renderBecauseYouLookedRail() {
    var root =
      document.getElementById('wmn-because-you-looked') ||
      document.querySelector('[data-wmn-because]');
    var grid =
      document.getElementById('wmn-because-grid') ||
      (root && root.querySelector('[data-wmn-because-grid]'));
    if (!root || !grid) return;

    function paint() {
      renderBecauseYouLookedPaint(root, grid);
    }

    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.ensureLoaded === 'function' &&
        !graphReady()
      ) {
        window.wmnCatalogGraph.ensureLoaded().then(paint).catch(function () {
          hideBecauseRail(root, grid);
        });
        return;
      }
    } catch (e) {
      /* fall through */
    }
    paint();
  }

  /**
   * Expand + rank Because candidates for a seed (no DOM). Used by paint + smoke.
   * @returns {{ seedKey: string, picks: Array<{handle:string,item:object,score:number}>, exclude: object }|null}
   */
  function becausePicksForSeed(seedInput) {
    if (!seedInput || !graphReady()) return null;

    var seed = String(seedInput);
    var seedKey = normalizeRecentlyViewedHandle(seed) || String(seed).trim();
    var resolved = window.wmnCatalogGraph.resolveProduct(seedKey) || {};
    if (
      !(resolved.artists || []).length &&
      !(resolved.genres || []).length &&
      !(resolved.countries || []).length
    ) {
      var seedNorm = normalizeBecauseHandleKey(seedKey);
      if (seedNorm) {
        var altResolved = window.wmnCatalogGraph.resolveProduct(seedNorm) || {};
        if (
          (altResolved.artists || []).length ||
          (altResolved.genres || []).length ||
          (altResolved.countries || []).length
        ) {
          seedKey = seedNorm;
          resolved = altResolved;
        } else {
          altResolved = window.wmnCatalogGraph.resolveProduct('the-' + seedNorm) || {};
          if (
            (altResolved.artists || []).length ||
            (altResolved.genres || []).length ||
            (altResolved.countries || []).length
          ) {
            seedKey = 'the-' + seedNorm;
            resolved = altResolved;
          }
        }
      }
    }

    function digTargetFromSeedOnly() {
      var g0 = (resolved.genres && resolved.genres[0]) || '';
      if (g0) return { kind: 'genre', slug: String(g0) };
      var c0 = (resolved.countries && resolved.countries[0]) || '';
      if (c0) return { kind: 'country', slug: String(c0) };
      var r0 = (resolved.regions && resolved.regions[0]) || '';
      if (r0) return { kind: 'region', slug: String(r0) };
      return null;
    }

    function buildForDigTarget(digTarget) {
      var expandIds = [];
      var i;
      var neighborSlugs = becauseDigNeighborSlugs(digTarget);
      // Expand the dig target entity first so the pool matches the title.
      if (digTarget && digTarget.slug) {
        if (digTarget.kind === 'genre') {
          expandIds.push('genre:' + digTarget.slug);
        } else if (digTarget.kind === 'country') {
          expandIds.push('country:' + digTarget.slug);
        } else if (digTarget.kind === 'region') {
          expandIds.push('region:' + digTarget.slug);
        }
      }
      // Neighbor genres (siblings / region bridges) for soft-fill candidates.
      for (i = 0; i < neighborSlugs.length; i++) {
        expandIds.push('genre:' + neighborSlugs[i]);
      }
      if (!expandIds.length) {
        for (i = 0; i < (resolved.artists || []).length; i++) {
          expandIds.push('artist:' + resolved.artists[i]);
        }
        for (i = 0; i < (resolved.genres || []).length; i++) {
          expandIds.push('genre:' + resolved.genres[i]);
        }
        for (i = 0; i < (resolved.countries || []).length; i++) {
          expandIds.push('country:' + resolved.countries[i]);
        }
      }
      if (!expandIds.length) return null;

      var candidates = window.wmnCatalogGraph.expand(expandIds, 2) || [];
      var exclude = becauseExcludeSet(seedKey);
      if (seed && seed !== seedKey) {
        becauseMergeExclude(exclude, becauseExcludeSet(seed));
      }

      function becauseMatchesDigTargetPure(handle) {
        if (!digTarget || !digTarget.slug) return true;
        if (digTarget.kind === 'genre') {
          return becauseProductMatchesGenre(handle, digTarget.slug);
        }
        if (digTarget.kind === 'country') {
          return becauseProductMatchesCountry(handle, digTarget.slug);
        }
        if (digTarget.kind === 'region') {
          return becauseProductMatchesRegion(handle, digTarget.slug);
        }
        return true;
      }

      function becauseMatchesDigTargetSoft(handle) {
        if (!digTarget || !digTarget.slug) return false;
        if (!neighborSlugs.length) return false;
        return becauseProductMatchesNeighborGenre(handle, neighborSlugs);
      }

      var pureRanked = [];
      var softRanked = [];
      for (i = 0; i < candidates.length; i++) {
        var h = candidates[i];
        if (!h) continue;
        var item = graphItemFromHandle(h);
        if (!item) continue;
        if (becauseIsExcluded(h, item, exclude)) continue;
        var row = {
          handle: h,
          item: item,
          score: becauseRankScore(h, resolved)
        };
        if (becauseMatchesDigTargetPure(h)) {
          pureRanked.push(row);
        } else if (becauseMatchesDigTargetSoft(h)) {
          // Soft fills rank below pure (same genre affinity / region bridges).
          softRanked.push({
            handle: row.handle,
            item: row.item,
            score: row.score - 2500
          });
        }
      }
      function byScoreThenHandle(a, b) {
        if (b.score !== a.score) return b.score - a.score;
        return String(a.handle).localeCompare(String(b.handle));
      }
      pureRanked.sort(byScoreThenHandle);
      softRanked.sort(byScoreThenHandle);

      var picks = [];
      var seenPick = {};
      function pushPick(row) {
        if (!row || !row.handle || seenPick[row.handle]) return;
        if (becauseIsExcluded(row.handle, row.item, exclude)) return;
        seenPick[row.handle] = true;
        picks.push(row);
      }
      for (i = 0; i < pureRanked.length && picks.length < BECAUSE_CAP; i++) {
        pushPick(pureRanked[i]);
      }
      // Country/region digs may soft-fill from geo genre bridges. Genre digs
      // stay pure-only — padding "Salsa" with tango/reggae lied to the title.
      if (!digTarget || digTarget.kind !== 'genre') {
        for (
          i = 0;
          i < softRanked.length && picks.length < BECAUSE_PAGE;
          i++
        ) {
          pushPick(softRanked[i]);
        }
      }

      return {
        seedKey: seedKey,
        picks: picks,
        exclude: exclude,
        resolved: resolved,
        digTarget: digTarget
      };
    }

    var digTarget = becauseDigDeeperTarget(seedKey);
    var built = buildForDigTarget(digTarget);
    // Thin country/region pools (e.g. Japan with 1–3 gated SKUs) leave Dig
    // deeper looking empty. Prefer the seed product's genre when that yields
    // a fuller rail — title follows built.digTarget so we never show Latin
    // under a Japan heading.
    var DIG_GEO_MIN = 4;
    if (
      digTarget &&
      (digTarget.kind === 'country' || digTarget.kind === 'region')
    ) {
      var geoCount = built && built.picks ? built.picks.length : 0;
      if (geoCount < DIG_GEO_MIN) {
        var seedGenre0 =
          (resolved.genres && resolved.genres[0]) || '';
        if (
          seedGenre0 &&
          (digTarget.kind !== 'genre' || digTarget.slug !== seedGenre0)
        ) {
          var genreBuilt = buildForDigTarget({
            kind: 'genre',
            slug: String(seedGenre0)
          });
          var genreCount =
            genreBuilt && genreBuilt.picks ? genreBuilt.picks.length : 0;
          if (genreCount > geoCount) {
            built = genreBuilt;
          }
        }
      }
    }
    // Empty gated pool (e.g. stale geo focus with zero matching SKUs) —
    // fall back to the seed's own genre/country so Dig deeper still appears.
    if (!built || !built.picks || !built.picks.length) {
      var seedTarget = digTargetFromSeedOnly();
      if (
        seedTarget &&
        (!digTarget ||
          digTarget.kind !== seedTarget.kind ||
          digTarget.slug !== seedTarget.slug)
      ) {
        built = buildForDigTarget(seedTarget);
      }
    }
    return built;
  }

  function renderBecauseYouLookedPaint(root, grid) {
    var seed = becauseSeedHandle();
    if (!seed || !graphReady()) {
      hideBecauseRail(root, grid);
      return;
    }

    var built = becausePicksForSeed(seed);
    if (!built || !built.picks || !built.picks.length) {
      hideBecauseRail(root, grid);
      return;
    }

    var seedKey = built.seedKey;
    var picks = built.picks;
    var exclude = built.exclude;

    var digLabel = '';
    if (built.digTarget && built.digTarget.slug) {
      digLabel = becauseLabelForTarget(built.digTarget);
    }
    if (!digLabel) digLabel = becauseDigDeeperLabel(seedKey);
    var titleText = digLabel
      ? 'Dig deeper into ' + digLabel
      : 'Dig deeper';
    var titleEl =
      root.querySelector('[data-wmn-because-title]') ||
      root.querySelector('.wmn-section-head__title');
    if (titleEl) {
      titleEl.textContent = titleText;
    }
    grid.setAttribute('aria-label', titleText);

    // Stable pool for Back/Next paging (wraps forever)
    var pool = [];
    var i;
    var staffFyBlock = getStaffPicksAffinityHandle();
    for (i = 0; i < picks.length; i++) {
      if (becauseIsExcluded(picks[i].handle, picks[i].item, exclude)) continue;
      if (staffFyBlock && picks[i].handle === staffFyBlock) continue;
      pool.push(picks[i]);
    }
    if (!pool.length) {
      hideBecauseRail(root, grid);
      return;
    }

    root._wmnBecauseState = { pool: pool, pageIndex: 0 };
    var pageFp =
      seedKey +
      '@0:' +
      pool
        .slice(0, BECAUSE_PAGE)
        .map(function (p) {
          return (p && p.handle) || '';
        })
        .join('|');
    // Live-refresh often re-paints identical Dig deeper content — wiping the
    // grid collapses height and fights sticky-header scroll anchoring.
    if (
      root.getAttribute('data-wmn-because-fp') === pageFp &&
      grid.querySelector('[data-wmn-because-card]')
    ) {
      root.removeAttribute('hidden');
      root.classList.remove('hidden');
      // Still nudge — Safari may have painted blank covers under [hidden].
      becausePatchCardImages(grid);
      becauseNudgeImages(grid);
      return;
    }

    root.setAttribute('data-wmn-because-seed', seedKey);
    // Reveal before paint so WebKit fetches covers (hidden subtree skips loads).
    root.removeAttribute('hidden');
    root.classList.remove('hidden');
    becausePaintPage(root, grid);

    if (!grid.querySelector('[data-wmn-because-card]')) {
      hideBecauseRail(root, grid);
      return;
    }

    becauseBindPageControls(root, grid);
    becauseNudgeImages(grid);

    root.setAttribute('data-wmn-because-live', String(Date.now()));
    root.setAttribute('data-wmn-because-fp', pageFp);

    try {
      if (
        window.wmnIntentAnalytics &&
        typeof window.wmnIntentAnalytics.log === 'function'
      ) {
        window.wmnIntentAnalytics.log({
          type: 'because_rail_shown',
          handle: seedKey,
          n: pool.length
        });
      }
    } catch (errShown) {
      /* optional */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Phase 3 — PDP related stack (More from artist + genre top-up)      */
  /* ------------------------------------------------------------------ */

  var PDP_ARTIST_CAP = 8;
  var PDP_ARTIST_DEPTH = 0;

  /**
   * Light exclude for PDP rails: current handle + cover-fingerprint twins.
   * Reuses becauseImageKey; does not block RV / For You (unlike Because).
   */
  function pdpExcludeSet(seedHandle) {
    var handles = {};
    var images = {};

    function blockHandle(h) {
      if (!h) return;
      var raw =
        normalizeRecentlyViewedHandle(h) ||
        String(h).replace(/^\s+|\s+$/g, '');
      if (!raw) return;
      handles[raw] = true;
      handles[String(h)] = true;
      var n = normalizeBecauseHandleKey(raw);
      if (n) {
        handles[n] = true;
        handles['the-' + n] = true;
      }
    }

    function blockImage(url) {
      var key = becauseImageKey(url);
      if (key) images[key] = true;
    }

    blockHandle(seedHandle);

    try {
      if (graphReady() && seedHandle) {
        var data = window.wmnCatalogGraph.getData();
        var seedNorm = normalizeBecauseHandleKey(seedHandle);
        var rawP =
          (data && data.products && data.products[seedHandle]) ||
          (data && data.products && seedNorm && data.products[seedNorm]) ||
          (data &&
            data.products &&
            seedNorm &&
            data.products['the-' + seedNorm]);
        if (rawP && rawP.img) {
          blockImage(rawP.img);
          var seedImgKey = becauseImageKey(rawP.img);
          if (seedImgKey && data.products) {
            var ph;
            for (ph in data.products) {
              if (!Object.prototype.hasOwnProperty.call(data.products, ph)) {
                continue;
              }
              var twin = data.products[ph];
              if (!twin || !twin.img) continue;
              if (becauseImageKey(twin.img) !== seedImgKey) continue;
              blockHandle(ph);
              blockImage(twin.img);
            }
          }
        }
        var seedItem = graphItemFromHandle(seedHandle);
        if (seedItem && seedItem.image) blockImage(seedItem.image);
      }
    } catch (eEx) {
      /* ignore */
    }

    return { handles: handles, images: images };
  }

  function pdpIsExcluded(handle, item, excl) {
    if (!handle || !excl) return true;
    if (excl.handles[handle]) return true;
    var n = normalizeBecauseHandleKey(handle);
    if (n && (excl.handles[n] || excl.handles['the-' + n])) return true;
    var image = item && item.image ? item.image : '';
    if (!image && graphReady()) {
      try {
        var data = window.wmnCatalogGraph.getData();
        var raw =
          data && data.products && (data.products[handle] || data.products[n]);
        if (raw && raw.img) image = raw.img;
      } catch (eImg) {
        /* ignore */
      }
    }
    var ik = becauseImageKey(image);
    if (ik && excl.images && excl.images[ik]) return true;
    return false;
  }

  function pdpArtistColumnClass(index) {
    return (
      'gallery-cell thumbnail visible-4 wmn-more-from-artist-card' +
      (index % 2 === 0 ? ' even' : ' odd')
    );
  }

  function pdpGenreColumnClass(index) {
    var cycle = index % 4;
    var pos = cycle === 0 ? 'alpha' : cycle === 3 ? 'omega' : '';
    return (
      'thumbnail four columns wmn-related-genre-card' +
      (pos ? ' ' + pos : '') +
      (index % 2 === 0 ? ' even' : ' odd')
    );
  }

  function hideMoreFromArtistRail(root, grid) {
    if (grid) {
      try {
        if (
          window.jQuery &&
          jQuery.fn.flickity &&
          jQuery(grid).hasClass('flickity-enabled')
        ) {
          jQuery(grid).flickity('destroy');
        }
      } catch (eFl) {
        /* ignore */
      }
      grid.innerHTML = '';
    }
    if (root) {
      root.setAttribute('hidden', 'hidden');
      root.classList.add('hidden');
      root.removeAttribute('data-wmn-artist-live');
      root.removeAttribute('data-wmn-artist-fp');
    }
  }

  function settleMoreFromArtistSlider($slider, grid) {
    if (!$slider || !$slider.length) return;
    function settle() {
      try {
        if (!$slider.hasClass('flickity-enabled')) return;
        $slider.flickity('reloadCells');
        $slider.flickity('resize');
      } catch (eSettle) {
        /* ignore */
      }
    }
    // Layout after paint + after images (lazy/async CDN) settle.
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        settle();
        window.requestAnimationFrame(settle);
      });
    } else {
      settle();
    }
    var imgs = grid ? grid.querySelectorAll('img') : [];
    var i;
    for (i = 0; i < imgs.length; i++) {
      if (imgs[i].complete) continue;
      imgs[i].addEventListener('load', settle, { once: true });
      imgs[i].addEventListener('error', settle, { once: true });
    }
    window.setTimeout(settle, 300);
    window.setTimeout(settle, 1000);
  }

  function initMoreFromArtistSlider(grid, count) {
    if (!grid || !count) return;
    // Match theme products-slider: length class = cell count for CSS centering.
    grid.setAttribute('data-products-available', String(count));
    grid.setAttribute('data-products-limit', String(Math.max(count, 1)));
    grid.className = grid.className
      .replace(/\bproducts-length-\d+\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    grid.classList.add('products-length-' + count);

    if (!window.jQuery || !jQuery.fn.flickity) return;
    var $slider = jQuery(grid);
    try {
      if ($slider.hasClass('flickity-enabled')) {
        $slider.flickity('destroy');
      }
    } catch (eDes) {
      /* ignore */
    }

    var perSlide = parseInt($slider.attr('data-products-per-slide'), 10) || 4;
    var available = count;
    var limit = parseInt($slider.attr('data-products-limit'), 10) || available;
    // Mirror app.js relatedProducts / featuredCollection options.
    var cellAlign =
      available > perSlide && limit > perSlide ? 'left' : 'center';
    var draggable = available > perSlide && limit > perSlide;
    var prevNext = draggable;
    var wrapAround = draggable;
    var initialIndex = 0;

    if (jQuery(window).width() <= 798) {
      cellAlign = 'center';
      draggable = true;
      prevNext = true;
      wrapAround = available > 1;
      initialIndex = available > 1 ? 1 : 0;
    }

    var arrowShape =
      typeof window.arrowSize !== 'undefined'
        ? window.arrowSize
        : { x0: 10, x1: 60, y1: 50, x2: 70, y2: 40, x3: 30 };

    try {
      $slider.flickity({
        lazyLoad: 2,
        imagesLoaded: true,
        draggable: draggable,
        cellAlign: cellAlign,
        prevNextButtons: prevNext,
        pageDots: false,
        wrapAround: wrapAround,
        contain: true,
        freeScroll: true,
        arrowShape: arrowShape,
        initialIndex: initialIndex
      });
      $slider.addClass('slider-initialized');
      settleMoreFromArtistSlider($slider, grid);
    } catch (eInit) {
      /* ignore */
    }
  }

  function moreFromArtistPicks(artistSlug, excludeHandle, limit) {
    if (!artistSlug || !graphReady()) return [];
    var cap = limit || PDP_ARTIST_CAP;
    var candidates =
      window.wmnCatalogGraph.expand(
        ['artist:' + artistSlug],
        PDP_ARTIST_DEPTH
      ) || [];
    var exclude = pdpExcludeSet(excludeHandle);
    var picks = [];
    var i;
    for (i = 0; i < candidates.length && picks.length < cap; i++) {
      var h = candidates[i];
      if (!h) continue;
      var item = graphItemFromHandle(h);
      if (!item) continue;
      if (pdpIsExcluded(h, item, exclude)) continue;
      // Strict: only this artist (title says More from {Artist}).
      var resolved = window.wmnCatalogGraph.resolveProduct(h) || {};
      var artists = resolved.artists || [];
      if (artists.indexOf(artistSlug) === -1) continue;
      picks.push({ handle: h, item: item });
    }
    return picks;
  }

  function renderMoreFromArtistRail() {
    var root = document.querySelector('[data-wmn-more-from-artist]');
    var grid =
      document.getElementById('wmn-more-from-artist-grid') ||
      (root && root.querySelector('[data-wmn-more-from-artist-grid]'));
    if (!root || !grid) return;

    function paint() {
      renderMoreFromArtistPaint(root, grid);
    }

    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.ensureLoaded === 'function' &&
        !graphReady()
      ) {
        window.wmnCatalogGraph
          .ensureLoaded()
          .then(paint)
          .catch(function () {
            hideMoreFromArtistRail(root, grid);
          });
        return;
      }
    } catch (e) {
      /* fall through */
    }
    paint();
  }

  function renderMoreFromArtistPaint(root, grid) {
    var artistSlug = root.getAttribute('data-wmn-artist-slug') || '';
    var excludeHandle = root.getAttribute('data-wmn-exclude-handle') || '';
    var limit =
      parseInt(root.getAttribute('data-wmn-limit'), 10) || PDP_ARTIST_CAP;

    if (!artistSlug || !graphReady()) {
      hideMoreFromArtistRail(root, grid);
      return;
    }

    // Confirm artist exists in graph (avoid empty expand of unknown slug).
    try {
      var data = window.wmnCatalogGraph.getData();
      if (!data || !data.artists || !data.artists[artistSlug]) {
        hideMoreFromArtistRail(root, grid);
        return;
      }
    } catch (eArt) {
      hideMoreFromArtistRail(root, grid);
      return;
    }

    var picks = moreFromArtistPicks(artistSlug, excludeHandle, limit);
    if (!picks.length) {
      hideMoreFromArtistRail(root, grid);
      return;
    }

    // Avoid destroy/rebuild mid-scroll on affinity/live-refresh repaints.
    var fingerprint = picks
      .map(function (p) {
        return p.handle;
      })
      .join(',');
    if (
      root.getAttribute('data-wmn-artist-fp') === fingerprint &&
      grid.querySelector('[data-wmn-more-from-artist-card]') &&
      window.jQuery &&
      jQuery(grid).hasClass('flickity-enabled')
    ) {
      return;
    }

    try {
      if (
        window.jQuery &&
        jQuery.fn.flickity &&
        jQuery(grid).hasClass('flickity-enabled')
      ) {
        jQuery(grid).flickity('destroy');
      }
    } catch (eDes2) {
      /* ignore */
    }

    grid.innerHTML = '';
    var i;
    for (i = 0; i < picks.length; i++) {
      var pick = picks[i];
      var ev = {
        h: pick.handle,
        title: pick.item.title || pick.handle,
        img: pick.item.image || '',
        price: '',
        tags: []
      };
      var card = buildSearchMemoryCard(ev, pdpArtistColumnClass(i));
      if (!card) continue;
      card.className = pdpArtistColumnClass(i);
      card.removeAttribute('data-wmn-injected');
      card.setAttribute('data-wmn-handle', pick.handle);
      card.setAttribute('data-wmn-title', ev.title);
      card.setAttribute('data-wmn-more-from-artist-card', '1');
      // Eager images so Flickity imagesLoaded + cell size settle before drag.
      var imgEl = card.querySelector('img');
      if (imgEl) {
        imgEl.loading = 'eager';
        imgEl.removeAttribute('loading');
        imgEl.setAttribute('loading', 'eager');
        imgEl.setAttribute('decoding', 'async');
      }
      grid.appendChild(card);
    }

    if (!grid.querySelector('[data-wmn-more-from-artist-card]')) {
      hideMoreFromArtistRail(root, grid);
      return;
    }

    root.removeAttribute('hidden');
    root.classList.remove('hidden');
    root.setAttribute('data-wmn-artist-live', String(Date.now()));
    root.setAttribute('data-wmn-artist-fp', fingerprint);
    initMoreFromArtistSlider(grid, picks.length);
  }

  function enrichRelatedByGenreRail() {
    var root = document.querySelector('[data-wmn-related-by-genre]');
    var grid =
      root && root.querySelector('[data-wmn-related-by-genre-grid]');
    if (!root || !grid) return;

    function paint() {
      enrichRelatedByGenrePaint(root, grid);
    }

    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.ensureLoaded === 'function' &&
        !graphReady()
      ) {
        window.wmnCatalogGraph.ensureLoaded().then(paint).catch(function () {});
        return;
      }
    } catch (e) {
      /* fall through */
    }
    paint();
  }

  function enrichRelatedByGenrePaint(root, grid) {
    if (!graphReady()) return;

    var genreSlug = root.getAttribute('data-wmn-genre-slug') || '';
    var excludeHandle = root.getAttribute('data-wmn-exclude-handle') || '';
    var limit = parseInt(root.getAttribute('data-wmn-limit'), 10) || 4;
    var matchedCount =
      parseInt(root.getAttribute('data-wmn-matched-count'), 10) || 0;
    if (!genreSlug || matchedCount >= limit) return;

    var need = limit - matchedCount;
    if (need <= 0) return;

    var existing = {};
    var existingNodes = grid.querySelectorAll('[data-wmn-handle]');
    var ei;
    for (ei = 0; ei < existingNodes.length; ei++) {
      var eh = existingNodes[ei].getAttribute('data-wmn-handle');
      if (eh) existing[eh] = true;
    }
    // Liquid thumbnails may lack data-wmn-handle — also parse matched-handles attr.
    var matchedAttr = root.getAttribute('data-wmn-matched-handles') || '';
    var parts = matchedAttr.split('|');
    for (ei = 0; ei < parts.length; ei++) {
      if (parts[ei]) existing[parts[ei]] = true;
    }
    // Fallback: scrape product links already in the grid.
    var links = grid.querySelectorAll('a[href*="/products/"]');
    for (ei = 0; ei < links.length; ei++) {
      var href = links[ei].getAttribute('href') || '';
      var m = href.match(/\/products\/([^/?#]+)/);
      if (m && m[1]) {
        try {
          existing[decodeURIComponent(m[1])] = true;
        } catch (eDec) {
          existing[m[1]] = true;
        }
      }
    }

    var expandIds = ['genre:' + genreSlug];
    try {
      if (typeof window.wmnCatalogGraph.relatedGenres === 'function') {
        var related = window.wmnCatalogGraph.relatedGenres(genreSlug) || [];
        var ri;
        for (ri = 0; ri < related.length; ri++) {
          if (related[ri] && related[ri].slug) {
            expandIds.push('genre:' + related[ri].slug);
          }
        }
      }
    } catch (eRel) {
      /* ignore */
    }

    var candidates = window.wmnCatalogGraph.expand(expandIds, 1) || [];
    var exclude = pdpExcludeSet(excludeHandle);
    var primary = [];
    var secondary = [];
    var i;
    for (i = 0; i < candidates.length; i++) {
      var h = candidates[i];
      if (!h || existing[h]) continue;
      var item = graphItemFromHandle(h);
      if (!item) continue;
      if (pdpIsExcluded(h, item, exclude)) continue;
      var resolved = window.wmnCatalogGraph.resolveProduct(h) || {};
      var genres = resolved.genres || [];
      var row = { handle: h, item: item };
      if (genres.indexOf(genreSlug) !== -1) {
        primary.push(row);
      } else {
        secondary.push(row);
      }
    }

    var picks = [];
    for (i = 0; i < primary.length && picks.length < need; i++) {
      picks.push(primary[i]);
    }
    for (i = 0; i < secondary.length && picks.length < need; i++) {
      picks.push(secondary[i]);
    }
    if (!picks.length) return;

    var startIndex = matchedCount;
    for (i = 0; i < picks.length; i++) {
      var pick = picks[i];
      if (existing[pick.handle]) continue;
      existing[pick.handle] = true;
      var ev = {
        h: pick.handle,
        title: pick.item.title || pick.handle,
        img: pick.item.image || '',
        price: '',
        tags: []
      };
      var card = buildSearchMemoryCard(
        ev,
        pdpGenreColumnClass(startIndex + i)
      );
      if (!card) continue;
      card.className = pdpGenreColumnClass(startIndex + i);
      card.removeAttribute('data-wmn-injected');
      card.setAttribute('data-wmn-handle', pick.handle);
      card.setAttribute('data-wmn-title', ev.title);
      card.setAttribute('data-wmn-genre-topup', '1');
      grid.appendChild(card);
    }

    root.setAttribute(
      'data-wmn-matched-count',
      String(
        grid.querySelectorAll(
          '[data-wmn-handle], a[href*="/products/"]'
        ).length
      )
    );
    root.setAttribute('data-wmn-genre-enriched', String(Date.now()));
  }

  function renderPdpRelatedRails() {
    renderMoreFromArtistRail();
    enrichRelatedByGenreRail();
  }

  function renderForYouRail() {
    var root = document.getElementById('wmn-for-you');
    var grid = document.getElementById('wmn-for-you-grid');
    if (!root || !grid) return;

    // Phase 2: endless feed owns homepage — never wipe mid-session.
    if (forYouFeedOwns(root)) {
      try {
        if (window.wmnForYouFeed && typeof window.wmnForYouFeed.ensure === 'function') {
          window.wmnForYouFeed.ensure();
        }
      } catch (eFeed) {
        /* ignore */
      }
      return;
    }

    function paint() {
      renderForYouRailPaint(root, grid);
    }

    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.ensureLoaded === 'function' &&
        !graphReady()
      ) {
        window.wmnCatalogGraph.ensureLoaded().then(paint).catch(paint);
        return;
      }
    } catch (e) {
      /* fall through */
    }
    paint();
  }

  function renderForYouRailPaint(root, grid) {
    var built = buildForYouPicks(18);
    if (built && built.picks && built.picks.length >= 3) {
      var picks = built.picks;
      var limitG =
        picks.length >= 9 ? 9 : picks.length >= 6 ? 6 : picks.length >= 3 ? 3 : picks.length;
      var classNameG = 'wmn-for-you-card thumbnail';
      grid.innerHTML = '';
      grid.className = 'product-list clearfix wmn-for-you-grid';
      picks.slice(0, limitG).forEach(function (item) {
        var ev = {
          h: item.handle,
          title: item.title || item.handle,
          img: item.image || '',
          price: '',
          tags: []
        };
        var card = buildSearchMemoryCard(ev, classNameG);
        if (!card) return;
        card.className = classNameG;
        card.removeAttribute('data-wmn-injected');
        card.setAttribute('data-wmn-handle', item.handle);
        card.setAttribute('data-wmn-title', ev.title);
        grid.appendChild(card);
      });
      var subtitleG = root.querySelector('.wmn-for-you__subtitle');
      if (subtitleG && built.labels && built.labels.length) {
        subtitleG.textContent =
          'Based on your interest in ' + built.labels.join(' & ');
      }
      root.removeAttribute('hidden');
      root.setAttribute('data-wmn-for-you-live', String(Date.now()));
      return;
    }

    var affinity = getAffinityCompat();
    var tops = topGenres(affinity, 2);

    if (!tops.length || tops[0].w < 4) {
      // Soft show: if we have events but weak genre weight, keep prior grid if any
      if (!grid.children.length) root.setAttribute('hidden', 'hidden');
      return;
    }

    var existingFallback = {};
    store.events.forEach(function (ev) {
      if (ev && ev.h) existingFallback[ev.h] = true;
    });

    var term = tops[0].g;
    var url =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/') +
      'search?type=product&q=' +
      encodeURIComponent(term) +
      '*&view=json';

    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('for-you fetch');
        return r.json();
      })
      .then(function (data) {
        var results = (data && data.results) || [];
        var picks = [];
        var seen = {};
        for (var i = 0; i < results.length && picks.length < 18; i++) {
          var item = results[i];
          if (!item || item.object_type !== 'product') continue;
          var handle = productHandleFromHref(item.url) || '';
          if (!handle || seen[handle]) continue;
          seen[handle] = true;
          var title = String(item.title || '').toLowerCase();
          if (title.indexOf('gift voucher') !== -1 || title.indexOf('gift card') !== -1) {
            continue;
          }
          picks.push(item);
        }
        if (!picks.length) {
          root.setAttribute('hidden', 'hidden');
          return;
        }

        picks.sort(function (a, b) {
          var ha = productHandleFromHref(a.url);
          var hb = productHandleFromHref(b.url);
          var sa =
            scoreByHandleTitleGenres(ha, a.title || '', [], affinity) + scoreHandle(ha);
          var sb =
            scoreByHandleTitleGenres(hb, b.title || '', [], affinity) + scoreHandle(hb);
          return sb - sa;
        });

        var limit = picks.length >= 9 ? 9 : picks.length >= 6 ? 6 : picks.length >= 3 ? 3 : picks.length;
        var className = 'wmn-for-you-card thumbnail';

        grid.innerHTML = '';
        grid.className = 'product-list clearfix wmn-for-you-grid';
        picks.slice(0, limit).forEach(function (item) {
          var handle = productHandleFromHref(item.url);
          var thumb =
            (item.image && item.image !== 'NULL' ? item.image : '') ||
            (item.thumbnail && item.thumbnail !== 'NULL' ? item.thumbnail : '');
          var ev = {
            h: handle,
            title: item.title || handle,
            img: withShopifyImageWidth(thumb, 1200),
            price: item.price || '',
            tags: item.tags || []
          };
          var card = buildSearchMemoryCard(ev, className);
          if (!card) return;
          card.className = className;
          card.removeAttribute('data-wmn-injected');
          card.setAttribute('data-wmn-handle', handle);
          card.setAttribute('data-wmn-title', ev.title);
          grid.appendChild(card);
        });

        var subtitle = root.querySelector('.wmn-for-you__subtitle');
        if (subtitle) {
          subtitle.textContent =
            'Based on your interest in ' +
            tops
              .map(function (t) {
                return t.g.replace(/\b\w/g, function (c) {
                  return c.toUpperCase();
                });
              })
              .join(' & ');
        }
        root.removeAttribute('hidden');
        root.setAttribute('data-wmn-for-you-live', String(Date.now()));
      })
      .catch(function () {
        root.setAttribute('hidden', 'hidden');
      });
  }

  function migrateAnonymousToCustomer() {
    var key = storageKey();
    if (key === BASE_KEY) return;
    try {
      var anon = parseStore(window.localStorage.getItem(BASE_KEY));
      if (
        (anon.events && anon.events.length) ||
        (anon.entities && Object.keys(anon.entities).length)
      ) {
        writeStore(mergeStores(readStore(), anon));
      }
    } catch (e) {
      /* ignore */
    }
    // Bring anon durable focus/uEmb under the customer-scoped interest key.
    try {
      var custInterestKey = interestProfileKey();
      if (custInterestKey === INTEREST_KEY) return;
      var anonRaw = window.localStorage.getItem(INTEREST_KEY);
      if (!anonRaw) return;
      var anonProf = JSON.parse(anonRaw);
      if (!anonProf || typeof anonProf !== 'object') return;
      var custRaw = window.localStorage.getItem(custInterestKey);
      var custProf = null;
      if (custRaw) {
        try {
          custProf = JSON.parse(custRaw);
        } catch (eParse) {
          custProf = null;
        }
      }
      var anonT = (anonProf && anonProf.t) || 0;
      var custT = (custProf && custProf.t) || 0;
      if (!custProf || anonT >= custT) {
        var mergedInterest = {
          v: 1,
          t: Math.max(anonT, custT, now()),
          focus:
            (anonProf && anonProf.focus) ||
            (custProf && custProf.focus) ||
            null,
          uEmb:
            (anonProf && anonProf.uEmb) ||
            (custProf && custProf.uEmb) ||
            null
        };
        // Prefer newer focus object by its own t when both exist.
        if (
          anonProf &&
          anonProf.focus &&
          custProf &&
          custProf.focus &&
          (custProf.focus.t || 0) > (anonProf.focus.t || 0)
        ) {
          mergedInterest.focus = custProf.focus;
        }
        if (
          anonProf &&
          anonProf.uEmb &&
          custProf &&
          custProf.uEmb &&
          (custProf.uEmb.t || 0) > (anonProf.uEmb.t || 0)
        ) {
          mergedInterest.uEmb = custProf.uEmb;
        }
        window.localStorage.setItem(
          custInterestKey,
          JSON.stringify(mergedInterest)
        );
      }
    } catch (e2) {
      /* ignore */
    }
  }

  function eventToDiscoverItem(ev) {
    if (!ev || !ev.h) return null;
    var title = ev.title || ev.h;
    var titleL = String(title).toLowerCase();
    if (titleL.indexOf('gift voucher') !== -1 || titleL.indexOf('gift card') !== -1) {
      return null;
    }
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    var img = withShopifyImageWidth(ev.img || '', 400) || null;
    return {
      handle: ev.h,
      title: title,
      artist: '',
      url: root + 'products/' + ev.h,
      image: img || null,
      genres: Array.isArray(ev.g) ? ev.g : []
    };
  }

  /**
   * Intent-conditioned For you grid:
   * Continue (recent) → Related artists → Genre lane → Exploit prefs → Explore bestsellers
   * with MMR artist diversity. Session focus boosts the active lane.
   */
  function rankByAffinity(items) {
    var limit = DISCOVER_LIMIT;
    var seed = items || [];
    ensureGraphWarm();

    // Align with homepage Picked for you when country/region prefs exist:
    // same expand + pin composition (not intent-field bestseller reorder).
    if (hasGeoForYouPrefs()) {
      try {
        var geoBuilt = buildForYouPicks(limit);
        if (geoBuilt && geoBuilt.picks && geoBuilt.picks.length >= 3) {
          return geoBuilt.picks.slice(0, limit);
        }
      } catch (geoErr) {
        /* fall through */
      }
    }

    // Intent field ranks whenever it has energy (explore-after-type OR lean/committed).
    // Skip when geographic prefs should own empty-query For you (handled above).
    if (
      !hasGeoForYouPrefs() &&
      fieldHasRankableEnergy() &&
      window.wmnIntentField &&
      typeof window.wmnIntentField.rankForYou === 'function'
    ) {
      try {
        var fieldState =
          typeof window.wmnIntentField.getField === 'function'
            ? window.wmnIntentField.getField()
            : null;
        var hasFieldNodes =
          fieldState &&
          fieldState.nodes &&
          Object.keys(fieldState.nodes).length > 0;
        if (hasFieldNodes) {
          var fieldRanked = window.wmnIntentField.rankForYou(seed, {
            limit: limit
          });
          if (fieldRanked && fieldRanked.length >= Math.min(8, limit)) {
            return fieldRanked;
          }
        }
      } catch (fieldErr) {
        /* fall through to legacy explore path only */
      }
    }

    // Entity expand (artist/genre/product) — same builder as homepage when possible
    try {
      var built2 = buildForYouPicks(limit);
      if (built2 && built2.picks && built2.picks.length >= Math.min(8, limit)) {
        return built2.picks.slice(0, limit);
      }
    } catch (e2) {
      /* ignore */
    }

    var store = readStore();
    var seen = {};
    var merged = [];
    var byHandle = {};

    function pushItem(item) {
      if (!item || !item.handle || seen[item.handle]) return;
      var t = String(item.title || '').toLowerCase();
      if (t.indexOf('gift voucher') !== -1 || t.indexOf('gift card') !== -1) return;
      seen[item.handle] = true;
      merged.push(item);
      byHandle[item.handle] = item;
    }

    if (store.events && store.events.length) {
      for (var i = store.events.length - 1; i >= 0; i--) {
        var ev = store.events[i];
        if (!ev || !ev.h) continue;
        var mapped = eventToDiscoverItem(ev);
        if (mapped) pushItem(mapped);
      }
    }

    for (var s = 0; s < seed.length; s++) {
      var seedItem = seed[s];
      if (!seedItem || !seedItem.handle) continue;
      if (seen[seedItem.handle]) {
        var cur = byHandle[seedItem.handle];
        if (cur) {
          cur.title = cur.title || seedItem.title;
          cur.artist = seedItem.artist || cur.artist || '';
          cur.url = seedItem.url || cur.url;
          cur.image = cur.image || seedItem.image || null;
          cur.genres =
            cur.genres && cur.genres.length ? cur.genres : seedItem.genres || [];
          cur.fromSeed = true;
        }
        continue;
      }
      seedItem.fromSeed = true;
      pushItem(seedItem);
    }

    var focus = readFocus();
    var expandIds = [];
    var focusArtists = (focus && focus.artists) || [];
    var focusGenres = (focus && focus.genres) || [];
    var ti;
    for (ti = 0; ti < focusArtists.length; ti++) {
      expandIds.push('artist:' + focusArtists[ti]);
    }
    for (ti = 0; ti < focusGenres.length; ti++) {
      expandIds.push('genre:' + focusGenres[ti]);
    }
    var topEnts = topEntityIds(store.entities || {}, 10);
    for (ti = 0; ti < topEnts.length; ti++) {
      if (expandIds.indexOf(topEnts[ti]) === -1) expandIds.push(topEnts[ti]);
    }

    if (graphReady() && expandIds.length) {
      var expanded = window.wmnCatalogGraph.expand(expandIds, 2) || [];
      for (var ex = 0; ex < expanded.length; ex++) {
        if (seen[expanded[ex]]) continue;
        var gItem = graphItemFromHandle(expanded[ex]);
        if (gItem) {
          gItem.fromGraph = true;
          pushItem(gItem);
        }
      }
    }

    var hasSignal =
      (store.events && store.events.length) ||
      Object.keys(store.entities || {}).length > 0 ||
      (focus && (focus.artists.length || focus.genres.length || focus.handles.length));
    if (!hasSignal) {
      return merged.slice(0, limit);
    }

    var affinity = getAffinityCompat();
    function baseScore(item) {
      if (!item || !item.handle) return 0;
      return (
        scoreHandle(item.handle) +
        scoreByHandleTitleGenres(
          item.handle,
          item.title || '',
          item.genres || [],
          affinity
        )
      );
    }

    // --- Row composition ---
    var out = [];
    var outSeen = {};

    function take(list) {
      for (var x = 0; x < list.length && out.length < limit; x++) {
        if (!list[x] || !list[x].handle || outSeen[list[x].handle]) continue;
        outSeen[list[x].handle] = true;
        out.push(list[x]);
      }
    }

    // 1) Continue — newest unique views / search clicks (max SLOT_CONTINUE)
    var continueItems = [];
    var pinSeen = {};
    if (store.events && store.events.length) {
      for (
        var pi = store.events.length - 1;
        pi >= 0 && continueItems.length < SLOT_CONTINUE;
        pi--
      ) {
        var pev = store.events[pi];
        if (!pev || !pev.h || pinSeen[pev.h]) continue;
        pinSeen[pev.h] = true;
        var pinned = byHandle[pev.h] || eventToDiscoverItem(pev);
        if (pinned) continueItems.push(pinned);
      }
    }
    take(continueItems);

    // 2) Related — same / related artists (prefer focus artists)
    var relatedPool = [];
    for (var ri = 0; ri < merged.length; ri++) {
      var rit = merged[ri];
      if (!rit || outSeen[rit.handle]) continue;
      var rak = itemArtistKey(rit);
      if (!rak) continue;
      var isFocusArtist =
        focusArtists.indexOf(rak) !== -1 ||
        (focusArtists.length &&
          graphReady() &&
          (function () {
            var node = window.wmnCatalogGraph.getData().artists[focusArtists[0]];
            return node && node.r && node.r.indexOf(rak) !== -1;
          })());
      var isPrefArtist = !!(store.entities && store.entities['artist:' + rak]);
      if (isFocusArtist || isPrefArtist || rit.fromGraph) {
        relatedPool.push(rit);
      }
    }
    take(mmrSelect(relatedPool, SLOT_RELATED, out, baseScore, 1));

    // 3) Genre lane — products matching focus / top genres
    var genreTargets = focusGenres.slice();
    if (!genreTargets.length) {
      var topG = topEntityIds(store.entities || {}, 3, ['genre']);
      for (ti = 0; ti < topG.length; ti++) {
        genreTargets.push(String(topG[ti]).replace(/^genre:/, ''));
      }
    }
    var genrePool = [];
    for (var gi = 0; gi < merged.length; gi++) {
      var git = merged[gi];
      if (!git || outSeen[git.handle]) continue;
      var gks = itemGenreKeys(git);
      var hit = false;
      for (var gk = 0; gk < gks.length; gk++) {
        if (genreTargets.indexOf(gks[gk]) !== -1) {
          hit = true;
          break;
        }
      }
      if (hit) genrePool.push(git);
    }
    take(mmrSelect(genrePool, SLOT_GENRE, out, baseScore, 1));

    // 4) Exploit — remaining high entity-score items (any source) with MMR
    var exploitPool = [];
    for (var ei = 0; ei < merged.length; ei++) {
      if (merged[ei] && !outSeen[merged[ei].handle] && baseScore(merged[ei]) >= 8) {
        exploitPool.push(merged[ei]);
      }
    }
    take(mmrSelect(exploitPool, SLOT_EXPLOIT, out, baseScore, 8));

    // 5) Explore — Best Sellers / seed not already shown (serendipity ~25%)
    var explorePool = [];
    for (var xi = 0; xi < merged.length; xi++) {
      var xit = merged[xi];
      if (!xit || outSeen[xit.handle]) continue;
      if (xit.fromSeed || (!xit.fromGraph && baseScore(xit) < 50)) {
        explorePool.push(xit);
      }
    }
    // Prefer lower personalization for explore (novelty)
    take(
      mmrSelect(
        explorePool,
        SLOT_EXPLORE,
        out,
        function (it) {
          return 40 - Math.min(35, baseScore(it) * 0.05) + (it.fromSeed ? 5 : 0);
        },
        0
      )
    );

    // Fill any remaining slots with next-best MMR from leftovers
    if (out.length < limit) {
      var rest = [];
      for (var yi = 0; yi < merged.length; yi++) {
        if (merged[yi] && !outSeen[merged[yi].handle]) rest.push(merged[yi]);
      }
      take(mmrSelect(rest, limit - out.length, out, baseScore, 0));
    }

    return out.slice(0, limit);
  }

  function collectionMatrixCards(container) {
    var nodes = container.children;
    var out = [];
    var i;
    for (i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (!n.classList || !n.classList.contains('thumbnail')) continue;
      if (!n.getAttribute || !n.getAttribute('data-wmn-handle')) continue;
      out.push(n);
    }
    return out;
  }

  function collectionCardFromEvent(node) {
    if (!node || !node.closest) return null;
    var card = node.closest('.thumbnail[data-wmn-handle]');
    if (!card || !card.closest) return null;
    if (!card.closest('.collection-template-section, .collection-template')) return null;
    return card;
  }

  function collectionCardGenres(card) {
    var raw = (card && card.getAttribute('data-wmn-genres')) || '';
    if (!raw) return [];
    return raw
      .split('|')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function collectionCardTitle(card) {
    if (!card) return '';
    var titled = card.getAttribute('data-wmn-title');
    if (titled) return titled;
    var el = card.querySelector('.title');
    return el ? (el.textContent || '').trim() : '';
  }

  var collectionLive = {
    hoverTimer: 0,
    hoverHandle: '',
    hoverX: 0,
    hoverY: 0,
    hoverStarted: 0,
    dwell: {},
    skip: {},
    exposedAt: {},
    io: null,
    tailTimer: 0
  };

  function markCollectionTail() {
    window.__wmnCollectionTailUntil = Date.now() + 2500;
  }

  function pointerStillOnCollectionCard(handle, x, y) {
    if (!handle) return false;
    try {
      var el = document.elementFromPoint(x, y);
      var card = collectionCardFromEvent(el);
      return !!(card && card.getAttribute('data-wmn-handle') === handle);
    } catch (ePt) {
      return false;
    }
  }

  function clearCollectionHover() {
    if (collectionLive.hoverTimer) {
      clearTimeout(collectionLive.hoverTimer);
      collectionLive.hoverTimer = 0;
    }
    collectionLive.hoverHandle = '';
    window.__wmnCollectionHoverHandle = '';
  }

  function emitCollectionSignal(card, kind, extra) {
    if (!card) return;
    var handle = card.getAttribute('data-wmn-handle');
    if (!handle) return;
    extra = extra || {};
    markCollectionTail();
    if (kind === 'listen') {
      recordView({
        handle: handle,
        title: collectionCardTitle(card),
        genres: collectionCardGenres(card),
        kind: 'listen'
      });
      return;
    }
    if (kind === 'skip') {
      recordFeedSignal({
        kind: 'skip',
        handle: handle,
        title: collectionCardTitle(card),
        source: 'collection-grid',
        amount: extra.amount == null ? 0.28 : extra.amount
      });
      return;
    }
    recordFeedSignal({
      kind: 'hover',
      handle: handle,
      title: collectionCardTitle(card),
      genres: collectionCardGenres(card),
      dwellMs: extra.dwellMs || 450,
      source: 'collection-grid'
    });
  }

  function scheduleCollectionTailReorder() {
    if (collectionLive.tailTimer) clearTimeout(collectionLive.tailTimer);
    collectionLive.tailTimer = setTimeout(function () {
      collectionLive.tailTimer = 0;
      if (window.__wmnProductNavUntil && Date.now() < window.__wmnProductNavUntil) return;
      function run() {
        var grids = document.querySelectorAll(
          '.collection-template-section .product-list.collection-matrix, .collection-template .product-list.collection-matrix'
        );
        var i;
        for (i = 0; i < grids.length; i++) reorderCollectionTail(grids[i]);
      }
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.ensureLoaded === 'function' &&
        !graphReady()
      ) {
        window.wmnCatalogGraph.ensureLoaded().then(run).catch(function () {});
        return;
      }
      run();
    }, 280);
  }

  function buildCollectionTailCard(pick, className) {
    if (!pick || !pick.handle) return null;
    var data = window.wmnCatalogGraph.getData && window.wmnCatalogGraph.getData();
    var products = (data && data.products) || {};
    var p = products[pick.handle] || {};
    var genreLabels = [];
    var gs = pick.genres || p.g || [];
    var gi;
    for (gi = 0; gi < gs.length && genreLabels.length < 2; gi++) {
      genreLabels.push(humanizeSlug(gs[gi]));
    }
    var card = buildSearchMemoryCard(
      {
        h: pick.handle,
        title: pick.title || p.t || pick.handle,
        img: pick.image || p.img || '',
        price: '',
        tags: []
      },
      className,
      { eager: false, collection: true, genres: genreLabels }
    );
    if (!card) return null;
    card.className = className + ' wmn-memory-search-card';
    card.setAttribute('data-wmn-fy-paint', '1');
    card.setAttribute('data-wmn-genres', genreLabels.join('|'));
    return card;
  }

  /**
   * Keep every card that is on screen (and the one under the pointer).
   * Replace only cards fully below the viewport with the new ranking.
   */
  function reorderCollectionTail(container) {
    if (!container || !wantsAlgorithmCollectionSort()) return false;
    if (!graphReady() || !collectionInterestWarm()) return false;
    var cards = collectionMatrixCards(container);
    if (cards.length < 2) return false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var keep = {};
    var replace = [];
    var i;
    for (i = 0; i < cards.length; i++) {
      var card = cards[i];
      var handle = card.getAttribute('data-wmn-handle');
      var rect = card.getBoundingClientRect();
      var hovering = handle && handle === window.__wmnCollectionHoverHandle;
      if (hovering || rect.top < vh - 8) {
        if (handle) keep[handle] = true;
      } else {
        replace.push(card);
      }
    }
    if (!replace.length) return false;

    var page = collectionPageNumber();
    var prim = collectionFocusPrimary();
    var focusOverride = focusFromCollectionPrimary(prim);
    var lim = Math.max(cards.length * 3, 72, page * 48);
    if (lim > 600) lim = 600;
    var fy = scoreCatalogForYou({
      limit: lim,
      explore: false,
      pinRecent: false,
      fillToLimit: true,
      focus: focusOverride || undefined
    });
    if (!fy || !fy.picks || !fy.picks.length) return false;

    var prior = fyHandlesBeforePage(page);
    var desired = [];
    var seen = {};
    for (i = 0; i < fy.picks.length && desired.length < replace.length; i++) {
      var pick = fy.picks[i];
      if (!pick || !pick.handle || keep[pick.handle] || prior[pick.handle] || seen[pick.handle]) continue;
      seen[pick.handle] = true;
      desired.push(pick);
    }
    if (!desired.length) return false;

    var same = desired.length === replace.length;
    if (same) {
      for (i = 0; i < desired.length; i++) {
        if (desired[i].handle !== replace[i].getAttribute('data-wmn-handle')) {
          same = false;
          break;
        }
      }
    }
    if (same) return false;

    var sample = cards[0];
    var className = 'thumbnail';
    if (sample && sample.className) {
      className = String(sample.className || '')
        .replace(/\bwmn-memory-search-card\b/g, '')
        .replace(/\bwmn-interest-card\b/g, '')
        .replace(/\beven\b/g, '')
        .replace(/\bodd\b/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    var pool = {};
    for (i = 0; i < replace.length; i++) {
      pool[replace[i].getAttribute('data-wmn-handle')] = replace[i];
    }
    var anchor = replace[replace.length - 1].nextSibling;
    var frag = document.createDocumentFragment();
    var used = {};
    window.__wmnCollectionTailPainting = true;
    try {
      for (i = 0; i < desired.length; i++) {
        var want = desired[i].handle;
        var node = pool[want];
        if (!node) node = buildCollectionTailCard(desired[i], className);
        if (!node) continue;
        frag.appendChild(node);
        used[want] = true;
      }
      for (i = 0; i < replace.length; i++) {
        var oldH = replace[i].getAttribute('data-wmn-handle');
        if (!used[oldH] && replace[i].parentNode) {
          replace[i].parentNode.removeChild(replace[i]);
        }
      }
      container.insertBefore(frag, anchor);
    } finally {
      setTimeout(function () {
        window.__wmnCollectionTailPainting = false;
      }, 500);
    }
    nudgeStuckLazyImages(container);
    observeCollectionCards(container);
    rememberFyPage(
      collectionPageNumber(),
      collectionMatrixCards(container).map(function (card) {
        return card.getAttribute('data-wmn-handle');
      })
    );
    return true;
  }

  function observeCollectionCards(root) {
    if (!collectionLive.io) return;
    var scope = root && root.querySelectorAll ? root : document;
    if (isHomepagePersonalRail(scope)) return;
    var lists;
    if (
      scope.classList &&
      scope.classList.contains('collection-matrix') &&
      scope.closest &&
      scope.closest('.collection-template-section, .collection-template')
    ) {
      lists = [scope];
    } else {
      lists = scope.querySelectorAll(
        '.collection-template-section .product-list.collection-matrix, .collection-template .product-list.collection-matrix'
      );
    }
    var i;
    for (i = 0; i < lists.length; i++) {
      var cards = collectionMatrixCards(lists[i]);
      var j;
      for (j = 0; j < cards.length; j++) {
        if (cards[j].getAttribute('data-wmn-live-obs') === '1') continue;
        cards[j].setAttribute('data-wmn-live-obs', '1');
        collectionLive.io.observe(cards[j]);
      }
    }
  }

  function bindCollectionLiveRank() {
    if (window.__wmnCollectionLive) return;
    window.__wmnCollectionLive = true;

    document.addEventListener(
      'pointerover',
      function (e) {
        var card = collectionCardFromEvent(e.target);
        if (!card) return;
        var handle = card.getAttribute('data-wmn-handle');
        if (!handle || handle === collectionLive.hoverHandle) return;
        clearCollectionHover();
        collectionLive.hoverHandle = handle;
        window.__wmnCollectionHoverHandle = handle;
        collectionLive.hoverX = e.clientX;
        collectionLive.hoverY = e.clientY;
        collectionLive.hoverStarted = Date.now();
        collectionLive.hoverTimer = setTimeout(function () {
          collectionLive.hoverTimer = 0;
          if (collectionLive.hoverHandle !== handle) return;
          var dwell = Date.now() - collectionLive.hoverStarted;
          if (dwell < 450) return;
          if (
            !pointerStillOnCollectionCard(
              handle,
              collectionLive.hoverX,
              collectionLive.hoverY
            )
          ) {
            return;
          }
          if (collectionLive.dwell[handle]) return;
          collectionLive.dwell[handle] = true;
          emitCollectionSignal(card, 'hover', { dwellMs: dwell });
        }, 450);
      },
      true
    );

    document.addEventListener(
      'pointermove',
      function (e) {
        if (!collectionLive.hoverHandle) return;
        if (typeof e.clientX === 'number') collectionLive.hoverX = e.clientX;
        if (typeof e.clientY === 'number') collectionLive.hoverY = e.clientY;
      },
      true
    );

    document.addEventListener(
      'pointerout',
      function (e) {
        var card = collectionCardFromEvent(e.target);
        if (!card) return;
        if (card.getAttribute('data-wmn-handle') !== collectionLive.hoverHandle) return;
        var next = e.relatedTarget ? collectionCardFromEvent(e.relatedTarget) : null;
        if (next === card) return;
        clearCollectionHover();
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        var btn =
          e.target && e.target.closest
            ? e.target.closest(
                '.collection-template-section [data-wmn-play], .collection-template-section [data-wmn-listen], .collection-template [data-wmn-play], .collection-template [data-wmn-listen]'
              )
            : null;
        if (!btn) return;
        var card = collectionCardFromEvent(btn) || collectionCardFromEvent(e.target);
        var handle =
          (card && card.getAttribute('data-wmn-handle')) ||
          btn.getAttribute('data-wmn-handle');
        if (!handle) return;
        collectionLive.dwell[handle] = true;
        if (!card) return;
        emitCollectionSignal(card, 'listen');
      },
      true
    );

    if (typeof window.IntersectionObserver === 'function') {
      collectionLive.io = new IntersectionObserver(
        function (entries) {
          if (window.__wmnCollectionTailPainting) return;
          var now = Date.now();
          var i;
          for (i = 0; i < entries.length; i++) {
            var en = entries[i];
            var card = en.target;
            if (!card || !card.getAttribute) continue;
            var handle = card.getAttribute('data-wmn-handle');
            if (!handle) continue;
            if (en.isIntersecting && en.intersectionRatio >= 0.55) {
              if (!collectionLive.exposedAt[handle]) collectionLive.exposedAt[handle] = now;
              continue;
            }
            if (en.isIntersecting) continue;
            var started = collectionLive.exposedAt[handle];
            var rect = card.getBoundingClientRect();
            var scrolledPast = rect.bottom <= 4;
            if (
              started &&
              now - started >= 700 &&
              scrolledPast &&
              !collectionLive.dwell[handle] &&
              !collectionLive.skip[handle]
            ) {
              collectionLive.skip[handle] = true;
              emitCollectionSignal(card, 'skip', { amount: 0.28 });
            }
            if (en.intersectionRatio === 0) delete collectionLive.exposedAt[handle];
          }
        },
        { threshold: [0, 0.55] }
      );
      observeCollectionCards(document);
      if (window.MutationObserver) {
        var scanTimer = 0;
        var mo = new MutationObserver(function () {
          if (scanTimer) return;
          scanTimer = setTimeout(function () {
            scanTimer = 0;
            observeCollectionCards(document);
          }, 120);
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
      }
    }
  }

  function init() {
    bindEngagementObservers();
    bindCollectionLiveRank();
    bindExtraSignals();
    // Login on this browser: fold anon taste into customer keys first,
    // then overlay cloud metafield (newer wins inside applyCloudInterest),
    // then copy durable → session for For You.
    migrateAnonymousToCustomer();
    mergeCloudPreferences();
    hydrateInterestFromDurable();
    recordFromPage();
    reorderSearch();
    reorderProductGrids();
    reorderAutocompleteLists();
    bindCollectionSortSelect();
    scheduleCollectionAlgorithmSort();
    nudgeStuckLazyImages(document);
    armStuckLazyImageObserver();
    rerankRecs(document);
    renderForYouRail();
    renderBecauseYouLookedRail();
    renderPdpRelatedRails();
    scheduleCloudWriteback();
    bindForYouLiveRefresh();
    bindSearchGhost();
  }

  /** Re-paint homepage Picked for you when rail/prefs affinity updates. */
  function bindForYouLiveRefresh() {
    if (window.__wmnForYouLiveBound) return;
    window.__wmnForYouLiveBound = true;
    var timer = 0;
    function schedule(reason) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = 0;
        function paint() {
          if (window.__wmnProductNavUntil && Date.now() < window.__wmnProductNavUntil) return;
          try {
            var root = document.getElementById('wmn-for-you');
            // Phase 2: feed owns section — tail rebuild only, never wipe.
            if (forYouFeedOwns(root)) {
              if (
                window.wmnForYouFeed &&
                typeof window.wmnForYouFeed.remount === 'function' &&
                reason &&
                String(reason).indexOf('rail') !== -1
              ) {
                window.wmnForYouFeed.remount(
                  'force-remount:' + (reason || 'live-refresh')
                );
              } else if (
                window.wmnForYouFeed &&
                typeof window.wmnForYouFeed.rebuildTail === 'function'
              ) {
                window.wmnForYouFeed.rebuildTail(reason || 'live-refresh');
              } else {
                document.dispatchEvent(
                  new CustomEvent('wmn:for-you-state', {
                    detail: { reason: reason || 'live-refresh' }
                  })
                );
              }
            } else {
              renderForYouRail();
            }
            renderBecauseYouLookedRail();
            renderPdpRelatedRails();
          } catch (e) {
            /* ignore */
          }
        }
        try {
          if (
            window.wmnCatalogGraph &&
            typeof window.wmnCatalogGraph.ensureLoaded === 'function'
          ) {
            window.wmnCatalogGraph.ensureLoaded().then(paint).catch(paint);
            return;
          }
        } catch (e2) {
          /* fall through */
        }
        paint();
      }, 200);
    }
    document.addEventListener('wmn:preferences-updated', function () {
      schedule('preferences-updated');
    });
    document.addEventListener('wmn:rail-affinity', function () {
      schedule('rail-affinity');
    });
    document.addEventListener('wmn:intent-field-updated', function () {
      schedule('intent-field-updated');
    });
    document.addEventListener('wmn:focus-changed', function () {
      schedule('focus-changed');
    });

    // Collection "For you" must re-rank when search/view intent changes —
    // homepage rails alone used to refresh, leaving /collections/all stuck on
    // the first cold sort (often Mali/Jazz imprint mass).
    function scheduleCollectionRefresh() {
      if (!wantsAlgorithmCollectionSort()) return;
      if (
        !document.querySelector(
          '.collection-template-section .product-list.collection-matrix, .collection-template .product-list.collection-matrix'
        )
      ) {
        return;
      }
      if (window.__wmnProductNavUntil && Date.now() < window.__wmnProductNavUntil) return;
      // Grid hover / scroll / play: refresh what is still below the fold.
      // A full matrix swap removes the card under the pointer.
      if (window.__wmnCollectionTailUntil && Date.now() < window.__wmnCollectionTailUntil) {
        scheduleCollectionTailReorder();
        return;
      }
      invalidateCollectionAlgorithmSort();
      scheduleCollectionAlgorithmSort();
    }
    document.addEventListener('wmn:preferences-updated', scheduleCollectionRefresh);
    document.addEventListener('wmn:intent-field-updated', scheduleCollectionRefresh);
    document.addEventListener('wmn:focus-changed', scheduleCollectionRefresh);
  }

  window.wmnEngagement = {
    init: init,
    recordView: recordView,
    recordSearch: recordSearch,
    recordSearchClick: recordSearchClick,
    recordSkipNegative: recordSkipNegative,
    recordFeedSignal: recordFeedSignal,
    getStore: readStore,
    reorderProductGrids: reorderProductGrids,
    scheduleCollectionAlgorithmSort: scheduleCollectionAlgorithmSort,
    wantsAlgorithmCollectionSort: wantsAlgorithmCollectionSort,
    nudgeStuckLazyImages: nudgeStuckLazyImages,
    rerankRecs: rerankRecs,
    gateShopifyRecs: gateShopifyRecs,
    renderForYouRail: renderForYouRail,
    renderBecauseYouLookedRail: renderBecauseYouLookedRail,
    becausePicksForSeed: becausePicksForSeed,
    becauseExcludeSet: becauseExcludeSet,
    becauseIsExcluded: becauseIsExcluded,
    becauseDigDeeperTarget: becauseDigDeeperTarget,
    becauseProductMatchesGenre: becauseProductMatchesGenre,
    renderMoreFromArtistRail: renderMoreFromArtistRail,
    enrichRelatedByGenreRail: enrichRelatedByGenreRail,
    renderPdpRelatedRails: renderPdpRelatedRails,
    moreFromArtistPicks: moreFromArtistPicks,
    buildForYouPicks: buildForYouPicks,
    scoreCatalogForYou: scoreCatalogForYou,
    scoreCatalogStaffPicks: scoreCatalogStaffPicks,
    blendStaffPicksAffinity: blendStaffPicksAffinity,
    hasStaffPicksAffinitySignal: hasStaffPicksAffinitySignal,
    getStaffPicksAffinityHandle: getStaffPicksAffinityHandle,
    staffPicksFortnightBucket: staffPicksFortnightBucket,
    hasGeoForYouPrefs: hasGeoForYouPrefs,
    rankByAffinity: rankByAffinity,
    fieldOwnsRanking: fieldOwnsRanking,
    getEntityPrefs: getEntityPrefs,
    getFocus: readFocus,
    getSearchSuggestion: getSearchSuggestion,
    getInterestProfile: readInterestProfile,
    applyCloudProfile: applyCloudProfile,
    buildTasteCloudProfile: buildTasteCloudProfile,
    promoteFocus: promoteFocus,
    scoreHandle: scoreHandle,
    scoreHandleEntities: scoreHandleEntities,
    recordQueryClick: recordQueryClick,
    queryClickBoost: queryClickBoost,
    queryClickWeight: queryClickWeight,
    queryClickTopHandles: queryClickTopHandles,
    ensureQueryClickCollective: ensureQueryClickCollective,
    logIntent: logIntentEvent,
    getPendingSync: getPendingSync,
    softmaxPiMap: softmaxPiMap,
    estimateIps: estimateIps,
    slotWeight: slotWeight,
    FY_POLICY_ID: FY_POLICY_ID,
    FY_IPS_CLIP: FY_IPS_CLIP,
    /** Theme recentlyViewed cookie helpers (Phase 1 history unify). */
    readRecentlyViewedCookie: readRecentlyViewedCookie,
    writeRecentlyViewedCookie: writeRecentlyViewedCookie,
    pushRecentlyViewedHandle: pushRecentlyViewedHandle,
    prependRecentlyViewedHandles: prependRecentlyViewedHandles,
    normalizeRecentlyViewedHandle: normalizeRecentlyViewedHandle,
    isLikelyProductHandle: isLikelyProductHandle,
    getAffinity: function () {
      return getAffinityCompat();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme InstantClick uses .on('change'); some builds also emit instantclick:newpage.
  // Without this, SPA navigations to PDPs never call recordFromPage (full genres/image).
  function onInstantNav() {
    init();
  }
  document.addEventListener('instantclick:newpage', onInstantNav);
  if (window.InstantClick && typeof InstantClick.on === 'function') {
    try {
      InstantClick.on('change', onInstantNav);
    } catch (errIc) {
      /* ignore */
    }
  }
})(window, document);
